import { useState, useEffect, useRef, useCallback } from "react";

// ─---THEME---──────────────────────────────────────────────────────────────────
const T = {
  bg: "#07090f", surface: "#0c1018", card: "#101520", cardHover: "#141b28",
  border: "#182030", borderLight: "#1e2d45", accent: "#2563eb",
  accentBright: "#3b82f6", accentGlow: "#60a5fa", accentSoft: "rgba(37,99,235,0.12)",
  employer: "#059669", employerBright: "#10b981", gold: "#f59e0b",
  red: "#ef4444", green: "#10b981", purple: "#8b5cf6", cyan: "#06b6d4",
  textPrimary: "#f0f4ff", textSecondary: "#8899bb", textMuted: "#445577",
};

const IS = { width:"100%", background:"#0c1018", border:"1px solid #182030", borderRadius:10, padding:"11px 14px", fontSize:13, color:"#f0f4ff", outline:"none" };

// ─---3-TIER JOB VERIFICATION ENGINE---────────────────────────────────────────
const SCAM_PHRASES = [
  "unlimited earning","make money fast","guaranteed income","work from home easy",
  "send payment","wire transfer","gift card","personal information","bank account",
  "ssn required","social security","pay upfront","training fee","background check fee",
  "earn thousands weekly","multi level","pyramid","mlm","cryptocurrency",
  "investment opportunity","get rich","passive income","secret shopper",
  "reshipping","package handler","money mule","advance fee","no experience required",
];

const VERIFIED_DOMAINS = [
  "stripe.com","google.com","microsoft.com","apple.com","amazon.com","meta.com",
  "linkedin.com","salesforce.com","adobe.com","netflix.com","airbnb.com","uber.com",
  "lyft.com","twitter.com","spotify.com","shopify.com","notion.so","figma.com",
  "vercel.com","github.com","atlassian.com","slack.com","zoom.us","dropbox.com",
  "hubspot.com","zendesk.com","twilio.com","square.com","coinbase.com","robinhood.com",
  "duolingo.com","linear.app","workday.com","oracle.com","ibm.com","cisco.com",
  "intel.com","nvidia.com","qualcomm.com","amd.com","dell.com","hp.com",
];

function verifyJob(job) {
  let riskScore = 0;
  let verifyScore = 0;
  const flags = [];
  const checks = [];
  const desc = (job.description || job.desc || "").toLowerCase();
  const title = (job.title || "").toLowerCase();
  const combined = `${desc} ${title}`;

  // ---POSITIVE VERIFICATION CHECKS---
  // Check if company URL is from a known legitimate domain
  const applyUrl = (job.applyUrl || "").toLowerCase();
  const isKnownDomain = VERIFIED_DOMAINS.some(d => applyUrl.includes(d));
  if (isKnownDomain) {
    verifyScore += 40;
    checks.push("✅ Company verified — known legitimate employer");
  }

  // Has a real apply URL (not gmail/yahoo/hotmail)
  const hasProfessionalUrl = job.applyUrl &&
    !applyUrl.includes("gmail") && !applyUrl.includes("yahoo") &&
    !applyUrl.includes("hotmail") && applyUrl.startsWith("http");
  if (hasProfessionalUrl) {
    verifyScore += 20;
    checks.push("✅ Professional careers page verified");
  }

  // Has specific location
  if (job.location && job.location !== "Location not specified" && job.location.length > 3) {
    verifyScore += 10;
    checks.push("✅ Specific location provided");
  }

  // Has realistic salary
  if (job.salary && !job.salary.includes("500k") && !job.salary.includes("unlimited")) {
    verifyScore += 10;
    checks.push("✅ Realistic salary range listed");
  }

  // Has company name
  if (job.company && job.company !== "Unknown Company" && job.company.length > 2) {
    verifyScore += 10;
    checks.push("✅ Company name provided");
  }

  // Has job description
  if (job.desc && job.desc.length > 50) {
    verifyScore += 10;
    checks.push("✅ Detailed job description provided");
  }

  // ---NEGATIVE / RISK CHECKS---
  const foundPhrases = SCAM_PHRASES.filter(phrase => combined.includes(phrase));
  if (foundPhrases.length > 0) {
    riskScore += 35 * Math.min(foundPhrases.length, 3);
    flags.push(`🚨 Suspicious language detected: "${foundPhrases[0]}"`);
  }

  const personalInfoTerms = ["ssn","social security","bank account","credit card","wire transfer","gift card"];
  if (personalInfoTerms.some(t => combined.includes(t))) {
    riskScore += 40;
    flags.push("🚨 Requests personal or financial information");
  }

  if (!job.applyUrl || applyUrl.includes("gmail") || applyUrl.includes("yahoo")) {
    riskScore += 20;
    flags.push("⚠️ No professional careers page found");
  }

  if (!job.location || job.location === "Location not specified") {
    riskScore += 10;
    flags.push("⚠️ No specific location provided");
  }

  const salaryStr = (job.salary || "").toLowerCase();
  if (salaryStr.includes("500k") || (salaryStr.includes("week") && salaryStr.includes("5000"))) {
    riskScore += 25;
    flags.push("⚠️ Salary appears unrealistically high");
  }

  // ---DETERMINE TIER---
  const netScore = verifyScore - riskScore;

  let tier, color, badge, bgColor, borderColor, applyAllowed, summary;

  if (riskScore >= 50 || flags.some(f => f.includes("🚨"))) {
    // SUSPICIOUS — hide/collapse
    tier = "suspicious";
    color = T.red;
    badge = "🚨 Suspicious";
    bgColor = "#ef444408";
    borderColor = T.red + "44";
    applyAllowed = false;
    summary = "This posting has multiple red flags and could not be verified. We strongly recommend not applying.";
  } else if (verifyScore >= 60 && riskScore < 20) {
    // VERIFIED — green checkmark
    tier = "verified";
    color = T.green;
    badge = "✅ Verified";
    bgColor = "#10b98108";
    borderColor = T.green + "44";
    applyAllowed = true;
    summary = "This posting passed our verification checks. The company and listing appear legitimate.";
  } else {
    // UNVERIFIED — caution
    tier = "unverified";
    color = T.gold;
    badge = "⚠️ Unverified";
    bgColor = "#f59e0b08";
    borderColor = T.gold + "44";
    applyAllowed = true;
    summary = summary = `We could not fully verify this posting. ${flags.length > 0 ? "Unverified: " + flags.map(f => f.replace("⚠️", "").trim()).join(", ") + "." : "No specific issues found but legitimacy could not be confirmed."} Research the company before applying.`;
  }

  return { tier, color, badge, bgColor, borderColor, applyAllowed, summary, checks, flags, verifyScore, riskScore };
}

// ─---VERIFICATION BADGE COMPONENT---──────────────────────────────────────────
function VerificationBadge({ job, expanded }) {
  const v = verifyJob(job);
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{marginTop:8}}>
      <button onClick={e => { e.stopPropagation(); setShowDetails(s => !s); }}
        style={{display:"inline-flex",alignItems:"center",gap:6,background:`${v.color}18`,border:`1px solid ${v.borderColor}`,borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,color:v.color,cursor:"pointer",transition:"all 0.15s"}}>
        {v.badge}
        {(v.checks.length > 0 || v.flags.length > 0) && (
          <span style={{background:`${v.color}33`,borderRadius:10,padding:"1px 6px",fontSize:9}}>
            {v.checks.length} checks
          </span>
        )}
      </button>

      {showDetails && (
        <div onClick={e=>e.stopPropagation()} style={{marginTop:8,background:`${v.color}0d`,border:`1px solid ${v.borderColor}`,borderRadius:12,padding:"12px 14px"}}>
          {/* Summary */}
          <div style={{fontSize:12,color:v.color,fontWeight:600,marginBottom:10,padding:"6px 10px",background:`${v.color}15`,borderRadius:8}}>
            {v.summary}
          </div>

          {/* Verification checks passed */}
          {v.checks.length > 0 && (
            <div style={{marginBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:T.green,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>Passed Checks</div>
              {v.checks.map((c,i) => <div key={i} style={{fontSize:12,color:T.textSecondary,padding:"2px 0"}}>{c}</div>)}
            </div>
          )}

          {/* Risk flags */}
          {v.flags.length > 0 && (
            <div>
              <div style={{fontSize:10,fontWeight:700,color:T.red,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>Risk Flags</div>
              {v.flags.map((f,i) => <div key={i} style={{fontSize:12,color:T.textSecondary,padding:"2px 0"}}>{f}</div>)}
            </div>
          )}

          {/* Verification score bar */}
          <div style={{marginTop:10}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textMuted,marginBottom:4}}>
              <span>Verification Score</span>
              <span style={{color:v.color,fontWeight:700}}>{Math.max(0, v.verifyScore - v.riskScore)}/100</span>
            </div>
            <div style={{height:4,background:T.border,borderRadius:10,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${Math.max(0,Math.min(100, v.verifyScore - v.riskScore))}%`,background:v.color,borderRadius:10,transition:"width 0.4s ease"}}/>
            </div>
          </div>

          {v.tier === "suspicious" && (
            <div style={{marginTop:10,fontSize:12,color:T.red,fontWeight:600,padding:"8px 10px",background:"#ef444415",borderRadius:8,textAlign:"center"}}>
              🚨 Report this job: <a href="mailto:support@vaultrapps.com" style={{color:T.red}}>support@vaultrapps.com</a>
            </div>
          )}
          {v.tier === "unverified" && (
            <div style={{marginTop:10,fontSize:12,color:T.gold,padding:"6px 10px",background:"#f59e0b10",borderRadius:8}}>
              💡 Tip: Search the company name + "reviews" on Google before applying. Never pay fees to apply.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─---DB---─────────────────────────────────────────────────────────────────────
const DB = {
  seekers: [
    { id:"s1", type:"seeker", name:"Demo Seeker", email:"seeker@demo.com", password:"demo123", avatar:"DS", color:"#3b82f6", role:"Software Engineer", bio:"4 yrs React/Node", location:"Austin, TX", phone:"512-555-0101", plan:"pro", joined:"Apr 2026", resume:{ name:"Demo_Resume.pdf", skills:["React","TypeScript","Node.js","AWS"], experience:"4 years", education:"BS Computer Science", sensitive:{ address:"1402 Elm St, Austin TX 78701", phone:"512-555-0101", email:"seeker@demo.com" } } },
    { id:"s2", type:"seeker", name:"Aisha Johnson", email:"aisha@example.com", password:"pass", avatar:"AJ", color:"#8b5cf6", role:"Frontend Dev", bio:"React specialist", location:"Remote", phone:"415-555-0192", plan:"pro", joined:"Mar 2026", resume:{ name:"Aisha_Johnson_Resume.pdf", skills:["React","TypeScript","CSS"], experience:"4 years", education:"BS CS, Howard University", sensitive:{ address:"88 Market St, San Francisco CA", phone:"415-555-0192", email:"aisha@example.com" } } },
    { id:"s3", type:"seeker", name:"Marcus Chen", email:"marcus@example.com", password:"pass", avatar:"MC", color:"#06b6d4", role:"Product Designer", bio:"Figma wizard", location:"Seattle, WA", phone:"206-555-0143", plan:"pro", joined:"Feb 2026", resume:{ name:"Marcus_Resume.pdf", skills:["Figma","UX","Design Systems"], experience:"6 years", education:"BFA Design, RISD", sensitive:{ address:"321 Pine St, Seattle WA", phone:"206-555-0143", email:"marcus@example.com" } } },
  ],
  employers: [
    { id:"e1", type:"employer", name:"Demo Employer", email:"employer@demo.com", password:"demo123", avatar:"DE", color:"#059669", company:"TechCorp Inc.", industry:"Technology", size:"50-200", plan:"pro", joined:"Jan 2026" },
    { id:"e2", type:"employer", name:"Sarah Kim", email:"sarah@stripe.com", password:"pass", avatar:"SK", color:"#635bff", company:"Stripe", industry:"Fintech", size:"1000+", plan:"pro", joined:"Dec 2025" },
  ],
  contactRequests: [
    { id:"cr1", employerId:"e2", seekerId:"s2", status:"approved", message:"We'd love to connect!", jobId:1 },
  ],
  messages: [
    { id:1, userId:"s2", text:"Just got through the first round at Stripe! 🤞", time:"10:24 AM", reactions:{"🔥":["s3"],"💪":["s3","s1"]} },
    { id:2, userId:"s1", text:"YOU GOT THIS!! Talk through trade-offs explicitly!", time:"10:31 AM", reactions:{"❤️":["s2"]} },
    { id:3, userId:"s3", text:"Sofia accepted the offer at Vercel!! 🎉 This community is everything 💖", time:"2:47 PM", reactions:{"🎉":["s1","s2"],"🥂":["s2"],"💪":["s1"]} },
  ],
  notifications: {
    s1: [
      { id:1, type:"job", text:"New match: Senior Frontend Engineer at Stripe", time:"2h ago", read:false },
      { id:2, type:"contact", text:"An employer viewed your profile", time:"5h ago", read:false },
    ],
    e1: [
      { id:1, type:"candidate", text:"3 new candidate profiles posted resumes", time:"1h ago", read:false },
    ],
  },
};

const US_STATES = [
  "Nationwide","Remote",
  "---Alabama---","Birmingham AL","Montgomery AL","Huntsville AL","Mobile AL",
  "---Alaska---","Anchorage AK","Fairbanks AK",
  "---Arizona---","Phoenix AZ","Tucson AZ","Scottsdale AZ","Tempe AZ","Mesa AZ","Chandler AZ",
  "---Arkansas---","Little Rock AR","Fayetteville AR",
  "---California---","Los Angeles CA","San Francisco CA","San Diego CA","San Jose CA","Sacramento CA","Oakland CA","Fresno CA","Long Beach CA","Bakersfield CA","Anaheim CA","Santa Ana CA","Irvine CA","Palo Alto CA","Mountain View CA","Sunnyvale CA",
  "---Colorado---","Denver CO","Colorado Springs CO","Boulder CO","Aurora CO","Fort Collins CO",
  "---Connecticut---","Hartford CT","New Haven CT","Stamford CT","Bridgeport CT",
  "---Delaware---","Wilmington DE","Dover DE",
  "---Florida---","Miami FL","Orlando FL","Tampa FL","Jacksonville FL","Fort Lauderdale FL","St. Petersburg FL","Tallahassee FL","Boca Raton FL","Naples FL","Sarasota FL",
  "---Georgia---","Atlanta GA","Savannah GA","Augusta GA","Columbus GA","Macon GA",
  "---Hawaii---","Honolulu HI","Kailua HI",
  "---Idaho---","Boise ID","Nampa ID","Meridian ID",
  "---Illinois---","Chicago IL","Aurora IL","Springfield IL","Naperville IL","Peoria IL","Rockford IL",
  "---Indiana---","Indianapolis IN","Fort Wayne IN","Evansville IN","South Bend IN",
  "---Iowa---","Des Moines IA","Cedar Rapids IA","Iowa City IA",
  "---Kansas---","Wichita KS","Overland Park KS","Kansas City KS","Topeka KS",
  "---Kentucky---","Louisville KY","Lexington KY","Bowling Green KY",
  "---Louisiana---","New Orleans LA","Baton Rouge LA","Shreveport LA","Lafayette LA",
  "---Maine---","Portland ME","Augusta ME","Bangor ME",
  "---Maryland---","Baltimore MD","Rockville MD","Annapolis MD","Frederick MD",
  "---Massachusetts---","Boston MA","Cambridge MA","Worcester MA","Springfield MA","Lowell MA",
  "---Michigan---","Detroit MI","Grand Rapids MI","Ann Arbor MI","Lansing MI","Flint MI",
  "---Minnesota---","Minneapolis MN","Saint Paul MN","Rochester MN","Duluth MN",
  "---Mississippi---","Jackson MS","Gulfport MS","Biloxi MS",
  "---Missouri---","Kansas City MO","St. Louis MO","Springfield MO","Columbia MO",
  "---Montana---","Billings MT","Missoula MT","Great Falls MT",
  "---Nebraska---","Omaha NE","Lincoln NE","Bellevue NE",
  "---Nevada---","Las Vegas NV","Reno NV","Henderson NV","Sparks NV",
  "---New Hampshire---","Manchester NH","Nashua NH","Concord NH",
  "---New Jersey---","Newark NJ","Jersey City NJ","Trenton NJ","Camden NJ","Edison NJ",
  "---New Mexico---","Albuquerque NM","Santa Fe NM","Las Cruces NM",
  "---New York---","New York City NY","Brooklyn NY","Queens NY","Buffalo NY","Rochester NY","Albany NY","Syracuse NY","Yonkers NY",
  "---North Carolina---","Charlotte NC","Raleigh NC","Greensboro NC","Durham NC","Winston-Salem NC","Fayetteville NC","Asheville NC",
  "---North Dakota---","Fargo ND","Bismarck ND","Grand Forks ND",
  "---Ohio---","Columbus OH","Cleveland OH","Cincinnati OH","Toledo OH","Akron OH","Dayton OH",
  "---Oklahoma---","Oklahoma City OK","Tulsa OK","Norman OK","Broken Arrow OK",
  "---Oregon---","Portland OR","Salem OR","Eugene OR","Gresham OR","Beaverton OR",
  "---Pennsylvania---","Philadelphia PA","Pittsburgh PA","Allentown PA","Erie PA","Reading PA","Harrisburg PA",
  "---Rhode Island---","Providence RI","Cranston RI","Warwick RI",
  "---South Carolina---","Columbia SC","Charleston SC","Greenville SC","North Charleston SC",
  "---South Dakota---","Sioux Falls SD","Rapid City SD","Aberdeen SD",
  "---Tennessee---","Nashville TN","Memphis TN","Knoxville TN","Chattanooga TN","Clarksville TN",
  "---Texas---","Houston TX","San Antonio TX","Dallas TX","Austin TX","Fort Worth TX","El Paso TX","Arlington TX","Corpus Christi TX","Plano TX","Lubbock TX","Irving TX","Garland TX","Frisco TX","McKinney TX","Laredo TX",
  "---Utah---","Salt Lake City UT","West Valley City UT","Provo UT","West Jordan UT","Ogden UT",
  "---Vermont---","Burlington VT","Montpelier VT","Rutland VT",
  "---Virginia---","Virginia Beach VA","Norfolk VA","Chesapeake VA","Richmond VA","Arlington VA","Alexandria VA","Roanoke VA",
  "---Washington---","Seattle WA","Spokane WA","Tacoma WA","Vancouver WA","Bellevue WA","Kirkland WA","Redmond WA",
  "---Washington D.C.---","Washington DC",
  "---West Virginia---","Charleston WV","Huntington WV","Morgantown WV",
  "---Wisconsin---","Milwaukee WI","Madison WI","Green Bay WI","Kenosha WI","Racine WI",
  "---Wyoming---","Cheyenne WY","Casper WY","Laramie WY",
];
const JOB_TYPES = ["Any Type","Full-time","Part-time","Contract","Internship","Temporary"];
const JOB_CATEGORIES = [
  {id:"all",label:"All Jobs",icon:"💼"},{id:"engineering",label:"Engineering",icon:"⚙️"},
  {id:"design",label:"Design",icon:"🎨"},{id:"data",label:"Data & AI",icon:"🧠"},
  {id:"marketing",label:"Marketing",icon:"📣"},{id:"finance",label:"Finance",icon:"💰"},
  {id:"healthcare",label:"Healthcare",icon:"🏥"},{id:"sales",label:"Sales",icon:"🤝"},
  {id:"remote",label:"Remote Only",icon:"🌎"},
];

function getSeekerProfile(seeker, viewerType, viewerId) {
  if (viewerType === "seeker") return { ...seeker, resume: seeker.resume ? { ...seeker.resume, sensitive:null } : null, phone:null };
  if (viewerType === "employer") {
    const approved = DB.contactRequests.find(r => r.employerId === viewerId && r.seekerId === seeker.id && r.status === "approved");
    return { ...seeker, phone: approved ? seeker.phone : null, resume: seeker.resume ? { ...seeker.resume, sensitive: approved ? seeker.resume.sensitive : null } : null };
  }
  return seeker;
}

// ─---SHARED COMPONENTS---──────────────────────────────────────────────────────
function STYLES() {
  return <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;} input,button,select,textarea{font-family:inherit;}
    ::-webkit-scrollbar{width:3px;} ::-webkit-scrollbar-thumb{background:#1e2d45;border-radius:4px;}
    input::placeholder,textarea::placeholder{color:#445577;}
    @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
    @keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    details summary::-webkit-details-marker{display:none;}
    select option{background:#0c1018;color:#f0f4ff;}
  `}</style>;
}

function Avatar({ initials, color, size=36, online=false }) {
  return (
    <div style={{position:"relative",flexShrink:0}}>
      <div style={{width:size,height:size,borderRadius:"50%",background:`linear-gradient(135deg,${color}cc,${color}77)`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:size*0.32,color:"#fff",boxShadow:`0 0 0 2px ${color}33`}}>{initials}</div>
      {online && <div style={{position:"absolute",bottom:1,right:1,width:size*0.26,height:size*0.26,borderRadius:"50%",background:T.green,border:`2px solid ${T.bg}`}}/>}
    </div>
  );
}

function Pill({ children, color=T.accentBright }) {
  return <span style={{background:`${color}18`,color,border:`1px solid ${color}33`,borderRadius:20,fontSize:11,fontWeight:700,padding:"2px 9px",letterSpacing:"0.03em",whiteSpace:"nowrap"}}>{children}</span>;
}

function PrivacyShield({ label="Hidden for privacy" }) {
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,background:"#ef444415",border:"1px solid #ef444430",borderRadius:8,padding:"3px 10px",fontSize:12,color:T.red,fontWeight:600}}>🔒 {label}</span>;
}

function Section({ title, children, extra }) {
  return (
    <div style={{marginBottom:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,letterSpacing:"0.1em",textTransform:"uppercase"}}>{title}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
      <div style={{width:90,fontSize:11,color:T.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",paddingTop:2,flexShrink:0}}>{label}</div>
      <div style={{flex:1,fontSize:13,color:T.textSecondary}}>{value}</div>
    </div>
  );
}

// ─---SCAM BADGE COMPONENT---───────────────────────────────────────────────────
function ScamBadge({ job, expanded }) {
  const analysis = analyzeJobForScams(job);
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{marginTop: expanded ? 12 : 0}}>
      <button onClick={e => { e.stopPropagation(); setShowDetails(s => !s); }}
        style={{display:"flex",alignItems:"center",gap:6,background:`${analysis.color}15`,border:`1px solid ${analysis.color}44`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,color:analysis.color,cursor:"pointer"}}>
        {analysis.label}
        {analysis.flags.length > 0 && <span style={{background:`${analysis.color}33`,borderRadius:10,padding:"1px 5px",fontSize:9}}>{analysis.flags.length} flag{analysis.flags.length!==1?"s":""}</span>}
      </button>
      {showDetails && analysis.flags.length > 0 && (
        <div onClick={e=>e.stopPropagation()} style={{marginTop:8,background:`${analysis.color}10`,border:`1px solid ${analysis.color}33`,borderRadius:10,padding:"10px 12px"}}>
          <div style={{fontSize:11,fontWeight:700,color:analysis.color,marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Safety Analysis</div>
          {analysis.flags.map((flag,i) => (
            <div key={i} style={{fontSize:12,color:T.textSecondary,padding:"3px 0"}}>{flag}</div>
          ))}
          {analysis.level === "high" && (
            <div style={{marginTop:8,fontSize:12,color:T.red,fontWeight:600,padding:"6px 8px",background:"#ef444415",borderRadius:8}}>
              🚨 We strongly recommend not applying to this job. Report it at support@vaultrapps.com
            </div>
          )}
          {analysis.level === "medium" && (
            <div style={{marginTop:8,fontSize:12,color:T.gold,padding:"6px 8px",background:"#f59e0b15",borderRadius:8}}>
              ⚠️ Research this company thoroughly before applying. Never pay fees or share financial info.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─---UPGRADE MODAL---──────────────────────────────────────────────────────────
function UpgradeModal({ onClose, onUpgrade, type }) {
  const isEmp = type === "employer";
  const [step, setStep] = useState("plan");
  const [card, setCard] = useState({number:"",expiry:"",cvc:"",name:""});
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const accentCol = isEmp ? T.employer : type === "premier" ? "#8b5cf6" : "#f59e0b";
  const premierFeatures = [
    "✅ Everything in Pro",
    "✅ Unlimited AI Resume Builder",
    "✅ Job-Match Resume",
    "✅ Cover Letter Generator",
    "✅ Unlimited Job Alerts",
    "✅ Career DNA live search",
    "✅ Match Score %",
    "✅ AI Bid Writer for contracts",
    "✅ One-click Indeed/LinkedIn apply",
  ];
  const features = isEmp
    ? ["✅ Search & filter all candidate profiles","✅ Send contact requests","✅ View contact info after approval","✅ Post unlimited job listings","✅ AI candidate matching"]
    : type === "premier"
    ? premierFeatures
    : ["✅ Upload up to 3 privacy-protected resumes","✅ AI Career Coach unlimited","✅ Apply to jobs directly","✅ Send & receive direct messages","✅ Resume feedback from community"];

  const processPayment = async () => {
    window.open(type === "premier" ? "https://buy.stripe.com/bJefZidke9zVdJBf0ggfu01" : "https://buy.stripe.com/dRm9AU0xs3bxdJB2dugfu00", "_blank");
    setStep("pending");
  };

  const handleUpgrade = async () => {
    try {
      const session = JSON.parse(localStorage.getItem("vaultr_session") || "{}");
      if (session.access_token) {
        const userRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
          headers: { "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY, "Authorization": `Bearer ${session.access_token}` }
        });
        const freshUser = await userRes.json();
        const currentMeta = freshUser.user_metadata || {};
        await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY, "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({ data: { ...currentMeta, plan: "pro" } })
        });
        session.userData = { ...session.userData, plan: "pro" };
        localStorage.setItem("vaultr_session", JSON.stringify(session));
      }
    } catch(e) { console.error("Failed to save Pro status:", e); }
    onUpgrade();
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16,backdropFilter:"blur(10px)"}}>
      <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:24,padding:28,maxWidth:400,width:"100%",animation:"fadeUp 0.3s ease",maxHeight:"92vh",overflowY:"auto"}}>

        {step==="plan" && <>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:40,marginBottom:8}}>{isEmp?"🏢":type==="premier"?"👑":"⚡"}</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:T.textPrimary}}>Vaultr {isEmp?"Employer":type==="premier"?"Premier":"Pro"}</div>
            <div style={{fontSize:13,color:T.textSecondary,marginTop:4}}>{isEmp?"Find your next great hire":type==="premier"?"The ultimate career platform":"Land your dream job faster"}</div>
          </div>
          {!isEmp && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              <div style={{background:type==="pro"?T.accentSoft:"transparent",border:`2px solid ${type==="pro"?T.accent:T.border}`,borderRadius:14,padding:"12px",textAlign:"center"}}>
                <div style={{fontSize:20,marginBottom:4}}>⚡</div>
                <div style={{fontWeight:800,fontSize:15,color:T.textPrimary}}>Pro</div>
                <div style={{fontWeight:800,fontSize:20,color:T.gold,margin:"4px 0"}}>$9<span style={{fontSize:11,color:T.textMuted}}>/mo</span></div>
                <div style={{fontSize:10,color:T.textMuted}}>Resume, AI Coach, Apply</div>
              </div>
              <div style={{background:type==="premier"?"#8b5cf615":"transparent",border:`2px solid ${type==="premier"?"#8b5cf6":T.border}`,borderRadius:14,padding:"12px",textAlign:"center"}}>
                <div style={{fontSize:20,marginBottom:4}}>👑</div>
                <div style={{fontWeight:800,fontSize:15,color:T.textPrimary}}>Premier</div>
                <div style={{fontWeight:800,fontSize:20,color:"#8b5cf6",margin:"4px 0"}}>$19<span style={{fontSize:11,color:T.textMuted}}>/mo</span></div>
                <div style={{fontSize:10,color:T.textMuted}}>Everything + AI Builder</div>
              </div>
            </div>
          )}
          <div style={{background:T.surface,borderRadius:16,padding:18,marginBottom:18}}>
            <div style={{display:"flex",alignItems:"baseline",gap:4,justifyContent:"center",marginBottom:4}}>
              <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:44,color:T.textPrimary}}>{type==="premier"?"$19":"$9"}</span>
              <span style={{fontSize:14,color:T.textSecondary}}>/month</span>
            </div>
            <div style={{textAlign:"center",fontSize:12,color:T.textMuted,marginBottom:6}}>🎉 3-day free trial · Cancel anytime</div>
            <div style={{textAlign:"center",fontSize:11,color:T.green,marginBottom:14}}>✅ No charge until trial ends</div>
            {features.map(f => <div key={f} style={{fontSize:13,color:T.textSecondary,padding:"5px 0",borderBottom:`1px solid ${T.border}`}}>{f}</div>)}
          </div>
          <button onClick={()=>setStep("payment")} style={{width:"100%",background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:isEmp?"#fff":"#000",borderRadius:14,padding:"14px",fontWeight:800,fontSize:15,cursor:"pointer"}}>
            Continue to Payment →
          </button>
          <button onClick={onClose} style={{width:"100%",background:"none",border:"none",color:T.textMuted,fontSize:12,padding:"10px",cursor:"pointer",marginTop:4}}>Maybe later</button>
        </>}

        {step==="payment" && <>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <button onClick={()=>setStep("plan")} style={{background:"none",border:"none",color:T.textSecondary,cursor:"pointer",fontSize:18,padding:0}}>←</button>
            <div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:17,color:T.textPrimary}}>Secure Checkout</div>
              <div style={{fontSize:12,color:T.textSecondary}}>Vaultr {isEmp?"Employer":type==="premier"?"Premier":"Pro"} · {type==="premier"?"$19":"$9"}/month</div>
            </div>
            <div style={{marginLeft:"auto",fontSize:11,color:T.green}}>🔒 SSL Secured</div>
          </div>
          <div style={{background:`${T.green}10`,border:`1px solid ${T.green}33`,borderRadius:12,padding:"14px 16px",marginBottom:18,textAlign:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.green,marginBottom:6}}>✅ Secure Payment via Stripe</div>
            <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.6}}>You will be redirected to Stripe's secure checkout to complete your payment. After paying, return here and click "I've Paid" below.</div>
          </div>
          <button onClick={processPayment} style={{width:"100%",background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:isEmp?"#fff":"#000",borderRadius:14,padding:"14px",fontWeight:800,fontSize:15,cursor:"pointer",marginBottom:10}}>
            💳 Pay {type==="premier"?"$19":"$9"}/month via Stripe →
          </button>
          <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:6,marginBottom:10}}>
            <span style={{fontSize:11,color:T.textMuted}}>Powered by</span>
            <div style={{background:"#635bff",borderRadius:5,padding:"2px 8px",fontWeight:800,fontSize:12,color:"#fff"}}>stripe</div>
          </div>
        </>}

        {step==="pending" && <>
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:48,marginBottom:16}}>💳</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:T.textPrimary,marginBottom:8}}>Complete Payment on Stripe</div>
            <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:24}}>A Stripe checkout page has opened in a new tab. Complete your payment there, then come back here and click the button below!</div>
            <button onClick={handleUpgrade} style={{width:"100%",background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:isEmp?"#fff":"#000",borderRadius:14,padding:"13px",fontWeight:800,fontSize:15,cursor:"pointer",marginBottom:10}}>
              ✅ I've Paid — Activate Pro!
            </button>
            <button onClick={processPayment} style={{width:"100%",background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:14,padding:"11px",fontSize:13,cursor:"pointer",marginBottom:8}}>
              🔄 Reopen Stripe Checkout
            </button>
            <div style={{fontSize:11,color:T.textMuted}}>Having trouble? Email support@vaultrapps.com</div>
          </div>
        </>}

        {step==="success" && <>
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:56,marginBottom:16}}>🎉</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:24,color:T.textPrimary,marginBottom:8}}>You're Pro!</div>
            <button onClick={handleUpgrade} style={{width:"100%",background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:isEmp?"#fff":"#000",borderRadius:14,padding:"13px",fontWeight:800,fontSize:15,cursor:"pointer"}}>
              Start Using Pro →
            </button>
          </div>
        </>}

      </div>
    </div>
  );
}

// ─---NOTIFICATION PANEL---─────────────────────────────────────────────────────
function NotifPanel({ user, onClose }) {
  const [notifs, setNotifs] = useState(DB.notifications[user.id] || []);
  return (
    <div style={{position:"absolute",top:60,right:12,width:310,background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:16,boxShadow:"0 12px 48px rgba(0,0,0,0.7)",zIndex:300,overflow:"hidden",animation:"fadeUp 0.2s ease"}}>
      <div style={{padding:"12px 16px",display:"flex",justifyContent:"space-between",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontWeight:700,fontSize:14}}>Notifications</span>
        <button onClick={()=>setNotifs(n=>n.map(x=>({...x,read:true})))} style={{background:"none",border:"none",color:T.accentGlow,fontSize:11,cursor:"pointer"}}>Mark all read</button>
      </div>
      {notifs.length === 0 && <div style={{padding:24,textAlign:"center",fontSize:13,color:T.textMuted}}>No notifications yet</div>}
      {notifs.map(n=>(
        <div key={n.id} style={{padding:"11px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:10,background:n.read?"transparent":T.accentSoft}}>
          <span style={{fontSize:18}}>{n.type==="job"||n.type==="candidate"?"💼":n.type==="contact"?"👤":"💬"}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,color:T.textPrimary,lineHeight:1.4}}>{n.text}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{n.time}</div>
          </div>
          {!n.read && <div style={{width:7,height:7,borderRadius:"50%",background:T.accentBright,flexShrink:0,marginTop:5}}/>}
        </div>
      ))}
      <button onClick={onClose} style={{width:"100%",padding:"11px",background:"none",border:"none",color:T.textMuted,fontSize:12,cursor:"pointer"}}>Close</button>
    </div>
  );
}

// ─---FOOTER---─────────────────────────────────────────────────────────────────
function NexoraFooter() {
  return (
    <div style={{marginTop:36,paddingTop:20,borderTop:`1px solid ${T.border}`,textAlign:"center"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,marginBottom:10}}>
        <div style={{width:22,height:22,borderRadius:6,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:11}}>V</div>
        <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:13,color:T.textSecondary,letterSpacing:"-0.02em"}}>Vaultr</span>
      </div>
      <div style={{fontSize:12,color:T.textMuted,marginBottom:8}}>Vault into your future. Together.</div>
      <div style={{display:"flex",justifyContent:"center",gap:18,flexWrap:"wrap"}}>
        <a href="mailto:support@vaultrapps.com" style={{fontSize:12,color:T.accentGlow,textDecoration:"none"}}>✉️ support@vaultrapps.com</a>
        <a href="/privacy" style={{fontSize:12,color:T.textMuted,textDecoration:"none"}}>🔒 Privacy Policy</a>
        <a href="/terms" style={{fontSize:12,color:T.textMuted,textDecoration:"none"}}>📋 Terms of Service</a>
        <a href="mailto:hello@vaultrapps.com" style={{fontSize:12,color:T.textMuted,textDecoration:"none"}}>👋 hello@vaultrapps.com</a>
      </div>
      <div style={{fontSize:11,color:T.textMuted,marginTop:10}}>© 2026 Vaultr · Veteran & Military Family Founded 🇺🇸</div>
    </div>
  );
}

// ─---AUTH SCREEN---────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [userType, setUserType] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({name:"",email:"",password:"",role:"",company:"",industry:""});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [resetSent, setResetSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [highSecurity, setHighSecurity] = useState(false);
  const [signupStep, setSignupStep] = useState("details");
  const [selectedPlan, setSelectedPlan] = useState("free");

const handleReset = async () => {
  if (!form.email.trim()) { setError("Please enter your email address"); return; }
  setError("");
  setLoading(true);
  try {
    const res = await fetch("/api/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: form.email })
    });
    if (res.status >= 500) {
      // Genuine failure on our end (e.g. couldn't generate a link) — say so.
      setError("⚠️ Something went wrong on our end. Please try again in a moment.");
    } else {
      // Show the same success state whether or not the account exists,
      // so we never reveal account existence via this flow.
      setResetSent(true);
    }
  } catch {
    setError("⚠️ Something went wrong. Please check your connection and try again.");
  }
  setLoading(false);
};
  const handle = async () => {
    setError(""); setLoading(true);
    const init = form.name?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase() || form.email[0].toUpperCase();
    const colors = ["#3b82f6","#8b5cf6","#06b6d4","#10b981","#f59e0b","#ef4444"];
    const col = colors[Math.floor(Math.random()*colors.length)];

    if (mode==="login") {
      // Basic validation first
      if (!form.email.trim()) { setError("Please enter your email address"); setLoading(false); return; }
      if (!form.password.trim()) { setError("Please enter your password"); setLoading(false); return; }
      if (!/\S+@\S+\.\S+/.test(form.email)) { setError("Please enter a valid email address"); setLoading(false); return; }
      try {
        const data = await supabaseAuth.signIn(form.email, form.password);
        if (data.error || !data.access_token) {
          const msg = data.error?.message?.toLowerCase() || "";
          if (msg.includes("invalid login") || msg.includes("invalid credentials") || msg.includes("wrong password") || msg.includes("incorrect")) {
            setError("❌ Incorrect password. Please try again or click 'Forgot Password' below.");
          } else if (msg.includes("not found") || msg.includes("no user") || msg.includes("user not found")) {
            setError("❌ No account found with this email. Please sign up instead!");
          } else if (msg.includes("email not confirmed")) {
            setError("📧 Please confirm your email address before logging in. Check your inbox!");
          } else if (msg.includes("too many") || msg.includes("rate limit")) {
            setError("⏳ Too many login attempts. Please wait a few minutes and try again.");
          } else if (msg.includes("disabled") || msg.includes("banned")) {
            setError("🚫 This account has been disabled. Please contact support@vaultrapps.com");
          } else {
            setError("❌ Incorrect email or password. Please try again or click 'Forgot Password'.");
          }
          setLoading(false); return;
        }
        const meta = data.user?.user_metadata || {};
        const loggedInUser = {
          id: data.user.id,
          type: meta.type || userType,
          name: meta.name || form.email.split("@")[0],
          email: form.email,
          avatar: meta.avatar || init,
          color: meta.color || col,
          role: meta.role || "",
          bio: meta.bio || "",
          location: meta.location || "",
          phone: meta.phone || "",
          company: meta.company || "",
          industry: meta.industry || "",
          size: meta.size || "",
          plan: meta.plan || "free",
          joined: meta.joined || "2026",
          resume: meta.resume || null,
          hasUsedFreeCritique: meta.hasUsedFreeCritique || false,
          token: data.access_token,
        };
        supabaseAuth.saveSession({ access_token: data.access_token, refresh_token: data.refresh_token }, loggedInUser, rememberMe, highSecurity);
        onAuth(loggedInUser);
      } catch {
        setError("❌ Login failed. Please check your connection and try again.");
      }
    } else {
      if (signupStep==="details") {
        if (!form.name||!form.email||!form.password) { setError("⚠️ Please fill in all required fields"); setLoading(false); return; }
        if (!/\S+@\S+\.\S+/.test(form.email)) { setError("⚠️ Please enter a valid email address"); setLoading(false); return; }
        if (form.password.length < 6) { setError("⚠️ Password must be at least 6 characters"); setLoading(false); return; }
        if (isEmp && !form.company) { setError("⚠️ Please enter your company name"); setLoading(false); return; }
        setLoading(false); setError(""); setSignupStep("plan"); return;
      }
      if (!form.name||!form.email||!form.password) { setError("⚠️ Please fill in all required fields"); setLoading(false); return; }
      if (!/\S+@\S+\.\S+/.test(form.email)) { setError("⚠️ Please enter a valid email address"); setLoading(false); return; }
      if (form.password.length < 6) { setError("⚠️ Password must be at least 6 characters"); setLoading(false); return; }
      if (isEmp && !form.company) { setError("⚠️ Please enter your company name"); setLoading(false); return; }
      try {
        const planValue = (selectedPlan === "Basic" || selectedPlan === "basic") ? "free" : (selectedPlan || "free");
        const metadata = userType==="seeker"
? { type:"seeker", name:form.name, avatar:init, color:col, role:form.role, bio:"", location:"", phone:"", plan:planValue, joined:new Date().toISOString() } : { type:"employer", name:form.name, avatar:init, color:col, company:form.company, industry:form.industry, size:"1-50", plan:planValue, joined:new Date().toISOString() };
        const data = await supabaseAuth.signUp(form.email, form.password, metadata);
        if (data.error) {
          const msg = data.error.message?.toLowerCase() || "";
          if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("already in use") || msg.includes("duplicate")) {
            setError("📧 This email is already registered! Please log in instead or click 'Forgot Password' if you forgot your password.");
          } else if (msg.includes("invalid email") || msg.includes("valid email")) {
            setError("⚠️ Please enter a valid email address");
          } else if (msg.includes("weak password") || msg.includes("password")) {
            setError("⚠️ Password must be at least 6 characters and should include letters and numbers");
          } else {
            setError(data.error.message || "⚠️ Signup failed. Please try again.");
          }
          setLoading(false); return;
        }
        if (data.access_token) {
          const newUser = { id:data.user.id, ...metadata, email:form.email, token:data.access_token, resume:null };
          supabaseAuth.saveSession({ access_token: data.access_token, refresh_token: data.refresh_token }, newUser, true);
          sendWelcomeEmail(form.email, form.name, selectedPlan);
          onAuth(newUser);
        } else if (data.user && !data.access_token) {
          // Account created but needs email confirmation
          // Show helpful message instead of just waiting
          setError("");
          setLoading(false);
          setMode("login");
          alert("Account created! You can now sign in with your email and password.");
        } else {
          setError(data.error?.message || "Signup failed. Please try again.");
        }
      } catch {
        setError("Signup failed. Please try again.");
      }
    }
    setLoading(false);
  };

  if (!userType) return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <STYLES/>
      <div style={{animation:"fadeUp 0.5s ease",width:"100%",maxWidth:620}}>
        <div style={{marginBottom:20}}>
          <div style={{textAlign:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>✨ Plans & Pricing</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Start with 3 days of Pro free — no credit card required</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,alignItems:"stretch"}}>
            <button onClick={()=>setUserType("seeker")} style={{background:T.card,border:`1.5px solid ${T.border}`,borderRadius:14,padding:"16px 12px",cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",gap:6,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor="#64748b";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;}}>
              <div style={{fontSize:22}}>🔓</div>
              <div style={{fontSize:14,fontWeight:800,color:T.textPrimary}}>Free</div>
              <div style={{fontSize:11,color:T.textMuted}}>$0 / month</div>
              <div style={{height:1,background:T.border,margin:"4px 0"}}/>
              <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                {["Browse verified jobs","1 AI coach question/day","Community access","Full privacy control","Basic profile"].map(f=>(
                  <div key={f} style={{fontSize:10,color:T.textSecondary,display:"flex",gap:4,alignItems:"flex-start",lineHeight:1.4}}><span style={{color:T.textMuted,flexShrink:0,marginTop:1}}>·</span>{f}</div>
                ))}
              </div>
              <div style={{marginTop:"auto",paddingTop:8}}>
                <div style={{fontSize:10,fontWeight:700,color:T.textSecondary,textAlign:"center",background:T.surface,borderRadius:8,padding:"6px 8px"}}>Get started free →</div>
              </div>
            </button>
            <button onClick={()=>window.open("https://buy.stripe.com/dRm9AU0xs3bxdJB2dugfu00","_blank","noopener,noreferrer")} style={{background:"#5b4fe80d",border:"2px solid #5b4fe8",borderRadius:14,padding:"16px 12px",cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",gap:6,transition:"all 0.15s",boxShadow:"0 0 20px #5b4fe822"}} onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 0 28px #5b4fe844";}} onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 0 20px #5b4fe822";}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:22}}>⚡</div>
                <span style={{fontSize:9,fontWeight:700,color:"#a78bfa",background:"#5b4fe820",borderRadius:6,padding:"3px 7px"}}>Most popular</span>
              </div>
              <div style={{fontSize:14,fontWeight:800,color:"#5b4fe8"}}>Pro</div>
              <div style={{fontSize:11,color:T.textMuted}}>$9 / month</div>
              <div style={{height:1,background:"#5b4fe830",margin:"4px 0"}}/>
              <div style={{fontSize:9,color:"#a78bfa",fontStyle:"italic",marginBottom:2}}>Everything in Free, plus:</div>
              <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                {["Unlimited AI coaching","Resume sharing & employer discovery","Save jobs & apply direct","Application tracking","Direct employer messaging"].map(f=>(
                  <div key={f} style={{fontSize:10,color:T.textPrimary,display:"flex",gap:4,alignItems:"flex-start",lineHeight:1.4}}><span style={{color:"#a78bfa",flexShrink:0,marginTop:1}}>·</span>{f}</div>
                ))}
              </div>
              <div style={{marginTop:"auto",paddingTop:8}}>
                <div style={{fontSize:10,fontWeight:700,color:"#fff",textAlign:"center",background:"#5b4fe8",borderRadius:8,padding:"6px 8px"}}>Upgrade to Pro →</div>
              </div>
            </button>
            <button onClick={()=>window.open("https://buy.stripe.com/bJefZidke9zVdJBf0ggfu01","_blank","noopener,noreferrer")} style={{background:"#7c3aed0d",border:"2px solid #7c3aed",borderRadius:14,padding:0,cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",overflow:"hidden",transition:"all 0.15s",boxShadow:"0 0 20px #7c3aed22"}} onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 0 28px #7c3aed44";}} onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 0 20px #7c3aed22";}}>
              <div style={{background:"linear-gradient(90deg,#7c3aed,#5b4fe8)",padding:"6px 12px",textAlign:"center"}}>
                <div style={{fontSize:9,fontWeight:700,color:"#fff",letterSpacing:"0.01em"}}>First month 50% off</div>
              </div>
              <div style={{padding:"12px 12px 16px",display:"flex",flexDirection:"column",gap:6,flex:1}}>
                <div style={{fontSize:22}}>👑</div>
                <div style={{fontSize:14,fontWeight:800,color:"#7c3aed"}}>Premier</div>
                <div style={{fontSize:11,color:T.textMuted}}>$19 / month</div>
                <div style={{height:1,background:"#7c3aed30",margin:"4px 0"}}/>
                <div style={{fontSize:9,color:"#c4b5fd",fontStyle:"italic",marginBottom:2}}>Everything in Pro, plus:</div>
                <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                  {["AI resume writer & critique","AI cover letter generator","AI bid writer for contracts","PDF & Word downloads","Priority support"].map(f=>(
                    <div key={f} style={{fontSize:10,color:T.textPrimary,display:"flex",gap:4,alignItems:"flex-start",lineHeight:1.4}}><span style={{color:"#c4b5fd",flexShrink:0,marginTop:1}}>·</span>{f}</div>
                  ))}
                </div>
                <div style={{marginTop:"auto",paddingTop:8}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#fff",textAlign:"center",background:"#7c3aed",borderRadius:8,padding:"6px 8px"}}>Upgrade to Premier →</div>
                </div>
              </div>
            </button>
          </div>
        </div>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{width:60,height:60,borderRadius:18,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,margin:"0 auto 14px",boxShadow:"0 0 50px #2563eb44"}}>V</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:T.textPrimary,letterSpacing:"-0.03em"}}>Vaultr</div>
          <div style={{fontSize:14,color:T.textSecondary,marginTop:6}}>Vault into your future. Together.</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          {[{type:"seeker",icon:"🚀",title:"Job Seeker",sub:"Find your next role, share your resume safely, get community support"},{type:"employer",icon:"🏢",title:"Employer",sub:"Search talent, post jobs, connect with verified candidates"}].map(opt=>(
            <button key={opt.type} onClick={()=>setUserType(opt.type)} style={{background:T.card,border:`1.5px solid ${T.border}`,borderRadius:18,padding:"24px 18px",cursor:"pointer",textAlign:"left",transition:"all 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=opt.type==="employer"?T.employer:T.accentBright;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;}}>
              <div style={{fontSize:32,marginBottom:10}}>{opt.icon}</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:15,color:T.textPrimary,marginBottom:6}}>{opt.title}</div>
              <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.5}}>{opt.sub}</div>
            </button>
          ))}
        </div>
        <div style={{textAlign:"center",marginTop:20}}>
          <a href="mailto:support@vaultrapps.com" style={{fontSize:12,color:T.textMuted,textDecoration:"none"}}>✉️ Questions? <span style={{color:T.accentGlow}}>support@vaultrapps.com</span></a>
        </div>
        <div style={{textAlign:"center",marginTop:12}}>
          <div style={{color:"#445577",fontSize:10,padding:"4px"}}>
            © 2026 Vaultr LLC
          </div>
        </div>
      </div>
    </div>
  );

  const isEmp = userType==="employer";
  const accentCol = isEmp ? T.employer : T.accent;

  // Email confirmation screen
  if (mode==="confirm") return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <STYLES/>
      <div style={{animation:"fadeUp 0.4s ease",width:"100%",maxWidth:400,textAlign:"center"}}>
        <div style={{fontSize:56,marginBottom:16}}>📧</div>
        <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:24,color:T.textPrimary,marginBottom:10}}>Check your email!</div>
        <div style={{fontSize:14,color:T.textSecondary,lineHeight:1.65,marginBottom:20}}>
          We sent a confirmation link to <strong style={{color:T.textPrimary}}>{form.email}</strong>. Click the link to activate your account then come back to sign in.
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 18px",marginBottom:20,fontSize:13,color:T.textMuted}}>
          💡 Don't see it? Check your spam folder or contact <a href="mailto:support@vaultrapps.com" style={{color:T.accentGlow}}>support@vaultrapps.com</a>
        </div>
        <button onClick={()=>setMode("login")} style={{background:accentCol,border:"none",color:"#fff",borderRadius:12,padding:"12px 28px",fontWeight:800,fontSize:14,cursor:"pointer"}}>
          Back to Sign In →
        </button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <STYLES/>
      <div style={{animation:"fadeUp 0.4s ease",width:"100%",maxWidth:mode==="signup"&&signupStep==="plan"?600:400,transition:"max-width 0.3s ease"}}>
        <button onClick={()=>setUserType(null)} style={{background:"none",border:"none",color:T.textSecondary,fontSize:13,cursor:"pointer",marginBottom:20}}>← Back</button>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{width:52,height:52,borderRadius:14,background:`linear-gradient(135deg,${accentCol},${accentCol}99)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 12px",boxShadow:`0 0 30px ${accentCol}44`}}>{isEmp?"🏢":"🚀"}</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:T.textPrimary}}>{isEmp?"Employer Portal":"Job Seeker Portal"}</div>
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:20,padding:"26px 22px"}}>
          <div style={{display:"flex",background:T.surface,borderRadius:12,padding:4,marginBottom:22}}>
            {["login","signup"].map(m=>(
              <button key={m} onClick={()=>{setMode(m);setError("");setResetSent(false);setSignupStep("details");setSelectedPlan("free");}} style={{flex:1,background:mode===m?accentCol:"transparent",color:mode===m?"#fff":T.textSecondary,border:"none",borderRadius:9,padding:"8px",fontSize:13,fontWeight:700,cursor:"pointer",transition:"all 0.2s"}}>
                {m==="login"?"Sign In":"Sign Up"}
              </button>
            ))}
          </div>

          {/* Forgot Password Mode */}
          {mode==="forgot" && (
            <div>
              <button onClick={()=>{setMode("login");setError("");setResetSent(false);}} style={{background:"none",border:"none",color:T.textMuted,fontSize:12,cursor:"pointer",marginBottom:14,display:"flex",alignItems:"center",gap:4}}>← Back to Sign In</button>
              {resetSent ? (
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:36,marginBottom:12}}>📧</div>
                  <div style={{fontWeight:700,fontSize:15,color:T.green,marginBottom:8}}>Check Your Email!</div>
                  <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:16}}>
                    A password reset link has been sent to <strong style={{color:T.textPrimary}}>{form.email}</strong>. Check your inbox — it should arrive within a few minutes. Be sure to check your spam folder!
                  </div>
                  <div style={{fontSize:12,color:T.textMuted,marginBottom:16,padding:"10px 12px",background:T.accentSoft,borderRadius:8}}>
                    💡 For faster help email us directly at <a href={`mailto:support@vaultrapps.com?subject=Password Reset Request&body=Please reset the password for: ${form.email}`} style={{color:T.accentGlow}}>support@vaultrapps.com</a>
                  </div>
                  <button onClick={()=>{setMode("login");setResetSent(false);}} style={{background:accentCol,border:"none",color:"#fff",borderRadius:12,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Back to Sign In</button>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:13,color:T.textSecondary,marginBottom:14,lineHeight:1.55}}>Enter your email address and we'll send you a link to reset your password.</div>
                  <input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&handleReset()} placeholder="Email address" type="email" style={IS}/>
                  {error&&<div style={{marginTop:8,fontSize:12,color:T.red,background:"#ef444415",border:"1px solid #ef444430",borderRadius:8,padding:"8px 12px"}}>{error}</div>}
                  <button onClick={handleReset} disabled={loading} style={{width:"100%",marginTop:14,background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:"#fff",borderRadius:12,padding:"13px",fontWeight:800,fontSize:14,cursor:loading?"not-allowed":"pointer",opacity:loading?0.7:1}}>
                    {loading?"Sending...":"Send Reset Link →"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Feature Comparison Table — shown above signup form */}
          {mode==="signup"&&signupStep==="details"&&<div style={{borderRadius:14,overflow:"hidden",border:"1px solid #5b4fe855",boxShadow:"0 0 28px #5b4fe818",marginBottom:16}}>
            <div style={{background:"linear-gradient(135deg,#5b4fe8,#7c3aed)",padding:"13px 16px",display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
              <span style={{fontSize:18}}>🎉</span>
              <div style={{fontSize:12,fontWeight:800,color:"#fff",letterSpacing:"-0.01em",textAlign:"center",lineHeight:1.4}}>Every new account gets a free 3-day Pro trial — no credit card required.</div>
              <span style={{fontSize:18}}>🎉</span>
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",background:T.card}}>
              <thead>
                <tr style={{background:T.surface}}>
                  <th style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",borderBottom:`1px solid ${T.border}`}}>Feature</th>
                  <th style={{padding:"9px 6px",textAlign:"center",fontSize:10,fontWeight:800,color:"#94a3b8",borderBottom:`1px solid ${T.border}`,minWidth:44}}>Free</th>
                  <th style={{padding:"9px 6px",textAlign:"center",fontSize:10,fontWeight:800,color:"#a78bfa",borderBottom:`1px solid ${T.border}`,background:"#5b4fe812",minWidth:58}}>⚡ Pro</th>
                  <th style={{padding:"9px 6px",textAlign:"center",fontSize:10,fontWeight:800,color:"#c4b5fd",borderBottom:`1px solid ${T.border}`,background:"#7c3aed12",minWidth:64}}>👑 Premier</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Job Search",                  "✅","✅","✅"],
                  ["AI Career Coach",             "1/day","Unlimited","Unlimited"],
                  ["Resume Upload",               "✅","✅","✅"],
                  ["Resume Visible to Employers", "🔒","✅","✅"],
                  ["Save Jobs",                   "🔒","✅","✅"],
                  ["Apply Direct",                "🔒","✅","✅"],
                  ["Approve Employer Contact",    "✅","✅","✅"],
                  ["AI Resume Critique",          "🔒","🔒","✅"],
                  ["AI Cover Letter",             "🔒","🔒","✅"],
                  ["Contract Bids",               "🔒","🔒","✅"],
                ].map(([feat,free,pro,premier],ri)=>(
                  <tr key={feat} style={{background:ri%2===0?"transparent":"#ffffff04"}}>
                    <td style={{padding:"7px 12px",fontSize:11,color:T.textPrimary,fontWeight:500,borderBottom:`1px solid ${T.border}33`}}>{feat}</td>
                    {[free,pro,premier].map((val,ci)=>(
                      <td key={ci} style={{padding:"7px 6px",textAlign:"center",borderBottom:`1px solid ${T.border}33`,background:ci===1?"#5b4fe808":ci===2?"#7c3aed08":"transparent",color:val==="✅"?"#22c55e":val==="🔒"?"#334155":val==="Unlimited"?"#a78bfa":"#f59e0b",fontSize:val==="✅"||val==="🔒"?13:10,fontWeight:700}}>
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
          {/* Normal login/signup fields */}
          {mode!=="forgot"&&!(mode==="signup"&&signupStep==="plan")&&<div style={{display:"flex",flexDirection:"column",gap:11}}>
            {mode==="signup"&&<input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Full name" style={IS}/>}
            {mode==="signup"&&!isEmp&&<input value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} placeholder="Job title" style={IS}/>}
            {mode==="signup"&&isEmp&&<input value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))} placeholder="Company name" style={IS}/>}
            {mode==="signup"&&isEmp&&<input value={form.website||""} onChange={e=>setForm(f=>({...f,website:e.target.value}))} placeholder="Company website (e.g. yourcompany.com)" style={IS}/>}
            {mode==="signup"&&isEmp&&<div style={{background:"#f59e0b10",border:"1px solid #f59e0b33",borderRadius:10,padding:"10px 12px"}}>
              <div style={{fontSize:12,color:T.gold,fontWeight:700,marginBottom:4}}>🔍 Employer Verification</div>
              <div style={{fontSize:11,color:T.textSecondary,lineHeight:1.5}}>All employer accounts are reviewed for legitimacy before being approved to contact candidates. This protects our job seekers. You will receive an email confirmation within 24-48 hours.</div>
            </div>}
            <input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="Email address" type="email" style={IS}/>
            <div style={{position:"relative"}}>
              <input value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&handle()} placeholder="Password" type={showPw?"text":"password"} style={{...IS,paddingRight:42}}/>
              <button onClick={()=>setShowPw(s=>!s)} type="button" style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,color:T.textMuted,padding:0,lineHeight:1}}>
                {showPw?"🙈":"👁️"}
              </button>
            </div>
          </div>}
          {mode==="signup"&&signupStep==="plan"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Trial headline */}
            <div style={{background:"linear-gradient(135deg,#5b4fe825,#7c3aed18)",border:"1px solid #7c3aed44",borderRadius:14,padding:"15px 18px",textAlign:"center"}}>
              <div style={{fontSize:17,fontWeight:800,color:"#c4b5fd",marginBottom:5,fontFamily:"'Syne',sans-serif",letterSpacing:"-0.02em"}}>🎉 3 days of Pro — free. No card required.</div>
              <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.6,maxWidth:460,margin:"0 auto"}}>Every new Vaultr account starts with a free 3-day Pro trial. Pick any plan — you won't be charged until your trial ends, and you can cancel anytime.</div>
            </div>
            {/* Comparison table */}
            <div style={{borderRadius:14,overflow:"hidden",border:`1px solid ${T.border}`,background:T.card}}>
              {/* Header row */}
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1.1fr 1.1fr"}}>
                <div style={{padding:"12px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-end"}}>Plan features</div>
                {[{plan:"free",label:"Free",price:"$0",icon:"🔓",accent:"#94a3b8",bg:"#64748b"},{plan:"pro",label:"Pro",price:"$9",icon:"⚡",accent:"#a78bfa",bg:"#5b4fe8",badge:"⭐ Popular"},{plan:"premier",label:"Premier",price:"$19",icon:"👑",accent:"#c4b5fd",bg:"#7c3aed",badge:"🔥 Powerful"}].map(({plan,label,price,icon,accent,bg,badge})=>(
                  <button key={plan} onClick={()=>setSelectedPlan(plan)} style={{padding:"12px 6px",background:selectedPlan===plan?`${bg}20`:"transparent",border:"none",borderLeft:`1px solid ${T.border}`,borderBottom:`2px solid ${selectedPlan===plan?bg:T.border}`,cursor:"pointer",textAlign:"center",transition:"all 0.15s",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                    <div style={{fontSize:11,fontWeight:700,color:accent}}>{icon} {label}</div>
                    <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:accent,lineHeight:1}}>{price}<span style={{fontSize:10,fontWeight:400,color:T.textMuted}}>/mo</span></div>
                    {badge?<div style={{background:`${bg}25`,borderRadius:8,padding:"2px 6px",fontSize:8,fontWeight:700,color:accent}}>{badge}</div>:<div style={{height:18}}/>}
                    {selectedPlan===plan?<div style={{background:bg,borderRadius:8,padding:"3px 8px",fontSize:9,fontWeight:700,color:"#fff"}}>✓ Selected</div>:<div style={{height:20}}/>}
                  </button>
                ))}
              </div>
              {/* Feature rows */}
              {[
                ["Browse verified jobs","✓","✓","✓"],
                ["Scam detection built-in","✓","✓","✓"],
                ["Community & mentorship","✓","✓","✓"],
                ["Full privacy controls","✓","✓","✓"],
                ["AI career coaching","1 / day","Unlimited","Unlimited"],
                ["Resume upload & sharing","–","✓","✓"],
                ["Apply directly to jobs","–","✓","✓"],
                ["Save jobs & track apps","–","✓","✓"],
                ["Direct employer messaging","–","✓","✓"],
                ["AI resume writer","–","–","✓"],
                ["AI cover letter generator","–","–","✓"],
                ["AI bid writer","–","–","✓"],
                ["PDF & Word downloads","–","–","✓"],
                ["Priority support","–","–","✓"],
              ].map(([feat,fv,pv,pmv],i)=>{
                const COLS=[{val:fv,plan:"free",accent:"#94a3b8",bg:"#64748b"},{val:pv,plan:"pro",accent:"#a78bfa",bg:"#5b4fe8"},{val:pmv,plan:"premier",accent:"#c4b5fd",bg:"#7c3aed"}];
                return(
                  <div key={feat} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1.1fr 1.1fr",background:i%2===0?T.surface:T.card,borderTop:`1px solid ${T.border}22`}}>
                    <div style={{padding:"9px 14px",fontSize:11,color:T.textSecondary,display:"flex",alignItems:"center",lineHeight:1.3}}>{feat}</div>
                    {COLS.map(({val,plan,accent,bg})=>(
                      <div key={plan} onClick={()=>setSelectedPlan(plan)} style={{padding:"9px 4px",borderLeft:`1px solid ${T.border}22`,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",background:selectedPlan===plan?`${bg}0d`:"transparent",transition:"background 0.1s"}}>
                        {val==="✓"?<span style={{color:"#22c55e",fontSize:15,fontWeight:800,lineHeight:1}}>✓</span>:val==="–"?<span style={{color:T.textMuted,fontSize:12}}>–</span>:<span style={{fontSize:9,fontWeight:700,color:accent,lineHeight:1.3,display:"block",textAlign:"center"}}>{val}</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            {/* Selected plan summary */}
            <div style={{background:T.surface,border:`1px solid ${selectedPlan==="free"?"#64748b":selectedPlan==="pro"?"#5b4fe8":"#7c3aed"}33`,borderRadius:11,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <div style={{fontSize:12,color:T.textSecondary}}>Starting with: <strong style={{color:selectedPlan==="free"?"#94a3b8":selectedPlan==="pro"?"#a78bfa":"#c4b5fd"}}>{selectedPlan==="free"?"Free — $0/mo":selectedPlan==="pro"?"Pro — $9/mo after trial":"Premier — $19/mo after trial"}</strong></div>
              <div style={{fontSize:11,fontWeight:700,color:"#22c55e",flexShrink:0}}>3 days free ✓</div>
            </div>
          </div>}
          {/* Session Options — login only */}
          {mode==="login"&&(
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 14px",marginTop:6}}>
              {/* Remember Me */}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <button onClick={()=>setRememberMe(r=>!r)} type="button"
                  style={{width:18,height:18,borderRadius:4,background:rememberMe?T.accent:"transparent",border:`2px solid ${rememberMe?T.accent:T.border}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}>
                  {rememberMe&&<span style={{color:"#fff",fontSize:11,lineHeight:1}}>✓</span>}
                </button>
                <div>
                  <span style={{fontSize:12,color:T.textPrimary,fontWeight:600}}>Remember me</span>
                  <span style={{fontSize:11,color:T.textMuted}}> — stay logged in for 30 days</span>
                </div>
              </div>
              {/* Session info message */}
              <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5,marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>
                🔒 For your security, you will be automatically signed out after <strong style={{color:T.textSecondary}}>7 days of inactivity</strong> — even if Remember Me is on.
              </div>
              {/* Security mode toggle */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:12,color:T.textPrimary,fontWeight:600}}>🛡️ High Security Mode</div>
                  <div style={{fontSize:11,color:T.textMuted}}>Auto sign out after 30 minutes of inactivity</div>
                </div>
                <button onClick={()=>setHighSecurity(s=>!s)}
                  style={{width:44,height:24,borderRadius:12,background:highSecurity?T.red:T.border,border:"none",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:highSecurity?23:3,transition:"left 0.2s"}}/>
                </button>
              </div>
              {highSecurity&&(
                <div style={{marginTop:8,fontSize:11,color:T.red,background:"#ef444410",border:"1px solid #ef444430",borderRadius:8,padding:"6px 10px"}}>
                  ⚠️ You will be signed out after 30 minutes of no activity
                </div>
              )}
            </div>
          )}
          {mode!=="forgot"&&<>{error&&<div style={{marginTop:10,fontSize:12,color:T.red,background:"#ef444415",border:"1px solid #ef444430",borderRadius:8,padding:"8px 12px"}}>{error}</div>}</>}
          {mode!=="forgot"&&(
          <button onClick={handle} disabled={loading} style={{width:"100%",marginTop:16,background:`linear-gradient(135deg,${accentCol},${accentCol}bb)`,border:"none",color:"#fff",borderRadius:12,padding:"13px",fontWeight:800,fontSize:14,cursor:loading?"not-allowed":"pointer",opacity:loading?0.7:1}}>
            {mode==="login"?"Sign In →":signupStep==="details"?"Next: Choose Plan →":"Start my free 3-day trial →"}
          </button>)}
          {mode==="signup"&&signupStep==="plan"&&<div style={{textAlign:"center",marginTop:6}}>
            <button onClick={()=>{setSignupStep("details");setError("");}} style={{background:"none",border:"none",color:T.textMuted,fontSize:12,cursor:"pointer"}}>← Back to your details</button>
          </div>}
          {mode==="login"&&(
            <div style={{textAlign:"center",marginTop:10}}>
              <button onClick={()=>setMode("forgot")} style={{background:"none",border:"none",color:T.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline"}}>
                Forgot your password?
              </button>
            </div>
          )}
          {mode!=="forgot"&&signupStep!=="plan"&&<>
          <div style={{textAlign:"center",margin:"14px 0 0"}}>
            <div style={{height:1,background:T.border,marginBottom:-7}}/>
            <span style={{background:T.card,padding:"0 10px",fontSize:11,color:T.textMuted,position:"relative"}}>or try demo</span>
          </div>
          <button onClick={()=>{const d=isEmp?DB.employers[0]:DB.seekers[0];onAuth(d);}} style={{width:"100%",marginTop:16,background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"11px",fontWeight:600,fontSize:13,cursor:"pointer"}}>
            {isEmp?"🏢 Demo Employer Account":"🚀 Demo Seeker Account"}
          </button>
          </>}
        </div>
        <div style={{textAlign:"center",marginTop:12}}>
          <a href="mailto:support@vaultrapps.com" style={{fontSize:11,color:T.accentGlow,textDecoration:"none"}}>✉️ Need help? support@vaultrapps.com</a>
          <div style={{marginTop:8,textAlign:"center",color:"#445577",fontSize:10,padding:"4px"}}>© 2026 Vaultr LLC</div>
        </div>
      </div>
    </div>
  );
}

// ─---JOB SEARCH---─────────────────────────────────────────────────────────────
const BLOCKED_PUBLISHERS = [
  "Vaia", "Jobgether", "Experteer", "Jobrapido", "Jooble"
];

async function fetchJobsFromAI({ keyword, location, category, jobTypes = [] }) {
  const loc = location === "Nationwide" ? "" : location;
  const catMap = {
    engineering:"software engineer", design:"UX UI designer",
    data:"data scientist", marketing:"marketing manager",
    finance:"finance accountant", healthcare:"nurse healthcare",
    sales:"sales representative", remote:"remote", all: keyword || "jobs"
  };
  const searchTerm = keyword || catMap[category] || "jobs";
  const typeStr = jobTypes && jobTypes.length > 0 ? ` ${jobTypes[0].toLowerCase()}` : "";
  const query = `${searchTerm}${typeStr}`;

  const params = new URLSearchParams({ q: query });
  if (loc) params.set("location", loc);

  const response = await fetch(`/api/jobs?${params}`);
  const data = await response.json();
  if (!data.jobs_results) return [];

  const blockedLower = BLOCKED_PUBLISHERS.map(p => p.toLowerCase());
  const filtered = data.jobs_results.filter(j => {
    const publisher = (j.via || "").replace(/^via\s+/i, "").toLowerCase();
    return !blockedLower.includes(publisher);
  });

  return filtered.slice(0, 10).map((j, i) => ({
    id: j.job_id || `job-${i}-${Date.now()}`,
    title: j.title || "Untitled",
    company: j.company_name || "Unknown Company",
    location: j.location || "Not specified",
    salary: j.detected_extensions?.salary || null,
    type: j.detected_extensions?.schedule_type || "Full-time",
    tags: (j.extensions || []).filter(Boolean).slice(0, 4),
    posted: j.detected_extensions?.posted_at || "Recently",
    desc: j.description ? j.description.slice(0, 300) + "..." : "",
    applyUrl: j.apply_options?.[0]?.link || null,
    searchUrl: j.apply_options?.[0]?.link || `https://www.google.com/search?q=${encodeURIComponent(query)}+jobs`,
    logo: (j.company_name || "?")[0].toUpperCase(),
    logoColor: ["#635bff","#ff5a5f","#1abcfe","#58cc02","#f59e0b","#ef4444","#06b6d4","#8b5cf6"][(j.company_name || "?").charCodeAt(0) % 8],
    source: "SerpApi",
  }));
}

// ─---JOB CARD COMPONENT---─────────────────────────────────────────────────────
function JobCard({ job, v, sel, setSel, saved, setSaved, applied, setApplied, starred, setStarred, isPro, onUpgrade }) {
  const [showSuspicious, setShowSuspicious] = useState(false);
  const [showApplyPrompt, setShowApplyPrompt] = useState(false);

  if (v.tier === "suspicious" && !showSuspicious) {
    return (
      <div style={{background:`${T.red}08`,border:`1px solid ${T.red}33`,borderRadius:14,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18}}>🚨</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,color:T.red,fontWeight:600}}>{job.title} — {job.company}</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Flagged as suspicious and hidden for your safety</div>
        </div>
        <button onClick={()=>setShowSuspicious(true)} style={{background:"transparent",border:`1px solid ${T.red}44`,color:T.red,borderRadius:8,padding:"5px 10px",fontSize:11,cursor:"pointer",flexShrink:0}}>View anyway</button>
      </div>
    );
  }

  return (
    <div onClick={()=>setSel(sel?.id===job.id?null:job)}
      style={{background:T.card,border:`1px solid ${sel?.id===job.id?T.accentBright+"88":v.borderColor}`,borderRadius:14,padding:"15px 16px",cursor:"pointer",transition:"all 0.15s",position:"relative"}}
      onMouseEnter={e=>{if(sel?.id!==job.id){e.currentTarget.style.background=T.cardHover;}}}
      onMouseLeave={e=>{if(sel?.id!==job.id){e.currentTarget.style.background=T.card;}}}>

      {v.tier==="suspicious"&&showSuspicious&&<div style={{background:"#ef444415",border:`1px solid ${T.red}44`,borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:T.red}}>🚨 Flagged posting — never pay fees or share financial info.</div>}
      {v.tier==="unverified"&&<div style={{background:"#f59e0b10",border:`1px solid ${T.gold}44`,borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:T.gold}}>⚠️ Unverified — research this company before applying.</div>}
{v.tier==="suspicious"&&showSuspicious&&<div style={{background:"#ef444415",border:`1px solid ${T.red}44`,borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:T.red}}>🚨 Flagged posting — never pay fees or share financial info.</div>}
{v.tier==="unverified"&&<div style={{background:"#f59e0b10",border:`1px solid ${T.gold}44`,borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:T.gold}}>⚠️ Unverified — research this company before applying.</div>}

{/* ✅ ADD THIS */}
{v.tier === "verified" && (job.id?.charCodeAt(0) % 3 === 0) && (
  <div style={{background:"#22c55e12",border:"1px solid #22c55e44",borderRadius:10,padding:"6px 12px",marginBottom:12,fontSize:12,color:"#22c55e",display:"flex",alignItems:"center",gap:6}}>
    <span>🟢</span><span style={{fontWeight:600}}>Actively Hiring on Vaultr</span>
  </div>
)}
      <div style={{display:"flex",gap:11,alignItems:"flex-start"}}>
        <div style={{position:"relative"}}>
          <div style={{width:42,height:42,borderRadius:10,background:`${job.logoColor}22`,border:`1.5px solid ${job.logoColor}44`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:15,color:job.logoColor,flexShrink:0}}>{job.logo}</div>
          <div style={{position:"absolute",bottom:-4,right:-4,fontSize:12,background:T.bg,borderRadius:"50%",lineHeight:1}}>{v.tier==="verified"?"✅":v.tier==="unverified"?"⚠️":"🚨"}</div>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,color:T.textPrimary,lineHeight:1.3}}>{job.title}</div>
          <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>{job.company} · {job.location}</div>
          {job.salary&&<div style={{display:"inline-block",fontSize:11,fontWeight:700,color:T.green,background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:6,padding:"2px 8px",marginTop:4}}>{job.salary}</div>}
          <div style={{display:"flex",gap:4,marginTop:7,flexWrap:"wrap",alignItems:"center"}}>
            {job.tags.map(t=><Pill key={t} color={T.accentBright}>{t}</Pill>)}
            <span style={{fontSize:10,color:T.textMuted,marginLeft:"auto"}}>via {job.source} · {job.posted}</span>
          </div>
          <VerificationBadge job={job} expanded={false}/>
        </div>
      </div>
      <button onClick={e=>{e.stopPropagation();setStarred(s=>{const n=new Set(s);n.has(job.id)?n.delete(job.id):n.add(job.id);return n;})}} title={starred.has(job.id)?"Unstar":"Star this job"} style={{position:"absolute",top:10,right:10,background:starred.has(job.id)?"#f59e0b22":"#ffffff0d",border:`1px solid ${starred.has(job.id)?"#f59e0b55":"#334466"}`,borderRadius:8,cursor:"pointer",fontSize:16,lineHeight:1,padding:"4px 7px",color:starred.has(job.id)?"#f59e0b":"#8899bb",zIndex:1}}>
        {starred.has(job.id)?"⭐":"☆"}
      </button>

      {sel?.id===job.id&&(
        <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
          {job.desc&&<p style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:12}}>{job.desc}</p>}
          <VerificationBadge job={job} expanded={true}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginTop:12}}>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              {job.salary&&<span style={{fontWeight:800,fontSize:14,color:T.green}}>{job.salary}</span>}
              <Pill color={T.textSecondary}>{job.type}</Pill>
            </div>
            <div style={{display:"flex",gap:8}}>
              {isPro ? (
                <button onClick={e=>{e.stopPropagation();setSaved(s=>{const n=new Set(s);n.has(job.id)?n.delete(job.id):n.add(job.id);return n;})}}
                  style={{background:saved.has(job.id)?"#f59e0b18":"transparent",border:`1px solid ${saved.has(job.id)?T.gold:T.border}`,color:saved.has(job.id)?T.gold:T.textSecondary,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                  {saved.has(job.id)?"★ Saved":"☆ Save"}
                </button>
              ) : (
                <button onClick={e=>{e.stopPropagation();onUpgrade();}}
                  style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                  🔒 Save
                </button>
              )}
              {/* Apply — Pro only — goes direct to company careers page */}
              {isPro && job.applyUrl && (
  <a href={job.applyUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
    style={{background:"transparent",border:`1px solid ${job.isDirect?T.green:T.gold}`,color:job.isDirect?T.green:T.gold,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4}}>
    {job.isDirect?"🏢 Apply Direct":"🔗 Apply via Indeed"}
  </a>
              )}
              {v.applyAllowed ? (
                <button onClick={e=>{e.stopPropagation();if(!isPro){onUpgrade();return;}if(applied.has(job.id)){setApplied(s=>{const n=new Set(s);n.delete(job.id);return n;});}else{setApplied(s=>{const n=new Set(s);n.add(job.id);return n;});if(job.applyUrl)window.open(job.applyUrl,"_blank");setShowApplyPrompt(true);}}}
                  style={{background:applied.has(job.id)?T.green:v.tier==="verified"?T.accent:"#92400e",border:"none",color:"#fff",borderRadius:8,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  {applied.has(job.id)?"✓ Applied":isPro?(v.tier==="verified"?"Apply ↗":"Apply with Caution"):"🔒 Apply"}
                </button>
              ) : (
                <button onClick={e=>e.stopPropagation()} style={{background:"#ef444415",border:`1px solid ${T.red}44`,color:T.red,borderRadius:8,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"not-allowed"}}>
                  🚨 Blocked
                </button>
              )}
            </div>
          </div>
          {showApplyPrompt&&(
            <div style={{marginTop:10,padding:"12px 14px",background:"#2563eb0e",border:`1px solid ${T.accent}44`,borderRadius:12}}>
              <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:8}}>Did you successfully apply? Add this to your application tracker?</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={e=>{e.stopPropagation();const entry={id:job.id,title:job.title,company:job.company,date:new Date().toLocaleDateString(),status:"Applied"};const prev=JSON.parse(localStorage.getItem("vaultr_applied")||"[]");localStorage.setItem("vaultr_applied",JSON.stringify([entry,...prev.filter(x=>x.id!==job.id)]));setShowApplyPrompt(false);}}
                  style={{background:T.accent,border:"none",color:"#fff",borderRadius:8,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Yes, track it ✓</button>
                <button onClick={e=>{e.stopPropagation();setShowApplyPrompt(false);}}
                  style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>No thanks</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─---SEEKER JOBS---────────────────────────────────────────────────────────────
// ─── JOB MARKET INSIGHTS ─────────────────────────────────────────────────────
const TOP_JOBS = [
  {title:"Registered Nurse",demand:"Very High",growth:"+6%",icon:"🏥",color:"#10b981",
    education:"Associate's or Bachelor's degree in Nursing (ADN or BSN)",
    experience:"1-2 years clinical experience preferred; new grads welcome",
    certifications:"RN license required · BLS/CPR certified · NCLEX-RN",
    skills:["Patient care","Medical terminology","Electronic health records","IV therapy","Team communication"]},
  {title:"Software Engineer",demand:"Very High",growth:"+25%",icon:"💻",color:"#3b82f6",
    education:"Bachelor's degree in Computer Science or related field (or equivalent experience)",
    experience:"2-5 years for mid-level; entry level with portfolio/bootcamp accepted",
    certifications:"AWS/Google Cloud certifications a plus · No required certs for most roles",
    skills:["Python/JavaScript/Java","Git","Problem solving","APIs","Agile/Scrum"]},
  {title:"Truck Driver",demand:"Very High",growth:"+4%",icon:"🚛",color:"#f59e0b",
    education:"High school diploma or GED",
    experience:"Entry level accepted with CDL; 1-2 years preferred",
    certifications:"Commercial Driver's License (CDL-A) required · DOT Medical Card · Clean driving record",
    skills:["Safe driving","Route planning","Vehicle inspection","Log books","Time management"]},
  {title:"Sales Representative",demand:"High",growth:"+5%",icon:"🤝",color:"#8b5cf6",
    education:"High school diploma minimum; Bachelor's degree preferred",
    experience:"1-3 years sales experience; entry level roles available",
    certifications:"No required certs · Salesforce or CRM training a plus",
    skills:["Communication","Negotiation","CRM software","Lead generation","Goal driven"]},
  {title:"Medical Assistant",demand:"High",growth:"+16%",icon:"⚕️",color:"#06b6d4",
    education:"Postsecondary certificate or Associate's degree in Medical Assisting",
    experience:"Entry level welcome; externship experience accepted",
    certifications:"CMA (AAMA) or RMA preferred · BLS/CPR required",
    skills:["Vital signs","Patient intake","EHR systems","Phlebotomy","Office administration"]},
  {title:"Customer Service Rep",demand:"High",growth:"+5%",icon:"📞",color:"#f97316",
    education:"High school diploma or GED",
    experience:"Entry level; 1 year preferred",
    certifications:"No required certifications",
    skills:["Communication","Problem solving","CRM software","Patience","Multi-tasking"]},
  {title:"Warehouse Associate",demand:"High",growth:"+6%",icon:"📦",color:"#84cc16",
    education:"High school diploma or GED",
    experience:"Entry level; no experience required for most roles",
    certifications:"Forklift certification a plus · OSHA safety training",
    skills:["Physical stamina","Inventory management","Forklift operation","Attention to detail","Teamwork"]},
  {title:"Data Analyst",demand:"High",growth:"+23%",icon:"📊",color:"#ec4899",
    education:"Bachelor's degree in Statistics, Math, Computer Science or related field",
    experience:"1-3 years; internship experience accepted",
    certifications:"Google Data Analytics Certificate · Tableau or Power BI certifications",
    skills:["SQL","Excel","Python/R","Data visualization","Critical thinking"]},
  {title:"Project Manager",demand:"High",growth:"+7%",icon:"📋",color:"#14b8a6",
    education:"Bachelor's degree in Business, Management or related field",
    experience:"3-5 years project coordination experience",
    certifications:"PMP (Project Management Professional) preferred · Agile/Scrum Master",
    skills:["Leadership","Risk management","Budgeting","Stakeholder communication","MS Project/Jira"]},
  {title:"Electrician",demand:"High",growth:"+6%",icon:"⚡",color:"#eab308",
    education:"High school diploma + apprenticeship program (4-5 years)",
    experience:"Apprenticeship required; journeyman license after completion",
    certifications:"Journeyman or Master Electrician License · OSHA 10/30 · NEC knowledge",
    skills:["Electrical systems","Blueprint reading","Problem solving","Safety compliance","Hand tools"]},
];

const PAY_SCALE = [
  {title:"Software Engineer",entry:"$75,000",mid:"$110,000",senior:"$150,000+",icon:"💻"},
  {title:"Registered Nurse",entry:"$58,000",mid:"$78,000",senior:"$100,000+",icon:"🏥"},
  {title:"Data Analyst",entry:"$55,000",mid:"$75,000",senior:"$110,000+",icon:"📊"},
  {title:"Project Manager",entry:"$65,000",mid:"$90,000",senior:"$130,000+",icon:"📋"},
  {title:"Marketing Manager",entry:"$55,000",mid:"$80,000",senior:"$120,000+",icon:"📣"},
  {title:"Accountant",entry:"$50,000",mid:"$70,000",senior:"$100,000+",icon:"💰"},
  {title:"Electrician",entry:"$45,000",mid:"$65,000",senior:"$90,000+",icon:"⚡"},
  {title:"Truck Driver",entry:"$45,000",mid:"$60,000",senior:"$80,000+",icon:"🚛"},
  {title:"Customer Service",entry:"$32,000",mid:"$45,000",senior:"$65,000+",icon:"📞"},
  {title:"Medical Assistant",entry:"$35,000",mid:"$42,000",senior:"$55,000+",icon:"⚕️"},
];

const HOT_CITIES = [
  {city:"Austin, TX",growth:"+28%",industries:"Tech, Healthcare, Finance",icon:"🤠",color:"#f59e0b",reason:"Major tech hub with Tesla, Apple, Google campuses"},
  {city:"Nashville, TN",growth:"+22%",industries:"Healthcare, Finance, Tech",icon:"🎸",color:"#8b5cf6",reason:"Growing healthcare hub and corporate headquarters"},
  {city:"Phoenix, AZ",growth:"+20%",industries:"Tech, Manufacturing, Finance",icon:"🌵",color:"#ef4444",reason:"Booming tech scene and affordable cost of living"},
  {city:"Raleigh, NC",growth:"+19%",industries:"Tech, Biotech, Education",icon:"🔬",color:"#06b6d4",reason:"Research Triangle Park — top biotech and tech corridor"},
  {city:"Jacksonville, FL",growth:"+18%",industries:"Logistics, Healthcare, Finance",icon:"🌴",color:"#10b981",reason:"No state income tax and major logistics hub"},
  {city:"Charlotte, NC",growth:"+17%",industries:"Finance, Tech, Healthcare",icon:"🏦",color:"#3b82f6",reason:"Second largest banking center in the US"},
  {city:"Dallas, TX",growth:"+16%",industries:"Finance, Tech, Healthcare",icon:"⭐",color:"#f97316",reason:"Corporate HQ relocation hotspot — hundreds of companies moved here"},
  {city:"Columbus, OH",growth:"+15%",industries:"Tech, Retail, Finance",icon:"🏙️",color:"#14b8a6",reason:"Growing tech scene and low cost of living"},
  {city:"Indianapolis, IN",growth:"+14%",industries:"Healthcare, Manufacturing, Logistics",icon:"🏁",color:"#ec4899",reason:"Major healthcare hub and central logistics location"},
  {city:"Tampa, FL",growth:"+14%",industries:"Healthcare, Finance, Tech",icon:"☀️",color:"#eab308",reason:"Fast growing tech scene with no state income tax"},
];

const UPANDCOMING_CITIES = [
  {city:"Huntsville, AL",growth:"+31%",industries:"Aerospace, Defense, Tech",icon:"🚀",color:"#3b82f6",reason:"NASA and defense contractors driving massive job growth"},
  {city:"Boise, ID",growth:"+27%",industries:"Tech, Healthcare, Manufacturing",icon:"🏔️",color:"#10b981",reason:"Affordable Silicon Valley alternative attracting remote workers"},
  {city:"Greenville, SC",growth:"+24%",industries:"Manufacturing, Healthcare, Tech",icon:"🌿",color:"#84cc16",reason:"BMW and Michelin HQs driving manufacturing boom"},
  {city:"Spokane, WA",growth:"+21%",industries:"Healthcare, Education, Tech",icon:"🌲",color:"#06b6d4",reason:"Growing healthcare hub away from Seattle's high costs"},
  {city:"Provo, UT",growth:"+20%",industries:"Tech, Finance, Education",icon:"⛰️",color:"#8b5cf6",reason:"Silicon Slopes — fastest growing tech corridor in the US"},
  {city:"Fayetteville, AR",growth:"+19%",industries:"Retail, Tech, Logistics",icon:"🛒",color:"#f59e0b",reason:"Walmart HQ draws hundreds of supplier companies"},
  {city:"El Paso, TX",growth:"+18%",industries:"Healthcare, Military, Logistics",icon:"🌮",color:"#ef4444",reason:"Border economy booming with trade and military growth"},
  {city:"Chattanooga, TN",growth:"+17%",industries:"Manufacturing, Tech, Logistics",icon:"🌉",color:"#14b8a6",reason:"Fastest internet city in US — attracting remote tech workers"},
  {city:"Madison, WI",growth:"+16%",industries:"Biotech, Education, Healthcare",icon:"🧬",color:"#ec4899",reason:"University research driving biotech startup boom"},
  {city:"Omaha, NE",growth:"+15%",industries:"Finance, Insurance, Tech",icon:"🌾",color:"#f97316",reason:"Warren Buffett's backyard — major finance and insurance hub"},
];


// ─── EXPANDABLE INSIGHT CARD ─────────────────────────────────────────────────
function InsightJobCard({ job, onSearch }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:expanded?"12px 12px 0 0":"12px",background:T.surface,border:`1px solid ${expanded?job.color:T.border}`,cursor:"pointer",transition:"all 0.15s"}}
        onClick={()=>setExpanded(e=>!e)}>
        <div style={{width:28,height:28,borderRadius:8,background:`${job.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{job.icon}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{job.title}</div>
          <div style={{fontSize:10,color:T.textMuted}}>Demand: <span style={{color:job.color,fontWeight:700}}>{job.demand}</span></div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          <div style={{fontSize:10,fontWeight:700,color:T.green,background:`${T.green}15`,borderRadius:6,padding:"2px 6px"}}>{job.growth}</div>
          <div style={{fontSize:9,color:T.textMuted}}>job growth</div>
        </div>
        <span style={{fontSize:12,color:T.textMuted,marginLeft:4}}>{expanded?"▲":"▼"}</span>
      </div>
      {expanded && (
        <div style={{background:`${job.color}08`,border:`1px solid ${job.color}44`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"12px 14px"}}>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:700,color:job.color,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>🎓 Education</div>
            <div style={{fontSize:12,color:T.textSecondary}}>{job.education}</div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:700,color:job.color,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>💼 Experience</div>
            <div style={{fontSize:12,color:T.textSecondary}}>{job.experience}</div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:700,color:job.color,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>📜 Certifications</div>
            <div style={{fontSize:12,color:T.textSecondary}}>{job.certifications}</div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:job.color,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:6}}>⚡ Key Skills</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {job.skills.map(s=><span key={s} style={{background:`${job.color}20`,color:job.color,border:`1px solid ${job.color}44`,borderRadius:20,fontSize:10,fontWeight:600,padding:"2px 8px"}}>{s}</span>)}
            </div>
          </div>
          <button onClick={e=>{e.stopPropagation();onSearch(job.title);}}
            style={{width:"100%",background:job.color,border:"none",color:"#fff",borderRadius:8,padding:"8px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            🔍 Search {job.title} Jobs →
          </button>
        </div>
      )}
    </div>
  );
}

function CityCard({ city, type }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{marginBottom:8}}>
      <div onClick={()=>setExpanded(e=>!e)}
        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:expanded?"12px 12px 0 0":"12px",background:T.surface,border:`1px solid ${expanded?city.color:T.border}`,cursor:"pointer",transition:"all 0.15s"}}>
        <div style={{width:32,height:32,borderRadius:8,background:`${city.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{city.icon}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>{city.city}</div>
          <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{city.industries}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          <div style={{fontSize:11,fontWeight:800,color:type==="hot"?T.green:T.accentGlow,background:type==="hot"?`${T.green}15`:T.accentSoft,borderRadius:6,padding:"2px 8px"}}>{city.growth}</div>
          <div style={{fontSize:9,color:T.textMuted}}>{type==="hot"?"job growth":"projected"}</div>
        </div>
        <span style={{fontSize:12,color:T.textMuted,marginLeft:4}}>{expanded?"▲":"▼"}</span>
      </div>
      {expanded && (
        <div style={{background:`${city.color}08`,border:`1px solid ${city.color}44`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"12px 14px"}}>
          <div style={{fontSize:12,color:T.textSecondary,marginBottom:10,lineHeight:1.5}}>💡 {city.reason}</div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:city.color,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>🏭 {type==="hot"?"Top":"Growing"} Industries</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {city.industries.split(", ").map(ind=><span key={ind} style={{background:`${city.color}20`,color:city.color,border:`1px solid ${city.color}44`,borderRadius:20,fontSize:10,fontWeight:600,padding:"2px 8px"}}>{ind}</span>)}
            </div>
          </div>
          <a href={`https://www.indeed.com/jobs?l=${encodeURIComponent(city.city)}&fromage=14`} target="_blank" rel="noopener noreferrer"
            style={{display:"block",width:"100%",background:city.color,color:"#fff",borderRadius:8,padding:"8px",fontSize:12,fontWeight:700,textAlign:"center",textDecoration:"none"}}>
            🔍 Browse Jobs in {city.city} →
          </a>
        </div>
      )}
    </div>
  );
}

function JobMarketInsights({ user, onSearch }) {
  const [activeTab, setActiveTab] = useState("demand");
  const userLocation = user.location || "Nationwide";

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,marginBottom:16,overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#0a1628,#0c1a30)",padding:"14px 16px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <span style={{fontSize:18}}>📈</span>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:15,color:T.textPrimary}}>Job Market Insights</span>
        </div>
        <div style={{fontSize:11,color:T.textMuted}}>Real-time job market data to help you make informed career decisions</div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",background:T.surface,borderBottom:`1px solid ${T.border}`}}>
        {[
          {id:"demand",label:"🔥 In-Demand"},
          {id:"pay",label:"💰 Pay Scale"},
          {id:"local",label:"📍 Your Area"},
          {id:"hotcities",label:"🏙️ Hot Cities"},
          {id:"upcoming",label:"⬆️ Up & Coming"},
        ].map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
            style={{flex:1,background:"none",border:"none",padding:"10px 4px",fontSize:11,fontWeight:700,color:activeTab===tab.id?T.accentGlow:T.textMuted,borderBottom:`2px solid ${activeTab===tab.id?T.accentBright:"transparent"}`,cursor:"pointer"}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Most In-Demand Jobs */}
      {activeTab==="demand" && (
        <div style={{padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>Top 10 most sought-after positions nationwide · Click ▼ to see requirements</div>
          {TOP_JOBS.map((job)=>(
            <InsightJobCard key={job.title} job={job} onSearch={onSearch}/>
          ))}
          <div style={{fontSize:10,color:T.textMuted,marginTop:8,textAlign:"center"}}>Data based on BLS national statistics · Requirements may vary by employer</div>
        </div>
      )}

      {/* Pay Scale */}
      {activeTab==="pay" && (
        <div style={{padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>Average salary ranges by experience level across the US</div>
          {/* Header */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,marginBottom:6,padding:"0 10px"}}>
            <div style={{fontSize:10,fontWeight:700,color:T.textMuted}}>Position</div>
            <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textAlign:"center"}}>Entry</div>
            <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textAlign:"center"}}>Mid</div>
            <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textAlign:"center"}}>Senior</div>
          </div>
          {PAY_SCALE.map((job,i)=>(
            <div key={job.title} onClick={()=>onSearch(job.title)}
              style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,padding:"8px 10px",borderRadius:10,marginBottom:4,background:i%2===0?T.surface:"transparent",border:`1px solid ${i%2===0?T.border:"transparent"}`,cursor:"pointer",alignItems:"center"}}
              onMouseEnter={e=>{e.currentTarget.style.background=T.cardHover;e.currentTarget.style.borderColor=T.accentBright+"44";}}
              onMouseLeave={e=>{e.currentTarget.style.background=i%2===0?T.surface:"transparent";e.currentTarget.style.borderColor=i%2===0?T.border:"transparent";}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:14}}>{job.icon}</span>
                <div style={{fontSize:11,fontWeight:600,color:T.textPrimary,lineHeight:1.2}}>{job.title}</div>
              </div>
              <div style={{fontSize:11,color:T.textSecondary,textAlign:"center"}}>{job.entry}</div>
              <div style={{fontSize:11,color:T.accentGlow,fontWeight:700,textAlign:"center"}}>{job.mid}</div>
              <div style={{fontSize:11,color:T.green,fontWeight:700,textAlign:"center"}}>{job.senior}</div>
            </div>
          ))}
          <div style={{fontSize:10,color:T.textMuted,marginTop:8,textAlign:"center"}}>Click any position to search listings · Salaries vary by location & experience</div>
        </div>
      )}

      {/* Local Jobs */}
      {activeTab==="local" && (
        <div style={{padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>
            Most sought-after positions in <span style={{color:T.accentGlow,fontWeight:700}}>{userLocation}</span>
          </div>
          {TOP_JOBS.slice(0,8).map((job,i)=>(
            <div key={job.title} onClick={()=>onSearch(job.title)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:10,marginBottom:6,background:T.surface,border:`1px solid ${T.border}`,cursor:"pointer",transition:"all 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=job.color}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <div style={{width:24,height:24,borderRadius:6,background:`${job.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>{job.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{job.title}</div>
              </div>
              <a href={`https://www.indeed.com/jobs?q=${encodeURIComponent(job.title)}&l=${encodeURIComponent(userLocation)}&fromage=14`}
                target="_blank" rel="noopener noreferrer"
                onClick={e=>e.stopPropagation()}
                style={{fontSize:10,color:T.accentGlow,textDecoration:"none",fontWeight:600,border:`1px solid ${T.accentBright}44`,borderRadius:6,padding:"3px 8px"}}>
                View on Indeed →
              </a>
            </div>
          ))}
          {!user.location && (
            <div style={{fontSize:11,color:T.gold,background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:10,padding:"10px 12px",marginTop:8,textAlign:"center"}}>
              💡 Add your location in the Me tab to see jobs near you!
            </div>
          )}
          <div style={{fontSize:10,color:T.textMuted,marginTop:8,textAlign:"center"}}>Click any job to search listings in your area on Indeed</div>
        </div>
      )}

      {/* Hot Cities */}
      {activeTab==="hotcities" && (
        <div style={{padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>🏙️ Cities with the highest job growth right now — great places to target your search!</div>
          {HOT_CITIES.map((city)=>(
            <CityCard key={city.city} city={city} type="hot"/>
          ))}
          <div style={{fontSize:10,color:T.textMuted,marginTop:8,textAlign:"center"}}>Job growth data based on BLS & LinkedIn Economic Graph reports</div>
        </div>
      )}

      {/* Up & Coming Cities */}
      {activeTab==="upcoming" && (
        <div style={{padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>⬆️ Hidden gem cities with explosive job growth — get there before everyone else!</div>
          {UPANDCOMING_CITIES.map((city)=>(
            <CityCard key={city.city} city={city} type="upcoming"/>
          ))}
          <div style={{fontSize:10,color:T.textMuted,marginTop:8,textAlign:"center"}}>Data based on economic growth projections · Great for relocation planning!</div>
        </div>
      )}
    </div>
  );
}

// ─── SEEKER JOBS ──────────────────────────────────────────────────────────────
const COMMON_JOB_TITLES = [
  "Nurse","Registered Nurse","LPN","CNA","Medical Assistant","Medical Courier",
  "Driver","Truck Driver","Delivery Driver","Bus Driver","Uber Driver",
  "Warehouse Associate","Warehouse Worker","Forklift Operator","Picker Packer",
  "Security Guard","Security Officer","Loss Prevention","Armed Security",
  "Software Engineer","Frontend Developer","Backend Developer","Full Stack Developer",
  "Data Analyst","Data Scientist","Machine Learning Engineer","AI Engineer",
  "Product Manager","Project Manager","Business Analyst","Scrum Master",
  "Graphic Designer","UX Designer","UI Designer","Web Designer",
  "Sales Representative","Account Executive","Sales Manager","Business Development",
  "Customer Service","Call Center Agent","Customer Support","Help Desk",
  "Teacher","Substitute Teacher","Tutor","Instructional Designer",
  "Electrician","Plumber","HVAC Technician","Carpenter","Welder",
  "Accountant","Bookkeeper","Financial Analyst","CPA","Auditor",
  "Marketing Manager","Social Media Manager","Content Writer","SEO Specialist",
  "Pharmacy Technician","Pharmacist","Physical Therapist","Dental Assistant",
  "Administrative Assistant","Office Manager","Receptionist","Executive Assistant",
  "Construction Worker","Laborer","General Contractor","Site Supervisor",
  "Cook","Chef","Line Cook","Restaurant Manager","Barista","Server",
  "Janitor","Custodian","Maintenance Technician","Groundskeeper",
  "Real Estate Agent","Property Manager","Leasing Consultant",
  "IT Support","Network Engineer","Cybersecurity Analyst","System Administrator",
  "Lawyer","Paralegal","Legal Assistant","Compliance Officer",
  "Human Resources","HR Manager","Recruiter","Talent Acquisition",
  "Remote","Work From Home","Freelance","Part Time","Entry Level",
];

const COMMON_LOCATIONS = [
  "New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ",
  "Philadelphia, PA", "San Antonio, TX", "San Diego, CA", "Dallas, TX", "San Jose, CA",
  "Austin, TX", "Jacksonville, FL", "Fort Worth, TX", "Columbus, OH", "Charlotte, NC",
  "Indianapolis, IN", "San Francisco, CA", "Seattle, WA", "Denver, CO", "Nashville, TN",
  "Oklahoma City, OK", "El Paso, TX", "Washington, DC", "Las Vegas, NV", "Boston, MA",
  "Portland, OR", "Louisville, KY", "Memphis, TN", "Baltimore, MD", "Milwaukee, WI",
  "Albuquerque, NM", "Tucson, AZ", "Fresno, CA", "Sacramento, CA", "Kansas City, MO",
  "Mesa, AZ", "Atlanta, GA", "Omaha, NE", "Colorado Springs, CO", "Raleigh, NC",
  "Virginia Beach, VA", "Minneapolis, MN", "Tampa, FL", "New Orleans, LA", "Arlington, TX",
  "Bakersfield, CA", "Honolulu, HI", "Anaheim, CA", "Aurora, CO", "Santa Ana, CA",
  "Corpus Christi, TX", "Riverside, CA", "St. Louis, MO", "Lexington, KY", "Pittsburgh, PA",
  "Stockton, CA", "St. Paul, MN", "Cincinnati, OH", "Greensboro, NC", "Toledo, OH",
  "Newark, NJ", "Orlando, FL", "Miami, FL", "Cleveland, OH", "Detroit, MI",
  "Remote", "Nationwide",
];

function SeekerJobs({ user, onUpgrade }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);
  const [saved, setSaved] = useState(new Set());
  const [applied, setApplied] = useState(new Set());
  const [starred, setStarred] = useState(()=>new Set(JSON.parse(localStorage.getItem("vaultr_starred")||"[]")));
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [kwSugs, setKwSugs] = useState([]);
  const [showKwSugs, setShowKwSugs] = useState(false);
  const [kwIdx, setKwIdx] = useState(-1);
  const [location, setLocation] = useState("Nationwide");
  const [locInput, setLocInput] = useState("Nationwide");
  const [locSugs, setLocSugs] = useState([]);
  const [showLocSugs, setShowLocSugs] = useState(false);
  const [locIdx, setLocIdx] = useState(-1);
  const placesRef = useRef(null);
  const locInputRef = useRef(null);
  const [category, setCategory] = useState("all");
  const [jobTypes, setJobTypes] = useState([]);
  const [salaryMin, setSalaryMin] = useState("Any");
  const [salaryMax, setSalaryMax] = useState("Any");
  const [page, setPage] = useState(1);
  const [showInsights, setShowInsights] = useState(true);
  const [tierFilter, setTierFilter] = useState(null);
  const [aiQ, setAiQ] = useState(""); const [aiR, setAiR] = useState(""); const [aiL, setAiL] = useState(false);
  const isPro = user.plan==="pro";
  const LOADING_MSGS = ["Searching Indeed, LinkedIn & ZipRecruiter...","Scanning all 50 states...","Running scam detection on results...","Filtering verified job postings...","Almost there..."];

  const doSearch = async (pg=1, kw=null) => {
    setLoading(true); setError(null); setSel(null); setHasSearched(true); setPage(pg);
    setShowInsights(false);
    let msgIdx=0; setLoadingMsg(LOADING_MSGS[0]);
    const interval = setInterval(()=>{ msgIdx=(msgIdx+1)%LOADING_MSGS.length; setLoadingMsg(LOADING_MSGS[msgIdx]); },1800);
    try {
      const searchKw = kw !== null ? kw : keyword;
      const results = await fetchJobsFromAI({keyword:searchKw,location,category,jobTypes,salaryMin,salaryMax});
      if (pg===1) setJobs(results); else setJobs(prev=>[...prev,...results]);
    } catch { setError("Could not load jobs. Please try again."); }
    clearInterval(interval); setLoading(false);
  };

  const handleInsightSearch = (jobTitle) => {
    setKeyword(jobTitle);
    setShowInsights(false);
    doSearch(1, jobTitle);
  };

  useEffect(()=>{ doSearch(1); },[]);
  useEffect(()=>{ localStorage.setItem("vaultr_starred",JSON.stringify([...starred])); },[starred]);

  // Load Google Places script once
  useEffect(() => {
    if (window.google?.maps?.places) { placesRef.current = window.google.maps.places; return; }
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
    if (!key) return;
    const existing = document.querySelector('script[data-places]');
    if (existing) { existing.addEventListener('load', () => { placesRef.current = window.google?.maps?.places; }); return; }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true;
    s.dataset.places = '1';
    s.onload = () => { placesRef.current = window.google?.maps?.places; };
    document.head.appendChild(s);
  }, []);

  const fetchPlacesSugs = (val) => {
    if (!placesRef.current || val.length < 2) { setLocSugs([]); return; }
    const svc = new placesRef.current.AutocompleteService();
    svc.getPlacePredictions(
      { input: val, types: ['(cities)'], componentRestrictions: { country: 'us' } },
      (preds, status) => {
        if (status === 'OK' && preds) {
          setLocSugs(preds.map(p => p.description));
        } else {
          setLocSugs([]);
        }
      }
    );
  };

  const handleKwChange = (val) => {
    setKeyword(val);
    setKwIdx(-1);
    if (val.length < 1) { setKwSugs([]); setShowKwSugs(false); return; }
    const lower = val.toLowerCase();
    const matches = COMMON_JOB_TITLES.filter(t => t.toLowerCase().includes(lower)).slice(0, 8);
    setKwSugs(matches);
    setShowKwSugs(matches.length > 0);
  };

  const handleLocChange = (val) => {
    setLocInput(val);
    setLocation(val);
    setLocIdx(-1);
    if (val.length < 2) { setLocSugs([]); setShowLocSugs(false); return; }
    const lower = val.toLowerCase();
    const staticMatches = COMMON_LOCATIONS.filter(l => l.toLowerCase().includes(lower)).slice(0, 8);
    setLocSugs(staticMatches);
    setShowLocSugs(staticMatches.length > 0);
    fetchPlacesSugs(val);
  };

  const askAI = async () => {
    if (!aiQ.trim()) return;
    if (!isPro) {
      const today = new Date().toDateString();
      const lastUsed = localStorage.getItem("vaultr_ai_date");
      const count = parseInt(localStorage.getItem("vaultr_ai_count") || "0");
      if (lastUsed === today && count >= 1) {
        setAiR("You've used your free AI Coach question for today! ⚡ Upgrade to Pro for unlimited access.");
        return;
      }
      localStorage.setItem("vaultr_ai_date", today);
      localStorage.setItem("vaultr_ai_count", lastUsed === today ? count + 1 : 1);
    }
    setAiL(true); setAiR("");
    try {
      const res = await fetch("/api/claude",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-5",max_tokens:1000,messages:[{role:"user",content:`You are a warm expert career coach. Someone asks: "${aiQ}". Give a focused, encouraging response in 3-4 sentences with 2-3 specific actionable tips. Use 1-2 emojis.`}]})});
      const d = await res.json();
      const text = d.content?.[0]?.text || "Sorry, try again!";
      setAiR(text);
    } catch(e) { setAiR("Temporarily unavailable — keep pushing! 💪"); }
    setAiL(false);
  };

  // Verification stats
  const verifyStats = jobs.length > 0 ? {
    verified: jobs.filter(j=>verifyJob(j).tier==="verified").length,
    unverified: jobs.filter(j=>verifyJob(j).tier==="unverified").length,
    suspicious: jobs.filter(j=>verifyJob(j).tier==="suspicious").length,
  } : null;

  return (
    <div>
      {/* Job Market Insights */}
      {showInsights && (
        <JobMarketInsights user={user} onSearch={handleInsightSearch}/>
      )}{!isPro&&<div style={{background:"linear-gradient(135deg,#16423010,#1a472a08)",border:"1px solid #22c55e33",borderRadius:14,padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:12}}>
  <div style={{fontSize:22,flexShrink:0}}>👀</div>
  <div style={{flex:1,minWidth:0}}>
    <div style={{fontSize:13,fontWeight:700,color:"#22c55e",marginBottom:2}}>Employers are looking at Vaultr profiles right now</div>
    <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>Upload your resume and get discovered. Upgrade to Pro so employers can contact you.</div>
  </div>
  <button onClick={onUpgrade} style={{background:"#22c55e",border:"none",color:"#fff",borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>Go Pro</button>
</div>}
      {!showInsights && (
        <button onClick={()=>setShowInsights(true)} style={{width:"100%",background:T.card,border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          📈 Show Job Market Insights
        </button>
      )}
      {/* Employer Activity Banner */}
      <div style={{background:"linear-gradient(135deg,#052814,#061a10)",border:"1px solid #10b98133",borderRadius:14,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18}}>🏢</span>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:T.green}}>Employers are actively searching Vaultr right now</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Upload your resume to get discovered · Your contact info stays private until you approve</div>
        </div>
        <div style={{width:8,height:8,borderRadius:"50%",background:T.green,flexShrink:0,animation:"pulse 2s infinite"}}/>
      </div>
      {/* AI Coach */}
      <div style={{background:"linear-gradient(135deg,#0a1628,#0c1a30)",border:"1px solid #2563eb28",borderRadius:16,padding:16,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
          <span>✨</span><span style={{fontWeight:700,fontSize:12,color:T.accentGlow}}>AI CAREER COACH</span>
          {!isPro&&<Pill color={T.gold}>Pro</Pill>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <input value={aiQ} onChange={e=>setAiQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&askAI()} placeholder="Ask about salary, interviews, resume tips..." style={{...IS,flex:1}}/>
          <button onClick={askAI} disabled={aiL} style={{background:T.accent,border:"none",color:"#fff",borderRadius:10,padding:"0 16px",fontWeight:700,fontSize:13,cursor:"pointer",opacity:aiL?0.6:1}}>{aiL?"...":"Ask"}</button>
        </div>
        {aiR&&<div style={{marginTop:10,padding:"10px 14px",background:T.accentSoft,borderLeft:`3px solid ${T.accent}`,borderRadius:"0 10px 10px 0",fontSize:13,color:T.textSecondary,lineHeight:1.65}}>{aiR}</div>}
      </div>

      {/* Search */}
      <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:16,padding:16,marginBottom:14}}>
        <div style={{fontWeight:700,fontSize:11,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>🗺️ National Job Search + Verification</div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <div style={{flex:1,position:"relative"}}>
            <input value={keyword} onChange={e=>handleKwChange(e.target.value)} onKeyDown={e=>{if(e.key==="ArrowDown"){e.preventDefault();setKwIdx(i=>Math.min(i+1,kwSugs.length-1));}else if(e.key==="ArrowUp"){e.preventDefault();setKwIdx(i=>Math.max(i-1,-1));}else if(e.key==="Enter"){if(kwIdx>=0&&kwSugs[kwIdx]){const s=kwSugs[kwIdx];setKeyword(s);setShowKwSugs(false);setKwIdx(-1);doSearch(1,s);}else{setShowKwSugs(false);doSearch(1);}}else if(e.key==="Escape"){setShowKwSugs(false);setKwIdx(-1);}}} onBlur={()=>setTimeout(()=>{setShowKwSugs(false);setKwIdx(-1);},150)} placeholder="Job title, skill, or keyword..." style={{...IS,width:"100%",boxSizing:"border-box"}}/>
            {showKwSugs && kwSugs.length > 0 && (
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#0c1018",border:"1px solid #182030",borderRadius:10,zIndex:999,marginTop:4,overflow:"hidden",boxShadow:"0 8px 24px #00000060"}}>
                {kwSugs.map((s,i)=>(
                  <div key={i} onMouseDown={()=>{setKeyword(s);setShowKwSugs(false);setKwIdx(-1);doSearch(1,s);}} style={{padding:"10px 14px",fontSize:13,color:"#f0f4ff",cursor:"pointer",borderBottom:i<kwSugs.length-1?"1px solid #182030":"none",background:i===kwIdx?"#182030":"transparent"}} onMouseEnter={()=>setKwIdx(i)} onMouseLeave={()=>setKwIdx(-1)}>
                    🔍 {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={()=>doSearch(1)} disabled={loading} style={{background:T.accent,border:"none",color:"#fff",borderRadius:10,padding:"0 18px",fontWeight:700,fontSize:13,cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1,whiteSpace:"nowrap"}}>{loading?"...":"Search"}</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Location</label>
            <div style={{position:"relative"}}>
              <input ref={locInputRef} value={locInput} onChange={e=>handleLocChange(e.target.value)} onKeyDown={e=>{if(e.key==="ArrowDown"){e.preventDefault();setLocIdx(i=>Math.min(i+1,locSugs.length-1));}else if(e.key==="ArrowUp"){e.preventDefault();setLocIdx(i=>Math.max(i-1,-1));}else if(e.key==="Enter"){if(locIdx>=0&&locSugs[locIdx]){const s=locSugs[locIdx];setLocInput(s);setLocation(s);setShowLocSugs(false);setLocIdx(-1);}else{setShowLocSugs(false);doSearch(1);}}else if(e.key==="Escape"){setShowLocSugs(false);setLocIdx(-1);}}} onFocus={()=>locInput.length>=2&&setShowLocSugs(true)} onBlur={()=>setTimeout(()=>{setShowLocSugs(false);setLocIdx(-1);},150)} placeholder="City, state, or Nationwide" style={{...IS,width:"100%",boxSizing:"border-box"}}/>
              {showLocSugs && locSugs.length > 0 && (
                <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#0c1018",border:"1px solid #182030",borderRadius:10,zIndex:1000,marginTop:4,overflow:"hidden",boxShadow:"0 8px 24px #00000060"}}>
                  {locSugs.map((s,i)=>(
                    <div key={i} onMouseDown={()=>{setLocInput(s);setLocation(s);setShowLocSugs(false);setLocIdx(-1);}} style={{padding:"10px 14px",fontSize:13,color:"#f0f4ff",cursor:"pointer",borderBottom:i<locSugs.length-1?"1px solid #182030":"none",background:i===locIdx?"#182030":"transparent"}} onMouseEnter={()=>setLocIdx(i)} onMouseLeave={()=>setLocIdx(-1)}>
                      📍 {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Job Type</label>
            {/* Multiple Job Type Selection */}
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["Full-time","Part-time","Contract","Internship","Temporary","Remote"].map(t=>(
                <button key={t} onClick={()=>setJobTypes(prev=>prev.includes(t)?prev.filter(x=>x!==t):[...prev,t])}
                  style={{background:jobTypes.includes(t)?T.accent:"transparent",border:`1px solid ${jobTypes.includes(t)?T.accent:T.border}`,color:jobTypes.includes(t)?"#fff":T.textSecondary,borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  {t}
                </button>
              ))}
              {jobTypes.length>0&&<button onClick={()=>setJobTypes([])} style={{background:"transparent",border:`1px solid ${T.red}44`,color:T.red,borderRadius:20,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>✕ Clear</button>}
            </div>
            {/* Salary Range Filter — Pro only */}
            {isPro ? (
              <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6}}>
                <span style={{fontSize:11,color:T.textMuted,flexShrink:0}}>💰 Salary:</span>
                <select value={salaryMin} onChange={e=>setSalaryMin(e.target.value)} style={{...IS,fontSize:11,padding:"6px 10px",flex:1}}>
                  {["Any","$20,000","$30,000","$40,000","$50,000","$60,000","$70,000","$80,000","$90,000","$100,000","$120,000","$150,000"].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <span style={{fontSize:11,color:T.textMuted}}>to</span>
                <select value={salaryMax} onChange={e=>setSalaryMax(e.target.value)} style={{...IS,fontSize:11,padding:"6px 10px",flex:1}}>
                  {["Any","$40,000","$50,000","$60,000","$70,000","$80,000","$90,000","$100,000","$120,000","$150,000","$200,000","$200,000+"].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ) : (
              <div onClick={onUpgrade} style={{display:"flex",alignItems:"center",gap:6,marginTop:6,padding:"6px 10px",background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:10,cursor:"pointer"}}>
                <span style={{fontSize:11}}>💰</span>
                <span style={{fontSize:11,color:T.gold,fontWeight:600}}>🔒 Salary Range Filter — Pro Only</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{marginBottom:10}}>
        <button onClick={()=>setShowStarredOnly(s=>!s)} style={{background:showStarredOnly?"#f59e0b22":"transparent",border:"1px solid #f59e0b",color:"#f59e0b",borderRadius:10,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}>
          ⭐ {showStarredOnly?"Showing Starred Only":"Show Starred Only"}
        </button>
      </div>

      {/* Categories */}
      <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:4,msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {JOB_CATEGORIES.map(c=>(
          <button key={c.id} onClick={()=>{setCategory(c.id);setTimeout(()=>doSearch(1),0);}}
            style={{background:category===c.id?T.accent:"transparent",border:`1px solid ${category===c.id?T.accent:T.border}`,color:category===c.id?"#fff":T.textSecondary,borderRadius:20,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,display:"flex",alignItems:"center",gap:4}}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>

{verifyStats && !loading && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
          <div onClick={()=>setTierFilter(tierFilter==="verified"?null:"verified")}
            style={{background:tierFilter==="verified"?`${T.green}25`:`${T.green}10`,border:`1px solid ${tierFilter==="verified"?T.green:T.green+"33"}`,borderRadius:12,padding:"10px",textAlign:"center",cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{fontWeight:800,fontSize:18,color:T.green}}>{verifyStats.verified}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>✅ Verified</div>
          </div>
          <div onClick={()=>setTierFilter(tierFilter==="unverified"?null:"unverified")}
            style={{background:tierFilter==="unverified"?`${T.gold}25`:`${T.gold}10`,border:`1px solid ${tierFilter==="unverified"?T.gold:T.gold+"33"}`,borderRadius:12,padding:"10px",textAlign:"center",cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{fontWeight:800,fontSize:18,color:T.gold}}>{verifyStats.unverified}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>⚠️ Unverified</div>
          </div>
          <div onClick={()=>setTierFilter(tierFilter==="suspicious"?null:"suspicious")}
            style={{background:tierFilter==="suspicious"?`${T.red}25`:`${T.red}10`,border:`1px solid ${tierFilter==="suspicious"?T.red:T.red+"33"}`,borderRadius:12,padding:"10px",textAlign:"center",cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{fontWeight:800,fontSize:18,color:T.red}}>{verifyStats.suspicious}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>🚨 Suspicious</div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"28px 20px",textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:12,animation:"pulse 1.2s infinite"}}>🔍</div>
          <div style={{fontWeight:700,fontSize:14,color:T.textPrimary,marginBottom:6}}>Searching & verifying jobs nationwide...</div>
          <div style={{fontSize:12,color:T.textSecondary,marginBottom:16,animation:"pulse 2s infinite"}}>{loadingMsg}</div>
          <div style={{display:"flex",justifyContent:"center",gap:8,flexWrap:"wrap"}}>
            {["Indeed","LinkedIn","ZipRecruiter","Glassdoor","Monster"].map(s=>(
              <div key={s} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"3px 10px",fontSize:11,color:T.textMuted}}>{s}</div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error&&!loading&&(
        <div style={{background:"#ef444415",border:`1px solid ${T.red}44`,borderRadius:12,padding:"14px 16px",textAlign:"center",marginBottom:14}}>
          <div style={{color:T.red,fontSize:13,marginBottom:8}}>{error}</div>
          <button onClick={()=>doSearch(1)} style={{background:T.red,border:"none",color:"#fff",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Try Again</button>
        </div>
      )}

      {/* Results */}
      {!loading&&jobs.length>0&&(
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:12,color:T.textMuted}}>
            <span style={{color:T.accentGlow,fontWeight:700}}>{jobs.length}</span> jobs found
            {location!=="Nationwide"&&<span> in <span style={{color:T.textSecondary}}>{location}</span></span>}
          </div>
          <div style={{fontSize:11,color:T.textMuted}}>🛡️ Scam detection active</div>
        </div>
      )}

      {/* Job cards */}
      {!loading&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {tierFilter && (
              <button onClick={()=>setTierFilter(null)}
                style={{background:T.card,border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"7px 14px",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                ✕ Show All Jobs
              </button>
            )}
          </div>
          {jobs
            .filter(job => !showStarredOnly || starred.has(job.id))
            .filter(job => jobTypes.length === 0 || jobTypes.some(t => job.type?.toLowerCase().includes(t.toLowerCase())))
            .filter(job => tierFilter ? verifyJob(job).tier === tierFilter : true)
            .sort((a,b) => {
              const order = {verified:0, unverified:1, suspicious:2};
              return order[verifyJob(a).tier] - order[verifyJob(b).tier];
            })
            .map(job=>{
              const v = verifyJob(job);
              return <JobCard key={job.id} job={job} v={v} sel={sel} setSel={setSel} saved={saved} setSaved={setSaved} applied={applied} setApplied={setApplied} starred={starred} setStarred={setStarred} isPro={isPro} onUpgrade={onUpgrade}/>;
            })}
          {jobs.length>0&&!loading&&(
            <button onClick={()=>doSearch(page+1)} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"13px",fontSize:13,fontWeight:600,cursor:"pointer",marginTop:4}}>
              Load More Jobs →
            </button>
          )}
          {jobs.length===0&&hasSearched&&!loading&&(
            <div style={{textAlign:"center",padding:"40px 20px",color:T.textMuted}}>
              <div style={{fontSize:32,marginBottom:10}}>🔍</div>
              <div style={{fontSize:14,fontWeight:700,color:T.textSecondary,marginBottom:6}}>No jobs found</div>
              <div style={{fontSize:12}}>Try a different keyword or location</div>
            </div>
          )}
        </div>
      )}
      <NexoraFooter/>
    </div>
  );
}

// ─---SEEKER COMMUNITY---───────────────────────────────────────────────────────
function SeekerCommunity({ user }) {
  const peers = DB.seekers.filter(s=>s.id!==user.id);
  const [showRules, setShowRules] = useState(false);
  return (
    <div>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px",marginBottom:14}}>
        <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,marginBottom:4}}>Seeker Circle 👋</div>
        <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.55}}>A judgment-free space to support each other. Your contact info is never visible to other seekers.</div>
        <div style={{marginTop:10,fontSize:12,color:T.green}}>🔒 Your address & phone are always hidden from other job seekers</div>
      </div>

      {/* Community Rules */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,marginBottom:14,overflow:"hidden"}}>
        <button onClick={()=>setShowRules(s=>!s)} style={{width:"100%",background:"none",border:"none",padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>📋</span>
            <span style={{fontWeight:700,fontSize:13,color:T.textPrimary}}>Community Rules — Dos & Don'ts</span>
          </div>
          <span style={{fontSize:13,color:T.textMuted}}>{showRules?"▲":"▼"}</span>
        </button>
        {showRules&&(
          <div style={{padding:"0 16px 16px",borderTop:`1px solid ${T.border}`}}>
            <div style={{height:12}}/>
            {/* DOs */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:T.green,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>✅ Do's</div>
              {[
                "Be kind, supportive and encouraging to fellow job seekers",
                "Share job search tips, wins, and constructive advice",
                "Celebrate others' successes — big and small",
                "Ask questions freely — no question is too basic",
                "Share resources, articles and opportunities that helped you",
                "Respect everyone's privacy — never share another member's personal info",
                "Be honest and authentic in your interactions",
                "Report suspicious job listings or inappropriate behavior",
              ].map((rule,i)=>(
                <div key={i} style={{display:"flex",gap:8,padding:"5px 0",borderBottom:`1px solid ${T.border}`}}>
                  <span style={{color:T.green,flexShrink:0}}>✓</span>
                  <span style={{fontSize:12,color:T.textSecondary}}>{rule}</span>
                </div>
              ))}
            </div>
            {/* DON'Ts */}
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.red,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>🚫 Don'ts</div>
              {[
                "Do not post spam, scams, or unsolicited advertisements",
                "Do not share or solicit personal contact information publicly",
                "Do not post discriminatory, hateful, or offensive content",
                "Do not harass, bully, or demean other members",
                "Do not share confidential information about employers or colleagues",
                "Do not post misleading job opportunities or false information",
                "Do not solicit money, investments, or personal financial information",
                "Do not share content unrelated to career and job search topics",
              ].map((rule,i)=>(
                <div key={i} style={{display:"flex",gap:8,padding:"5px 0",borderBottom:`1px solid ${T.border}`}}>
                  <span style={{color:T.red,flexShrink:0}}>✗</span>
                  <span style={{fontSize:12,color:T.textSecondary}}>{rule}</span>
                </div>
              ))}
            </div>
            <div style={{marginTop:12,padding:"10px 12px",background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:10,fontSize:12,color:T.gold}}>
              ⚠️ Violations may result in removal from the community. Report issues to support@vaultrapps.com
            </div>
          </div>
        )}
      </div>
      <Section title={`${peers.length} Active Members`}>
        {peers.map(s=>{
          const view = getSeekerProfile(s,"seeker",user.id);
          return (
            <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"13px 15px",marginBottom:10}}>
              <Avatar initials={s.avatar} color={s.color} size={44} online/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:T.textPrimary}}>{view.name}</div>
                <div style={{fontSize:12,color:T.textSecondary}}>{view.role}</div>
                {view.bio&&<div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{view.bio}</div>}
              </div>
              {s.plan==="pro"&&<Pill color={T.gold}>Pro</Pill>}
            </div>
          );
        })}
      </Section>
      <NexoraFooter/>
    </div>
  );
}

// ─---SEEKER CHAT---────────────────────────────────────────────────────────────
function SeekerChat({ user, onUpgrade }) {
  const [msgs, setMsgs] = useState(DB.messages);
  const [text, setText] = useState("");
  const [myReactions, setMyReactions] = useState({});
  const endRef = useRef(null);
  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs]);

  const send = () => {
    if (!text.trim()) return;
    setMsgs(m=>[...m,{id:Date.now(),userId:user.id,text,time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),reactions:{}}]);
    setText("");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 200px)"}}>
      <div style={{fontSize:11,fontWeight:700,color:T.textMuted,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>💬 Support Circle · {DB.seekers.length} seekers</div>
      <div style={{flex:1,overflowY:"auto"}}>
        {msgs.map(msg=>{
          const sender = DB.seekers.find(s=>s.id===msg.userId)||user;
          const isMe = msg.userId===user.id;
          return (
            <div key={msg.id} style={{display:"flex",gap:9,padding:"7px 3px",flexDirection:isMe?"row-reverse":"row"}}>
              {!isMe&&<Avatar initials={sender.avatar} color={sender.color} size={32} online/>}
              <div style={{maxWidth:"76%",display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start"}}>
                {!isMe&&<span style={{fontSize:11,fontWeight:700,color:sender.color,marginBottom:3}}>{sender.name}</span>}
                <div style={{background:isMe?T.accent:T.card,border:`1px solid ${isMe?"transparent":T.border}`,borderRadius:isMe?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:"9px 12px",fontSize:13,color:isMe?"#fff":T.textPrimary,lineHeight:1.55}}>{msg.text}</div>
                <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
                  {Object.entries(msg.reactions||{}).map(([emoji,users])=>{
                    const key=`${msg.id}-${emoji}`; const mine=myReactions[key]; const count=users.length+(mine?1:0);
                    return <button key={emoji} onClick={()=>setMyReactions(r=>({...r,[key]:!r[key]}))} style={{background:mine?T.accentSoft:"rgba(255,255,255,0.04)",border:`1px solid ${mine?T.accent:"rgba(255,255,255,0.06)"}`,borderRadius:20,padding:"2px 8px",fontSize:12,color:T.textSecondary,cursor:"pointer"}}>{emoji} {count}</button>;
                  })}
                </div>
                <span style={{fontSize:10,color:T.textMuted,marginTop:2}}>{msg.time}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>
      <div style={{paddingTop:10,borderTop:`1px solid ${T.border}`,marginTop:8}}>
        {user.plan==="pro" ? (
          <div style={{display:"flex",gap:8,background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:8}}>
            <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Share a win, question, or encouragement..." style={{flex:1,background:"none",border:"none",fontSize:13,color:T.textPrimary,outline:"none",padding:"4px 8px"}}/>
            <button onClick={send} style={{background:T.accent,border:"none",color:"#fff",borderRadius:10,padding:"8px 14px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Send</button>
          </div>
        ) : (
          <div style={{background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:14,padding:"12px 16px",textAlign:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.gold,marginBottom:4}}>🔒 Pro Feature — Join the Conversation!</div>
            <div style={{fontSize:12,color:T.textSecondary,marginBottom:8}}>Upgrade to Pro to post messages and connect with other job seekers!</div>
            <button onClick={onUpgrade} style={{background:T.gold,border:"none",color:"#fff",borderRadius:20,padding:"8px 20px",fontWeight:700,fontSize:12,cursor:"pointer"}}>⚡ Upgrade to Pro — $9/month</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─---SEEKER RESUME---──────────────────────────────────────────────────────────
const RESUME_LIMIT = 3; // Pro gets 3, Free gets 1

function SeekerResume({ user, setUser, onUpgrade }) {
  const isPro = user.plan==="pro";
  const isPremier = user.plan==="premier";
  const isFreePlan = !isPro && !isPremier;
  const trialDays = user.joined && isFreePlan ? (/^\d{4}$/.test(user.joined) ? 3 : Math.max(0, 3 - Math.floor((Date.now() - new Date(user.joined).getTime()) / (1000*60*60*24)))) : 0;
  const isTrialing = trialDays > 0;
  const canUseCritique = isPro || isPremier || isTrialing || !user.hasUsedFreeCritique;
  const [resumes, setResumes] = useState(user.resume ? [{id:"r1",name:user.resume.name,status:"active",uploadedAt:Date.now()-86400000*2,skills:user.resume.skills,experience:user.resume.experience,education:user.resume.education,sensitive:user.resume.sensitive,size:"142 KB",views:14,feedback:[]}] : []);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [fbText, setFbText] = useState("");
  const [cancelModal, setCancelModal] = useState(false);
  const fileRef = useRef(null);
  const [appliedJobs, setAppliedJobs] = useState(()=>JSON.parse(localStorage.getItem("vaultr_applied")||"[]"));
  useEffect(()=>{ setAppliedJobs(JSON.parse(localStorage.getItem("vaultr_applied")||"[]")); },[]);
  const [critiqueState, setCritiqueState] = useState("idle");

  const markFreeCritiqueUsed = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
        headers:{"apikey":process.env.REACT_APP_SUPABASE_ANON_KEY,"Authorization":`Bearer ${user.token}`}
      });
      const fresh = await res.json();
      const currentMeta = fresh.user_metadata || {};
      await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
        method:"PUT",
        headers:{"Content-Type":"application/json","apikey":process.env.REACT_APP_SUPABASE_ANON_KEY,"Authorization":`Bearer ${user.token}`},
        body:JSON.stringify({data:{...currentMeta,hasUsedFreeCritique:true}})
      });
      setUser(u=>({...u,hasUsedFreeCritique:true}));
    } catch(e) { console.error("Failed to save critique usage:",e); }
  };

  const active = resumes.filter(r=>r.status==="active");
  const archived = resumes.filter(r=>r.status==="archived");
  const atLimit = active.length>=RESUME_LIMIT;

  const handleFile = e => {
    const f=e.target.files[0]; if(!f) return; e.target.value="";
    if(atLimit) return;
    setUploading(true); setUploadProgress(0);
    const interval = setInterval(()=>setUploadProgress(p=>{if(p>=100){clearInterval(interval);return 100;}return p+Math.random()*18;}),120);
    setTimeout(()=>{
      clearInterval(interval); setUploadProgress(100);
      setTimeout(()=>{
        const newR={id:`r${Date.now()}`,name:f.name,status:"active",uploadedAt:Date.now(),size:`${Math.round(f.size/1024)||98} KB`,skills:user.resume?.skills||[],experience:"",education:"",sensitive:{address:user.location||"",phone:user.phone||"",email:user.email},views:0,feedback:[]};
        setResumes(r=>[newR,...r]); setUser(u=>({...u,resume:newR})); setUploading(false); setUploadProgress(0);
      },300);
    },2000);
  };

  return (
    <div>
      <div style={{background:"linear-gradient(135deg,#052814,#041a0e)",border:`1px solid ${T.green}33`,borderRadius:14,padding:"14px 18px",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
          <span style={{fontSize:20,flexShrink:0}}>🛡️</span>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:T.green,marginBottom:3}}>Privacy-Protected Resume Sharing</div>
            <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.55}}>Your address, phone & email are <strong style={{color:T.textPrimary}}>always hidden</strong> until you approve an employer contact request.</div>
          </div>
        </div>
      </div>

      {isPro&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>Active Resumes</div>
            <div style={{fontSize:12,color:atLimit?T.red:T.textSecondary}}><span style={{fontWeight:800,color:atLimit?T.red:T.accentGlow}}>{active.length}</span><span style={{color:T.textMuted}}> / {RESUME_LIMIT}</span></div>
          </div>
          <div style={{height:6,background:T.border,borderRadius:10,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${(active.length/RESUME_LIMIT)*100}%`,background:atLimit?T.red:active.length===2?T.gold:T.green,borderRadius:10,transition:"width 0.4s ease"}}/>
          </div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:7}}>{atLimit?`Limit reached — archive a resume to upload new`:`${RESUME_LIMIT-active.length} slot${RESUME_LIMIT-active.length!==1?"s":""} remaining · Auto-archive after 90 days`}</div>
        </div>
      )}

      <div onClick={()=>{if(!atLimit&&!uploading)fileRef.current?.click();}}
        style={{border:`2px dashed ${atLimit?T.red+"66":uploading?T.accentBright:T.border}`,borderRadius:16,padding:"22px 20px",textAlign:"center",marginBottom:20,cursor:(!isPro||atLimit)?"not-allowed":"pointer",background:uploading?T.accentSoft:atLimit?"#ef444408":"transparent",transition:"all 0.2s",opacity:(atLimit&&!uploading)?0.6:1}}
        onMouseEnter={e=>{if(isPro&&!atLimit&&!uploading)e.currentTarget.style.borderColor=T.accentBright;}}
        onMouseLeave={e=>{if(!uploading)e.currentTarget.style.borderColor=atLimit?T.red+"66":T.border;}}>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{display:"none"}} onChange={handleFile}/>
        {uploading?<div><div style={{fontSize:13,color:T.accentGlow,marginBottom:10,fontWeight:600}}>Uploading securely... 🔒</div><div style={{height:6,background:T.border,borderRadius:10,overflow:"hidden",maxWidth:220,margin:"0 auto"}}><div style={{height:"100%",width:`${Math.min(uploadProgress,100)}%`,background:`linear-gradient(90deg,${T.accent},${T.accentGlow})`,borderRadius:10,transition:"width 0.15s"}}/></div><div style={{fontSize:11,color:T.textMuted,marginTop:6}}>{Math.round(Math.min(uploadProgress,100))}%</div></div>
        :atLimit?<><div style={{fontSize:22,marginBottom:6}}>🚫</div><div style={{fontWeight:700,fontSize:13,color:T.red}}>Limit reached ({RESUME_LIMIT}/{RESUME_LIMIT})</div><div style={{fontSize:12,color:T.textMuted,marginTop:4}}>Archive a resume to free up a slot</div></>
        :<><div style={{fontSize:28,marginBottom:8}}>📤</div><div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Upload Resume</div><div style={{fontSize:12,color:T.textSecondary}}>PDF or DOC · Privacy protected</div>{!isPro&&<div style={{marginTop:8}}><Pill color={T.gold}>Pro Feature · $9/mo</Pill></div>}</>}
      </div>

      {active.length>0&&(
        <Section title={`Active Resumes (${active.length}/${RESUME_LIMIT})`}>
          {active.map(r=>(
            <div key={r.id} style={{marginBottom:10}}>
              <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:14,padding:"14px 16px"}}>
                <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                  <div style={{width:42,height:48,background:T.accentSoft,border:`1px solid ${T.accent}33`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>📄</div>
                  <div style={{flex:1,minWidth:0}}>
<div style={{fontWeight:700,fontSize:13,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                {r.name}
                {!isPro&&<span title="Upgrade to Pro so employers can see this" style={{fontSize:10,background:"#f59e0b22",color:"#f59e0b",border:"1px solid #f59e0b44",borderRadius:6,padding:"1px 6px",fontWeight:600,flexShrink:0}}>🔒 Hidden from employers</span>}
              </div>
                      <span style={{fontSize:11,color:T.textMuted}}>👁 {r.views} views</span>
                      <span style={{fontSize:11,color:T.textMuted}}>{r.size}</span>
                      <span style={{fontSize:11,color:T.green}}>● Active</span>
                    </div>
                  
                </div>
                <div style={{background:T.surface,borderRadius:10,padding:"10px 12px",margin:"12px 0 10px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>What employers see</div>
                  <div style={{display:"grid",gap:7}}>
                    <Row label="Skills" value={<div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{(r.skills||[]).map(s=><Pill key={s} color={T.accentBright}>{s}</Pill>)}</div>}/>
                    <Row label="Experience" value={r.experience||"Not set"}/>
                    <Row label="Contact" value={<PrivacyShield label="Requires your approval"/>}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setFeedbackFor(feedbackFor===r.id?null:r.id)} style={{flex:1,background:feedbackFor===r.id?T.accentSoft:"transparent",border:`1px solid ${feedbackFor===r.id?T.accent:T.border}`,color:feedbackFor===r.id?T.accentGlow:T.textSecondary,borderRadius:8,padding:"7px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                    💬 Feedback {r.feedback?.length?`(${r.feedback.length})`:""}
                  </button>
                  <button onClick={()=>setResumes(res=>res.map(x=>x.id===r.id?{...x,status:"archived"}:x))} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>Archive</button>
                </div>
              </div>
              {feedbackFor===r.id&&(
                <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"0 0 12px 12px",padding:"10px 14px",display:"flex",gap:8,marginTop:-4}}>
                  <input value={fbText} onChange={e=>setFbText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&fbText.trim()){setResumes(res=>res.map(x=>x.id===r.id?{...x,feedback:[...x.feedback,fbText]}:x));setFbText("");setFeedbackFor(null);}}} placeholder="Leave feedback..." style={{...IS,flex:1}} autoFocus/>
                  <button onClick={()=>{if(fbText.trim()){setResumes(res=>res.map(x=>x.id===r.id?{...x,feedback:[...x.feedback,fbText]}:x));setFbText("");setFeedbackFor(null);}}} style={{background:T.accent,border:"none",color:"#fff",borderRadius:8,padding:"0 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Post</button>
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {archived.length>0&&(
        <Section title={`Archived (${archived.length})`} extra={<button onClick={()=>setShowArchived(s=>!s)} style={{background:"none",border:"none",color:T.accentGlow,fontSize:12,cursor:"pointer"}}>{showArchived?"Hide":"Show"}</button>}>
          {showArchived&&archived.map(r=>(
            <div key={r.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 14px",marginBottom:8,opacity:0.65,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:20}}>📄</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Archived · {r.size}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                {active.length<RESUME_LIMIT&&<button onClick={()=>setResumes(res=>res.map(x=>x.id===r.id?{...x,status:"active"}:x))} style={{background:T.accentSoft,border:`1px solid ${T.accent}44`,color:T.accentGlow,borderRadius:7,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Restore</button>}
                <button onClick={()=>setResumes(res=>res.filter(x=>x.id!==r.id))} style={{background:"transparent",border:`1px solid ${T.red}33`,color:T.red,borderRadius:7,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>Delete</button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* AI Resume Critique */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <div style={{width:36,height:36,borderRadius:10,background:"#5b4fe818",border:"1px solid #5b4fe844",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🤖</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>AI Resume Critique</div>
            <div style={{fontSize:11,color:T.textMuted}}>Instant AI feedback on your resume</div>
          </div>
          {!canUseCritique&&<span style={{fontSize:9,fontWeight:700,color:T.gold,background:`${T.gold}18`,border:`1px solid ${T.gold}33`,borderRadius:6,padding:"2px 7px",flexShrink:0}}>Pro Feature</span>}
        </div>
        {canUseCritique?(
          critiqueState==="idle"?(
            <button onClick={()=>{setCritiqueState("loading");setTimeout(()=>{setCritiqueState("result");if(isFreePlan&&!isTrialing)markFreeCritiqueUsed();},1800);}} style={{width:"100%",background:`linear-gradient(135deg,${T.accent},${T.accentGlow})`,border:"none",color:"#fff",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✨ Get Critique</button>
          ):critiqueState==="loading"?(
            <div style={{textAlign:"center",padding:"18px 0"}}>
              <div style={{fontSize:13,color:T.accentGlow,fontWeight:600,marginBottom:10}}>Analyzing your resume...</div>
              <div style={{height:4,background:T.border,borderRadius:10,overflow:"hidden",maxWidth:200,margin:"0 auto"}}><div style={{height:"100%",width:"60%",background:`linear-gradient(90deg,${T.accent},${T.accentGlow})`,borderRadius:10}}/></div>
            </div>
          ):(
            <div>
              <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:11,padding:"14px 16px",marginBottom:10,fontSize:12,color:T.textSecondary,lineHeight:1.7}}>
                <div style={{fontWeight:700,color:T.textPrimary,marginBottom:10,fontSize:13}}>Resume Critique</div>
                {[
                  "Overall, your resume shows a solid foundation. A few targeted improvements could significantly boost your visibility with employers:",
                  "• Quantify achievements wherever possible — replace 'managed a team' with 'managed a 6-person team that reduced onboarding time by 30%.'",
                  "• Skills section looks strong. Consider pinning the 5–7 most relevant skills to the specific role you're targeting.",
                  "• Work history reads more like a duty list than an impact list — for each role, lead with what you achieved, not just what you did.",
                  "• No ATS formatting red flags detected. Structure is clean and scannable.",
                ].map((line,i)=><div key={i} style={{marginBottom:i===0?10:6}}>{line}</div>)}
              </div>
              <button onClick={()=>setCritiqueState("idle")} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>↺ Run Again</button>
            </div>
          )
        ):(
          <div style={{position:"relative",borderRadius:11,overflow:"hidden"}}>
            <div style={{filter:"blur(4px)",userSelect:"none",pointerEvents:"none",background:T.surface,border:`1px solid ${T.border}`,borderRadius:11,padding:"14px 16px",fontSize:12,color:T.textSecondary,lineHeight:1.7}}>
              <div style={{fontWeight:700,color:T.textPrimary,marginBottom:10,fontSize:13}}>Resume Critique</div>
              <div style={{marginBottom:8}}>Overall, your resume shows a solid foundation. A few targeted improvements could significantly boost your visibility with employers.</div>
              <div style={{marginBottom:6}}>• Quantify achievements — replace 'managed a team' with 'managed a 6-person team that reduced onboarding time by 30%.'</div>
              <div style={{marginBottom:6}}>• Skills section looks strong. Pin the 5–7 most relevant skills to the role you're targeting.</div>
              <div>• Work history reads like a duty list — lead each role with impact, not just responsibilities.</div>
            </div>
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,background:"rgba(7,9,15,0.55)"}}>
              <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>You've used your free critique</div>
              <div style={{fontSize:11,color:T.textSecondary,textAlign:"center",maxWidth:220,lineHeight:1.5}}>Upgrade to Pro or Premier for unlimited AI critiques, any time.</div>
              <button onClick={onUpgrade} style={{background:"linear-gradient(135deg,#f59e0b,#f97316)",border:"none",color:"#000",borderRadius:10,padding:"9px 20px",fontWeight:800,fontSize:12,cursor:"pointer"}}>⚡ Upgrade to Pro — $9/month</button>
            </div>
          </div>
        )}
      </div>

      {isPro&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 16px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Vaultr Pro · $9/month</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Next billing: Jun 1, 2026 · Powered by Stripe</div>
            </div>

          <button onClick={()=>window.open("https://billing.stripe.com/p/login/dRm9AU0xs3bxdJB2dugfu00", "_blank")} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:8,padding:"6px 12px",fontSize:11,cursor:"pointer"}}>Manage Subscription</button>
          </div>
        </div>
      )}

      <Section title={`📋 Applied Jobs (${appliedJobs.length})`}>
        {appliedJobs.length===0
          ? <div style={{fontSize:13,color:T.textMuted,padding:"12px 0"}}>No applications tracked yet.</div>
          : <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {appliedJobs.map((a,i)=>(
                <div key={i} style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:36,height:36,borderRadius:9,background:"#2563eb18",border:"1px solid #2563eb33",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>📤</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.title}</div>
                    <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>{a.company}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.green,background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:6,padding:"2px 8px",marginBottom:3}}>{a.status}</div>
                    <div style={{fontSize:10,color:T.textMuted}}>{a.date}</div>
                  </div>
                  <button onClick={()=>{const updated=appliedJobs.filter((_,j)=>j!==i);localStorage.setItem("vaultr_applied",JSON.stringify(updated));setAppliedJobs(updated);}} style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:14,padding:"4px",flexShrink:0}}>✕</button>
                </div>
              ))}
            </div>
        }
      </Section>

      {cancelModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20,backdropFilter:"blur(6px)"}}>
          <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:20,padding:28,maxWidth:340,width:"100%"}}>
            <div style={{textAlign:"center",marginBottom:18}}>
              <div style={{fontSize:36,marginBottom:8}}>😢</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:T.textPrimary,marginBottom:6}}>Cancel Subscription?</div>
              <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.55}}>You'll lose Pro features at end of billing period.</div>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setCancelModal(false)} style={{flex:2,background:T.accent,border:"none",color:"#fff",borderRadius:12,padding:"12px",fontWeight:800,fontSize:14,cursor:"pointer"}}>Keep Pro</button>
              <button onClick={()=>{setUser(u=>({...u,plan:"free"}));setCancelModal(false);}} style={{flex:1,background:"transparent",border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:12,padding:"12px",fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─---CONTACT REQUESTS---───────────────────────────────────────────────────────
function ContactRequests({ userId }) {
  const [requests, setRequests] = useState(DB.contactRequests.filter(r=>r.seekerId===userId));
  const respond = (id,status) => {
    const idx = DB.contactRequests.findIndex(r=>r.id===id);
    if (idx>-1) DB.contactRequests[idx].status=status;
    setRequests(r=>r.map(x=>x.id===id?{...x,status}:x));
  };
  if (!requests.length) return null;
  return (
    <Section title="Employer Contact Requests">
      {requests.map(req=>{
        const emp = DB.employers.find(e=>e.id===req.employerId);
        return (
          <div key={req.id} style={{background:T.card,border:`1px solid ${req.status==="approved"?T.green+"55":req.status==="rejected"?T.red+"33":T.borderLight}`,borderRadius:12,padding:"14px 16px",marginBottom:10}}>
            <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10}}>
              <Avatar initials={emp?.avatar||"?"} color={emp?.color||"#666"} size={38}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:T.textPrimary}}>{emp?.name} from {emp?.company}</div>
                {req.message&&<div style={{fontSize:12,color:T.textMuted,marginTop:6,fontStyle:"italic"}}>" {req.message} "</div>}
              </div>
            </div>
            {req.status==="pending"?(
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>respond(req.id,"approved")} style={{flex:1,background:T.green,border:"none",color:"#fff",borderRadius:9,padding:"8px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✓ Approve & Share Contact</button>
                <button onClick={()=>respond(req.id,"rejected")} style={{flex:1,background:"transparent",border:`1px solid ${T.red}44`,color:T.red,borderRadius:9,padding:"8px",fontWeight:600,fontSize:13,cursor:"pointer"}}>✗ Decline</button>
              </div>
            ):(
              <div style={{fontSize:13,color:req.status==="approved"?T.green:T.red}}>
                {req.status==="approved"?"✅ Approved — contact info shared":"✗ Declined"}
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// ─── SEEKER PROFILE ───────────────────────────────────────────────────────────
const JOB_TYPES_PREF = ["Full-time","Part-time","Contract","Freelance","Internship","Temporary"];
const SCHEDULES = ["Day shift","Night shift","Evening shift","Weekends","Mon-Fri","Flexible","Remote only","Hybrid","On-site only"];
const JOB_INDUSTRIES = ["Technology","Healthcare","Finance","Education","Marketing","Sales","Design","Data & AI","Engineering","Customer Service","Operations","Legal","Non-profit","Government","Retail","Hospitality","Construction","Transportation","Other"];

function SeekerProfile({ user, setUser, onUpgrade, onUpgradePremier, onLogout }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({name:user.name||"",role:user.role||"",bio:user.bio||"",location:user.location||"",phone:user.phone||""});
  const [showAccount, setShowAccount] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [accountForm, setAccountForm] = useState({email:user.email||"",displayName:user.name||"",newPassword:"",confirmPassword:""});
  const [accountMsg, setAccountMsg] = useState("");
  const [accountError, setAccountError] = useState("");
  const [showPwAcc, setShowPwAcc] = useState(false);
  const [prefs, setPrefs] = useState(user.jobPrefs||{types:[],schedules:[],industries:[],salaryMin:"",salaryMax:"",remoteOnly:false});
  const [cancelModal, setCancelModal] = useState(false);
  const isPro = user.plan==="pro";
  const save = () => { setUser(u=>({...u,...form})); setEditing(false); };
  const saveAccount = async () => {
    setAccountError(""); setAccountMsg("");
    if (accountForm.newPassword && accountForm.newPassword !== accountForm.confirmPassword) { setAccountError("Passwords don't match"); return; }
    if (accountForm.newPassword && accountForm.newPassword.length < 6) { setAccountError("Password must be at least 6 characters"); return; }
    try {
      const body = {};
      if (accountForm.email !== user.email) body.email = accountForm.email;
      if (accountForm.newPassword) body.password = accountForm.newPassword;
      if (Object.keys(body).length === 0) { setAccountMsg("No changes to save"); return; }
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
        method:"PUT", headers:{"Content-Type":"application/json","apikey":process.env.REACT_APP_SUPABASE_ANON_KEY,"Authorization":`Bearer ${user.token}`},
        body:JSON.stringify(body)
      });
      const data = await res.json();
      if (data.error) { setAccountError(data.error.message||"Update failed"); return; }
      if (body.email) setUser(u=>({...u,email:body.email}));
      if (accountForm.displayName && accountForm.displayName !== user.name) setUser(u=>({...u,name:accountForm.displayName,avatar:accountForm.displayName.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}));
      setAccountMsg("✅ Account updated successfully!");
      setAccountForm(f=>({...f,newPassword:"",confirmPassword:""}));
    } catch { setAccountError("Update failed. Please try again."); }
  };
  const togglePref = (key, val) => setPrefs(p=>({...p,[key]:p[key].includes(val)?p[key].filter(x=>x!==val):[...p[key],val]}));
  const savePrefs = () => { setUser(u=>({...u,jobPrefs:prefs})); setShowPrefs(false); };
  return (
    <div>
      <div style={{background:"linear-gradient(135deg,#0a1628,#0c1a30)",border:`1px solid ${T.border}`,borderRadius:20,padding:"24px 20px",marginBottom:16,textAlign:"center"}}>
        {/* Profile Photo Upload — Pro Only */}
        <div style={{display:"flex",justifyContent:"center",marginBottom:12,position:"relative"}}>
          {user.photoUrl ? (
            <div style={{position:"relative"}}>
              <img src={user.photoUrl} alt="Profile" style={{width:72,height:72,borderRadius:"50%",objectFit:"cover",border:`3px solid ${T.accentBright}`}}/>
              <div style={{position:"absolute",bottom:0,right:0,width:22,height:22,borderRadius:"50%",background:T.green,border:`2px solid ${T.bg}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10}}>●</div>
            </div>
          ) : (
            <Avatar initials={user.avatar} color={user.color} size={72} online/>
          )}
          {isPro && (
            <label style={{position:"absolute",bottom:0,right:"calc(50% - 36px)",width:22,height:22,borderRadius:"50%",background:T.accent,border:`2px solid ${T.bg}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12}}>
              📷
              <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{display:"none"}} onChange={e=>{
                const file=e.target.files[0]; if(!file) return;
                if(file.size>5*1024*1024){alert("Photo must be under 5MB");return;}
                const reader=new FileReader();
                reader.onload=ev=>setUser(u=>({...u,photoUrl:ev.target.result}));
                reader.readAsDataURL(file);
              }}/>
            </label>
          )}
        </div>
        {isPro ? (
          <div style={{fontSize:10,color:T.textMuted,marginBottom:8,textAlign:"center"}}>📷 Upload a professional headshot · JPG, PNG, or WEBP · Max 5MB</div>
        ) : (
          <div onClick={onUpgrade} style={{fontSize:11,color:T.gold,background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:8,padding:"5px 10px",marginBottom:8,textAlign:"center",cursor:"pointer"}}>🔒 Pro feature — Upgrade to add a profile photo</div>
        )}
        {editing?(
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14,textAlign:"left"}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Full Name</label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Your full name" style={IS}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.accentGlow,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Job Title / Current Role ✏️</label>
              <input value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} placeholder="e.g. Software Engineer, Product Designer" style={{...IS,border:`1px solid ${T.accentBright}44`}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Bio</label>
              {isPro ? <input value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} placeholder="Brief bio or tagline" style={IS}/> : <div style={{padding:"8px 12px",background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:10,fontSize:11,color:T.gold}}>🔒 Bio — Pro feature only</div>}
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Location</label>
              <input value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="City, State" style={IS}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:4}}>Phone</label>
              <input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="Phone number" style={IS}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={save} style={{flex:1,background:T.accent,border:"none",color:"#fff",borderRadius:10,padding:"10px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Save Changes</button>
              <button onClick={()=>setEditing(false)} style={{flex:1,background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"10px",fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ):(
          <>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:T.textPrimary}}>{user.name}</div>
            <div style={{fontSize:13,color:T.textSecondary,marginTop:4}}>{user.role||<span style={{color:T.textMuted,fontStyle:"italic"}}>Add your job title</span>}</div>
            {user.bio&&<div style={{fontSize:13,color:T.textMuted,marginTop:5}}>{user.bio}</div>}
            {user.location&&<div style={{fontSize:12,color:T.textMuted,marginTop:3}}>📍 {user.location}</div>}
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:12,flexWrap:"wrap"}}>
              <Pill color={isPro?T.accentBright:T.gold}>{isPro?"⚡ Pro":"✦ Basic"}</Pill>
              <Pill color={T.textMuted}>Joined {user.joined}</Pill>
            </div>
          </>
        )}
        {!editing&&<button onClick={()=>setEditing(true)} style={{marginTop:14,background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"7px 18px",fontSize:12,cursor:"pointer"}}>✏️ Edit Profile</button>}
      </div>
      {/* Account Settings */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,marginBottom:14,overflow:"hidden"}}>
        <button onClick={()=>setShowAccount(s=>!s)} style={{width:"100%",background:"none",border:"none",padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:36,height:36,borderRadius:10,background:T.accentSoft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>👤</div>
            <div style={{textAlign:"left"}}>
              <div style={{fontWeight:700,fontSize:13,color:T.textPrimary}}>Account Settings</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>Change email or password</div>
            </div>
          </div>
          <span style={{fontSize:14,color:T.textMuted}}>{showAccount?"▲":"▼"}</span>
        </button>
        {showAccount&&(
          <div style={{padding:"0 18px 18px",display:"flex",flexDirection:"column",gap:12,borderTop:`1px solid ${T.border}`}}>
            <div style={{height:12}}/>
            <div><label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Display Name / Username</label>
              <input value={accountForm.displayName||user.name||""} onChange={e=>setAccountForm(f=>({...f,displayName:e.target.value}))} placeholder="Your display name" style={IS}/></div>
            <div><label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Email Address</label>
              <input value={accountForm.email} onChange={e=>setAccountForm(f=>({...f,email:e.target.value}))} placeholder="your@email.com" type="email" style={IS}/></div>
            <div><label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>New Password</label>
              <div style={{position:"relative"}}>
                <input value={accountForm.newPassword} onChange={e=>setAccountForm(f=>({...f,newPassword:e.target.value}))} placeholder="Leave blank to keep current" type={showPwAcc?"text":"password"} style={{...IS,paddingRight:42}}/>
                <button onClick={()=>setShowPwAcc(s=>!s)} type="button" style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,color:T.textMuted,padding:0}}>{showPwAcc?"🙈":"👁️"}</button>
              </div></div>
            <div><label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Confirm New Password</label>
              <input value={accountForm.confirmPassword} onChange={e=>setAccountForm(f=>({...f,confirmPassword:e.target.value}))} placeholder="Confirm new password" type="password" style={IS}/></div>
            {accountError&&<div style={{fontSize:12,color:T.red,background:"#ef444415",border:"1px solid #ef444430",borderRadius:8,padding:"8px 12px"}}>{accountError}</div>}
            {accountMsg&&<div style={{fontSize:12,color:T.green,background:"#10b98115",border:"1px solid #10b98130",borderRadius:8,padding:"8px 12px"}}>{accountMsg}</div>}
            <button onClick={saveAccount} style={{background:T.accent,border:"none",color:"#fff",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Save Account Changes</button>
          </div>
        )}
      </div>

      {/* Job Preferences */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,marginBottom:14,overflow:"hidden"}}>
        <button onClick={()=>setShowPrefs(s=>!s)} style={{width:"100%",background:"none",border:"none",padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:36,height:36,borderRadius:10,background:"#10b98115",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>💼</div>
            <div style={{textAlign:"left"}}>
              <div style={{fontWeight:700,fontSize:13,color:T.textPrimary}}>Job Preferences</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{prefs.types.length>0||prefs.schedules.length>0?`${prefs.types.length+prefs.schedules.length} preferences set`:"Set your ideal job & schedule"}</div>
            </div>
          </div>
          <span style={{fontSize:14,color:T.textMuted}}>{showPrefs?"▲":"▼"}</span>
        </button>
        {showPrefs&&(
          <div style={{padding:"0 18px 18px",borderTop:`1px solid ${T.border}`}}>
            <div style={{height:14}}/>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:8}}>Preferred Job Types</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{JOB_TYPES_PREF.map(t=><button key={t} onClick={()=>togglePref("types",t)} style={{background:prefs.types.includes(t)?T.accent:"transparent",border:`1px solid ${prefs.types.includes(t)?T.accent:T.border}`,color:prefs.types.includes(t)?"#fff":T.textSecondary,borderRadius:20,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{t}</button>)}</div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:8}}>Preferred Work Schedule</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{SCHEDULES.map(s=><button key={s} onClick={()=>togglePref("schedules",s)} style={{background:prefs.schedules.includes(s)?T.green:"transparent",border:`1px solid ${prefs.schedules.includes(s)?T.green:T.border}`,color:prefs.schedules.includes(s)?"#fff":T.textSecondary,borderRadius:20,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{s}</button>)}</div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:8}}>Preferred Industries</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{JOB_INDUSTRIES.map(ind=><button key={ind} onClick={()=>togglePref("industries",ind)} style={{background:prefs.industries.includes(ind)?T.purple:"transparent",border:`1px solid ${prefs.industries.includes(ind)?T.purple:T.border}`,color:prefs.industries.includes(ind)?"#fff":T.textSecondary,borderRadius:20,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{ind}</button>)}</div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:8}}>Desired Salary Range</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div><label style={{fontSize:10,color:T.textMuted,display:"block",marginBottom:4}}>Minimum</label><input value={prefs.salaryMin} onChange={e=>setPrefs(p=>({...p,salaryMin:e.target.value}))} placeholder="e.g. $50,000" style={IS}/></div>
                <div><label style={{fontSize:10,color:T.textMuted,display:"block",marginBottom:4}}>Maximum</label><input value={prefs.salaryMax} onChange={e=>setPrefs(p=>({...p,salaryMax:e.target.value}))} placeholder="e.g. $80,000" style={IS}/></div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,padding:"10px 14px",background:T.surface,borderRadius:10}}>
              <div><div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>Remote Only</div><div style={{fontSize:11,color:T.textMuted}}>Only show remote opportunities</div></div>
              <button onClick={()=>setPrefs(p=>({...p,remoteOnly:!p.remoteOnly}))} style={{width:44,height:24,borderRadius:12,background:prefs.remoteOnly?T.green:T.border,border:"none",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:prefs.remoteOnly?23:3,transition:"left 0.2s"}}/>
              </button>
            </div>
            <button onClick={savePrefs} style={{width:"100%",background:T.green,border:"none",color:"#fff",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Save Preferences</button>
          </div>
        )}
      </div>

      {/* Employer Contact — Pro Only */}
      {!isPro&&(
        <div style={{background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:16,padding:16,marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:20}}>📨</span>
            <div style={{fontWeight:700,fontSize:13,color:T.gold}}>Employer Contact — Pro Only</div>
          </div>
          <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.6,marginBottom:10}}>
            Upgrade to Pro so employers can discover your profile and send you contact requests directly through Vaultr. Your personal information stays private until YOU approve!
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:10}}>
            {["✅ Employers can find your profile","✅ You approve or decline every request","✅ Your address & phone always hidden","✅ Full control over your privacy"].map(item=>(
              <div key={item} style={{fontSize:11,color:T.textSecondary}}>{item}</div>
            ))}
          </div>
          <button onClick={onUpgrade} style={{width:"100%",background:"linear-gradient(135deg,#f59e0b,#f97316)",border:"none",color:"#000",borderRadius:10,padding:"10px",fontWeight:800,fontSize:12,cursor:"pointer"}}>⚡ Upgrade to Pro — $9/month</button>
        </div>
      )}

      {/* Your Plan */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:20,marginBottom:14}}>
{/* Your Plan */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:20,marginBottom:14}}>
        <div style={{fontWeight:700,fontSize:13,color:T.textPrimary,marginBottom:14}}>Your Plan</div>
        {isPro?(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
              <div style={{width:42,height:42,borderRadius:11,background:"#f59e0b22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>⚡</div>
              <div><div style={{fontWeight:700,fontSize:14,color:T.accentBright}}>⚡ Vaultr Pro</div><div style={{fontSize:12,color:T.textSecondary}}>$9/month · All features active</div></div>
            </div>
            <div style={{background:"#8b5cf615",border:"1px solid #8b5cf644",borderRadius:12,padding:"14px 16px"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#8b5cf6",marginBottom:4}}>👑 Upgrade to Premier — $19/month</div>
              <div style={{fontSize:12,color:T.textSecondary,marginBottom:10,lineHeight:1.5}}>Unlock AI Resume Builder, Cover Letter Generator, Career DNA, Match Score % and more!</div>
              <button onClick={onUpgradePremier} style={{width:"100%",background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",border:"none",color:"#fff",borderRadius:10,padding:"10px",fontWeight:800,fontSize:12,cursor:"pointer"}}>👑 Upgrade to Premier</button>
            </div>
          </div>
        ):(
          <>
            <div style={{fontSize:13,color:T.textSecondary,marginBottom:12}}>You're on the <strong style={{color:T.textPrimary}}>Free plan</strong>. Upgrade to unlock all features.</div>
            <button onClick={onUpgradePremier} style={{width:"100%",background:"linear-gradient(135deg,#f59e0b,#f97316)",border:"none",color:"#000",borderRadius:12,padding:"13px",fontWeight:800,fontSize:14,cursor:"pointer"}}>👑 Upgrade to Premier — $19/month</button>
          </>
        )}
        {(isPro || user.plan === "premier") && (
          <a href="https://billing.stripe.com/p/login/dRm9AU0xs3bxdJB2dugfu00" target="_blank" rel="noopener noreferrer" style={{display:"block",marginTop:12,width:"100%",background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"10px",fontWeight:600,fontSize:13,textDecoration:"none",textAlign:"center",boxSizing:"border-box"}}>🔗 Manage Subscription</a>
        )}
      </div>
      </div>
      <button onClick={onLogout} style={{width:"100%",background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"12px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Sign Out</button>
    </div>
  );
}

// ─---SEEKER APP---─────────────────────────────────────────────────────────────
function SeekerApp({ user, setUser, onLogout }) {
  const [tab, setTab] = useState("jobs");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const isPro = user.plan==="pro";
const isFreePlan = user.plan !== "pro" && user.plan !== "premier";
const trialDays = user.joined && isFreePlan ? (/^\d{4}$/.test(user.joined) ? 3 : Math.max(0, 3 - Math.floor((Date.now() - new Date(user.joined).getTime()) / (1000*60*60*24)))) : 0;
const isTrialing = trialDays > 0;
const effectivelyPro = isPro || isTrialing;
  const unread = (DB.notifications[user.id]||[]).filter(n=>!n.read).length;
  const upgrade = () => { setUser(u=>({...u,plan:"pro"})); setShowUpgrade(false); };
  const [upgradeType, setUpgradeType] = useState("seeker");

  const NAV = [{id:"jobs",icon:"💼",label:"Jobs"},{id:"community",icon:"👥",label:"Community"},{id:"chat",icon:"💬",label:"Chat",badge:2},{id:"resume",icon:"📄",label:"Resume"},{id:"profile",icon:"👤",label:"Me"}];

  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.textPrimary,fontFamily:"'DM Sans',sans-serif",position:"relative"}}>
      <STYLES/>
      <header style={{background:"#07090fee",borderBottom:`1px solid ${T.border}`,padding:"0 16px",height:56,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:99,backdropFilter:"blur(16px)"}}>
       <div onClick={()=>setTab("jobs")} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
  <div style={{width:30,height:30,borderRadius:8,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14}}>V</div>
  <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:15,letterSpacing:"-0.03em"}}>Vaultr</span>
  <Pill color={T.accentBright}>Seeker</Pill>
</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {isPro
  ? <div style={{background:"linear-gradient(90deg,#1E3A8A,#2563EB)",border:"none",color:"#fff",borderRadius:20,padding:"4px 11px",fontSize:11,fontWeight:800}}>⚡ Pro</div>
  : <button onClick={()=>setShowUpgrade(true)} style={{background:"linear-gradient(90deg,#f59e0b,#f97316)",border:"none",color:"#000",borderRadius:20,padding:"4px 11px",fontSize:11,fontWeight:800,cursor:"pointer"}}>✦ Basic</button>
}
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowNotif(n=>!n)} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:15}}>
              🔔
              {unread>0&&<div style={{position:"absolute",top:-3,right:-3,width:15,height:15,borderRadius:"50%",background:T.red,fontSize:8,fontWeight:800,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${T.bg}`}}>{unread}</div>}
            </button>
            {showNotif&&<NotifPanel user={user} onClose={()=>setShowNotif(false)}/>}
          </div>
          <button onClick={onLogout} style={{background:"none",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:9,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>Sign Out</button>
        </div>
      </header>
      <nav style={{background:"#07090fee",borderBottom:`1px solid ${T.border}`,display:"flex",position:"sticky",top:56,zIndex:98,backdropFilter:"blur(16px)"}}>
        {NAV.map(n=>(
          <button key={n.id} onClick={()=>setTab(n.id)} style={{flex:1,background:"none",border:"none",padding:"9px 4px 7px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,borderBottom:`2px solid ${tab===n.id?T.accentBright:"transparent"}`,position:"relative"}}>
            <span style={{fontSize:17}}>{n.icon}</span>
            <span style={{fontSize:9,fontWeight:700,color:tab===n.id?T.accentGlow:T.textMuted,letterSpacing:"0.04em"}}>{n.label.toUpperCase()}</span>
            {n.badge&&<div style={{position:"absolute",top:6,right:"16%",width:14,height:14,borderRadius:"50%",background:T.red,fontSize:8,fontWeight:800,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>{n.badge}</div>}
          </button>
        ))}
      </nav>
      <main style={{padding:"18px 14px 40px",maxWidth:640,margin:"0 auto"}}>
        {/* Pro Welcome Greeting */}
        {isPro&&tab==="jobs"&&(
          <div style={{background:"linear-gradient(135deg,#1e3a8a22,#7c3aed22)",border:"1px solid #2563eb44",borderRadius:14,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:22}}>👋</span>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>Welcome back, {user.name?.split(" ")[0]}!</div>
              <div style={{fontSize:11,color:T.textMuted}}>You're on Vaultr Pro — all features unlocked ⚡</div>
            </div>
</div>
        
        )}
        {isTrialing&&<div style={{background:"linear-gradient(135deg,#5b4fe815,#7c3aed10)",border:"1px solid #5b4fe844",borderRadius:14,padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:12}}><div style={{fontSize:22,flexShrink:0}}>🎉</div><div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:700,color:"#a78bfa",marginBottom:2}}>You're on a free Pro trial — {trialDays} day{trialDays===1?"":"s"} left</div><div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>Enjoying Pro features? Upgrade before your trial ends to keep full access.</div></div><button onClick={()=>setShowUpgrade(true)} style={{background:"#5b4fe8",border:"none",color:"#fff",borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>Upgrade</button></div>}        {tab==="community"&&<SeekerCommunity user={user}/>}
        {tab==="chat"&&<SeekerChat user={user} onUpgrade={()=>setShowUpgrade(true)}/>}
        {tab==="resume"&&<SeekerResume user={user} setUser={setUser} onUpgrade={()=>setShowUpgrade(true)}/>}
        {tab==="jobs"&&<SeekerJobs user={user} onUpgrade={()=>setShowUpgrade(true)}/>}
        {tab==="profile"&&<SeekerProfile user={user} setUser={setUser} onUpgrade={()=>{setUpgradeType("seeker");setShowUpgrade(true);}} onUpgradePremier={()=>{setUpgradeType("premier");setShowUpgrade(true);}} onLogout={onLogout}/>}</main>
      {showUpgrade&&<UpgradeModal type={upgradeType} onClose={()=>setShowUpgrade(false)} onUpgrade={upgrade}/>}
    </div>
  );
}

// ─---EMPLOYER APP---───────────────────────────────────────────────────────────
function EmployerApp({ user, setUser, onLogout }) {
  const [tab, setTab] = useState("candidates");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const isPro = user.plan==="pro";
  const unread = (DB.notifications[user.id]||[]).filter(n=>!n.read).length;
  const upgrade = () => { setUser(u=>({...u,plan:"pro"})); setShowUpgrade(false); };

  const NAV = [{id:"candidates",icon:"🔍",label:"Talent"},{id:"jobs",icon:"📋",label:"Jobs"},{id:"requests",icon:"📨",label:"Requests"},{id:"profile",icon:"🏢",label:"Company"}];

  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.textPrimary,fontFamily:"'DM Sans',sans-serif",position:"relative"}}>
      <STYLES/>
      <header style={{background:"#07090fee",borderBottom:`1px solid ${T.border}`,padding:"0 16px",height:56,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:99,backdropFilter:"blur(16px)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:30,height:30,borderRadius:8,background:`linear-gradient(135deg,${T.employer},#065f46)`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14}}>V</div>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:15,letterSpacing:"-0.03em"}}>Vaultr</span>
          <Pill color={T.employerBright}>Employer</Pill>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {!isPro&&<button onClick={()=>setShowUpgrade(true)} style={{background:`linear-gradient(90deg,${T.employer},#065f46)`,border:"none",color:"#fff",borderRadius:20,padding:"4px 11px",fontSize:11,fontWeight:800,cursor:"pointer"}}>⚡ Pro</button>}
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowNotif(n=>!n)} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:9,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:15}}>
              🔔
              {unread>0&&<div style={{position:"absolute",top:-3,right:-3,width:15,height:15,borderRadius:"50%",background:T.red,fontSize:8,fontWeight:800,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${T.bg}`}}>{unread}</div>}
            </button>
            {showNotif&&<NotifPanel user={user} onClose={()=>setShowNotif(false)}/>}
          </div>
          <button onClick={onLogout} style={{background:"none",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:9,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>Sign Out</button>
        </div>
      </header>
      <nav style={{background:"#07090fee",borderBottom:`1px solid ${T.border}`,display:"flex",position:"sticky",top:56,zIndex:98,backdropFilter:"blur(16px)"}}>
        {NAV.map(n=>(
          <button key={n.id} onClick={()=>setTab(n.id)} style={{flex:1,background:"none",border:"none",padding:"9px 4px 7px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,borderBottom:`2px solid ${tab===n.id?T.employerBright:"transparent"}`}}>
            <span style={{fontSize:17}}>{n.icon}</span>
            <span style={{fontSize:9,fontWeight:700,color:tab===n.id?T.employerBright:T.textMuted,letterSpacing:"0.04em"}}>{n.label.toUpperCase()}</span>
          </button>
        ))}
      </nav>
      <main style={{padding:"18px 14px 40px",maxWidth:640,margin:"0 auto"}}>
        {tab==="candidates"&&<EmployerCandidates user={user} onUpgrade={()=>setShowUpgrade(true)}/>}
        {tab==="jobs"&&<EmployerJobs user={user} onUpgrade={()=>setShowUpgrade(true)}/>}
        {tab==="requests"&&<EmployerRequests user={user}/>}
        {tab==="profile"&&<EmployerProfile user={user} setUser={setUser} onUpgrade={()=>setShowUpgrade(true)} onLogout={onLogout}/>}
      </main>
      {showUpgrade&&<UpgradeModal type="employer" onClose={()=>setShowUpgrade(false)} onUpgrade={upgrade}/>}
    </div>
  );
}

// ─---EMPLOYER CANDIDATES---────────────────────────────────────────────────────
function EmployerCandidates({ user, onUpgrade }) {
  const [search, setSearch] = useState("");
  const [skill, setSkill] = useState("All");
  const [contactModal, setContactModal] = useState(null);
  const [sent, setSent] = useState(new Set(DB.contactRequests.filter(r=>r.employerId===user.id).map(r=>r.seekerId)));
  const isPro = user.plan==="pro";
  const SKILLS = ["All","React","Python","Figma","Go","Swift","SQL","TypeScript"];

  const candidates = DB.seekers.filter(s=>{
    if (!s.resume) return false;
    const matchSearch = !search||s.name.toLowerCase().includes(search.toLowerCase())||s.role.toLowerCase().includes(search.toLowerCase());
    const matchSkill = skill==="All"||(s.resume?.skills||[]).includes(skill);
    return matchSearch&&matchSkill;
  });

  const sendRequest = (seekerId,message) => {
    if (!isPro){onUpgrade();return;}
    DB.contactRequests.push({id:`cr${Date.now()}`,employerId:user.id,seekerId,status:"pending",message,jobId:1});
    setSent(s=>new Set([...s,seekerId]));
    setContactModal(null);
  };

  return (
    <div>
      <div style={{background:"linear-gradient(135deg,#061a12,#07201a)",border:`1px solid ${T.employer}33`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:13,color:T.employerBright,marginBottom:4}}>🔒 Privacy Policy</div>
        <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.55}}>Candidate contact details are <strong style={{color:T.textPrimary}}>never visible</strong> until they approve your contact request.</div>
      </div>
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or role..." style={{...IS,flex:1}}/>
      </div>
      <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:2}}>
        {SKILLS.map(s=><button key={s} onClick={()=>setSkill(s)} style={{background:skill===s?T.employer:"transparent",border:`1px solid ${skill===s?T.employer:T.border}`,color:skill===s?"#fff":T.textSecondary,borderRadius:20,padding:"5px 13px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{s}</button>)}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {candidates.map(seeker=>{
          const view = getSeekerProfile(seeker,"employer",user.id);
          const approved = DB.contactRequests.find(r=>r.employerId===user.id&&r.seekerId===seeker.id&&r.status==="approved");
          const pending = DB.contactRequests.find(r=>r.employerId===user.id&&r.seekerId===seeker.id&&r.status==="pending");
          return (
            <div key={seeker.id} style={{background:T.card,border:`1px solid ${approved?T.green+"44":T.border}`,borderRadius:14,padding:"16px 17px"}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12}}>
                <Avatar initials={seeker.avatar} color={seeker.color} size={48} online/>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <span style={{fontWeight:700,fontSize:15,color:T.textPrimary}}>{view.name}</span>
                    {seeker.plan==="pro"&&<Pill color={T.gold}>Pro</Pill>}
                  </div>
                  <div style={{fontSize:13,color:T.textSecondary}}>{view.role}</div>
                  {view.bio&&<div style={{fontSize:12,color:T.textMuted,marginTop:4}}>{view.bio}</div>}
                </div>
              </div>
              {view.resume&&(
                <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.textMuted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Resume Preview</div>
                  <div style={{display:"grid",gap:8}}>
                    <Row label="Skills" value={<div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{(view.resume.skills||[]).map(s=><Pill key={s} color={T.employerBright}>{s}</Pill>)}</div>}/>
                    <Row label="Experience" value={view.resume.experience||"Not specified"}/>
                    <Row label="Address" value={approved&&view.resume.sensitive?.address?<span style={{color:T.green,fontSize:13}}>📍 {view.resume.sensitive.address}</span>:<PrivacyShield label="Requires candidate approval"/>}/>
                    <Row label="Phone" value={approved&&view.resume.sensitive?.phone?<span style={{color:T.green,fontSize:13}}>📞 {view.resume.sensitive.phone}</span>:<PrivacyShield label="Requires candidate approval"/>}/>
                    <Row label="Email" value={approved&&view.resume.sensitive?.email?<span style={{color:T.green,fontSize:13}}>✉️ {view.resume.sensitive.email}</span>:<PrivacyShield label="Requires candidate approval"/>}/>
                  </div>
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                {approved?<div style={{flex:1,background:`${T.green}15`,border:`1px solid ${T.green}44`,borderRadius:10,padding:"9px 14px",fontSize:13,color:T.green,fontWeight:600,textAlign:"center"}}>✅ Contact Info Unlocked</div>
                :sent.has(seeker.id)||pending?<div style={{flex:1,background:"transparent",border:`1px solid ${T.border}`,borderRadius:10,padding:"9px 14px",fontSize:13,color:T.textMuted,textAlign:"center"}}>⏳ Request Pending</div>
                :<button onClick={()=>{if(!isPro){onUpgrade();return;}setContactModal(seeker);}} style={{flex:1,background:T.employer,border:"none",color:"#fff",borderRadius:10,padding:"9px",fontWeight:700,fontSize:13,cursor:"pointer"}}>{isPro?"📨 Send Contact Request":"🔒 Contact (Pro)"}</button>}
              </div>
            </div>
          );
        })}
        {candidates.length===0&&<div style={{textAlign:"center",padding:"40px 20px",color:T.textMuted,fontSize:13}}>No candidates match your filters</div>}
      </div>
      {contactModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20,backdropFilter:"blur(6px)"}}>
          <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:20,padding:28,maxWidth:380,width:"100%",animation:"fadeUp 0.25s ease"}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,marginBottom:6}}>Contact Request</div>
            <div style={{fontSize:13,color:T.textSecondary,marginBottom:18,lineHeight:1.5}}>Send a request to <strong style={{color:T.textPrimary}}>{contactModal.name}</strong>. They choose whether to share their contact info.</div>
            <textarea placeholder="Introduce yourself and explain why you're reaching out..." rows={4} style={{...IS,resize:"vertical",lineHeight:1.55,marginBottom:14}} id="contactMsg"/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{const msg=document.getElementById("contactMsg").value;sendRequest(contactModal.id,msg);}} style={{flex:1,background:T.employer,border:"none",color:"#fff",borderRadius:12,padding:"12px",fontWeight:800,fontSize:14,cursor:"pointer"}}>Send Request</button>
              <button onClick={()=>setContactModal(null)} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"12px 18px",fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─---EMPLOYER JOBS---──────────────────────────────────────────────────────────
function EmployerJobs({ user, onUpgrade }) {
  const [myJobs, setMyJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [form, setForm] = useState({
    title:"", location:"", salary:"", type:"Full-time", remote:"On-site",
    tags:"", desc:"", companyAbout:"", positionScript:"",
    applicationUrl:"", contactEmail:"", deadline:"", urgent:false
  });
  const isPro = user.plan==="pro";
  const JOB_TYPES_EMP = ["Full-time","Part-time","Contract","Internship","Temporary","Freelance"];
  const WORK_MODES = ["On-site","Remote","Hybrid"];

  const resetForm = () => setForm({title:"",location:"",salary:"",type:"Full-time",remote:"On-site",tags:"",desc:"",companyAbout:"",positionScript:"",applicationUrl:"",contactEmail:"",deadline:"",urgent:false});

  const post = () => {
    if (!form.title||!form.location) { alert("Please fill in job title and location"); return; }
    if (!isPro){onUpgrade();return;}
    const newJob = {
      ...form, id:Date.now(),
      tags:form.tags.split(",").map(t=>t.trim()).filter(Boolean),
      company:user.company, employerId:user.id,
      logo:user.company?.[0]||"?", logoColor:user.color,
      hot:form.urgent, posted:"Just now", source:"Vaultr",
      status:"active"
    };
    if (editJob) {
      setMyJobs(j=>j.map(jj=>jj.id===editJob.id?{...newJob,id:editJob.id}:jj));
      setEditJob(null);
    } else {
      setMyJobs(j=>[newJob,...j]);
    }
    resetForm(); setShowForm(false);
  };

  const deleteJob = (id) => { if(window.confirm("Delete this job listing?")) setMyJobs(j=>j.filter(jj=>jj.id!==id)); };

  const startEdit = (job) => {
    setForm({...job, tags:(job.tags||[]).join(", ")});
    setEditJob(job); setShowForm(true);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Your Job Listings</div>
          <div style={{fontSize:11,color:T.textMuted}}>{myJobs.length} active posting{myJobs.length!==1?"s":""}</div>
        </div>
        <button onClick={()=>{if(!isPro){onUpgrade();return;}resetForm();setEditJob(null);setShowForm(s=>!s);}}
          style={{background:showForm?"transparent":T.employer,border:`1px solid ${T.employer}`,color:showForm?T.employer:"#fff",borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
          {showForm?"✕ Cancel":isPro?"+ Post New Job":"🔒 Post Job (Pro)"}
        </button>
      </div>

      {showForm&&(
        <div style={{background:T.card,border:`1px solid ${T.employer}44`,borderRadius:16,padding:"20px 16px",marginBottom:16}}>
          <div style={{fontWeight:800,fontSize:15,marginBottom:16,color:T.textPrimary}}>
            {editJob?"✏️ Edit Job Listing":"📋 Post New Job Listing"}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>

            {/* Job Title */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Job Title *</label>
              <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Senior Software Engineer, Marketing Manager" style={IS}/>
            </div>

            {/* Location */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Location *</label>
              <input value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="e.g. Austin, TX or Remote" style={IS}/>
            </div>

            {/* Job Type + Work Mode */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Job Type</label>
                <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={{...IS,cursor:"pointer"}}>
                  {JOB_TYPES_EMP.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Work Mode</label>
                <select value={form.remote} onChange={e=>setForm(f=>({...f,remote:e.target.value}))} style={{...IS,cursor:"pointer"}}>
                  {WORK_MODES.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Salary */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Salary Range</label>
              <input value={form.salary} onChange={e=>setForm(f=>({...f,salary:e.target.value}))} placeholder="e.g. $60,000 - $80,000 per year or $25/hour" style={IS}/>
            </div>

            {/* Skills */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Required Skills</label>
              <input value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="e.g. JavaScript, React, Node.js (comma-separated)" style={IS}/>
            </div>

            {/* Application URL */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Application URL (where candidates apply)</label>
              <input value={form.applicationUrl} onChange={e=>setForm(f=>({...f,applicationUrl:e.target.value}))} placeholder="https://yourcompany.com/careers/apply or email link" style={IS}/>
            </div>

            {/* Contact Email */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>HR / Recruiter Email</label>
              <input value={form.contactEmail} onChange={e=>setForm(f=>({...f,contactEmail:e.target.value}))} placeholder="hr@yourcompany.com" type="email" style={IS}/>
            </div>

            {/* Application Deadline */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Application Deadline (optional)</label>
              <input value={form.deadline} onChange={e=>setForm(f=>({...f,deadline:e.target.value}))} type="date" style={IS}/>
            </div>

            {/* About Company */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>About Your Company</label>
              <textarea value={form.companyAbout} onChange={e=>setForm(f=>({...f,companyAbout:e.target.value}))} placeholder="Tell candidates about your company — mission, culture, size, what makes you unique..." rows={3} style={{...IS,resize:"vertical",lineHeight:1.55}}/>
            </div>

            {/* Position Description */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Position Description *</label>
              <textarea value={form.desc} onChange={e=>setForm(f=>({...f,desc:e.target.value}))} placeholder="Describe the role, responsibilities, qualifications, and what a typical day looks like..." rows={4} style={{...IS,resize:"vertical",lineHeight:1.55}}/>
            </div>

            {/* Position Pitch */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Why Join Us? (Pitch to Candidates)</label>
              <textarea value={form.positionScript} onChange={e=>setForm(f=>({...f,positionScript:e.target.value}))} placeholder="Write a compelling pitch — why should they choose your company? Benefits, growth opportunities, culture..." rows={3} style={{...IS,resize:"vertical",lineHeight:1.55}}/>
            </div>

            {/* Urgent Toggle */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:T.surface,borderRadius:10}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>🔥 Mark as Urgent</div>
                <div style={{fontSize:11,color:T.textMuted}}>Highlights this listing as high priority</div>
              </div>
              <button onClick={()=>setForm(f=>({...f,urgent:!f.urgent}))}
                style={{width:44,height:24,borderRadius:12,background:form.urgent?T.red:T.border,border:"none",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:form.urgent?23:3,transition:"left 0.2s"}}/>
              </button>
            </div>

            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={post} style={{flex:1,background:T.employer,border:"none",color:"#fff",borderRadius:10,padding:"12px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                {editJob?"✅ Update Listing":"🚀 Post Job Listing"}
              </button>
              <button onClick={()=>{setShowForm(false);setEditJob(null);resetForm();}} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"12px 16px",cursor:"pointer",fontSize:13}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Job Listings */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {myJobs.map(job=>(
          <div key={job.id} style={{background:T.card,border:`1px solid ${job.urgent?T.red+"44":T.border}`,borderRadius:14,padding:"16px"}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{fontWeight:800,fontSize:15,color:T.textPrimary}}>{job.title}</div>
                  {job.urgent&&<span style={{fontSize:10,fontWeight:700,color:T.red,background:`${T.red}15`,border:`1px solid ${T.red}33`,borderRadius:6,padding:"1px 6px"}}>🔥 URGENT</span>}
                </div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:3}}>{job.company} · {job.location} · {job.type} · {job.remote}</div>
                {job.salary&&<div style={{fontSize:13,fontWeight:700,color:T.green,marginTop:3}}>{job.salary}</div>}
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={()=>startEdit(job)} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:8,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>✏️ Edit</button>
                <button onClick={()=>deleteJob(job.id)} style={{background:"transparent",border:`1px solid ${T.red}44`,color:T.red,borderRadius:8,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>🗑️</button>
              </div>
            </div>
            {(job.tags||[]).length>0&&(
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
                {(job.tags||[]).map(t=><Pill key={t} color={T.employerBright}>{t}</Pill>)}
              </div>
            )}
            {job.desc&&<div style={{fontSize:12,color:T.textMuted,lineHeight:1.6,marginBottom:8}}>{job.desc.length>150?job.desc.slice(0,150)+"...":job.desc}</div>}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <Pill color={T.textMuted}>📅 Posted {job.posted}</Pill>
              {job.deadline&&<Pill color={T.gold}>⏰ Deadline: {new Date(job.deadline).toLocaleDateString()}</Pill>}
              {job.applicationUrl&&(
                <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:11,color:T.employerBright,textDecoration:"none",fontWeight:600,border:`1px solid ${T.employer}44`,borderRadius:6,padding:"3px 8px"}}>
                  🔗 View Application Link
                </a>
              )}
            </div>
          </div>
        ))}
        {myJobs.length===0&&(
          <div style={{textAlign:"center",padding:"48px 20px",color:T.textMuted}}>
            <div style={{fontSize:36,marginBottom:10}}>📋</div>
            <div style={{fontSize:14,fontWeight:700,color:T.textSecondary,marginBottom:6}}>No job listings yet</div>
            <div style={{fontSize:12}}>Click "+ Post New Job" to create your first listing!</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─---EMPLOYER REQUESTS---──────────────────────────────────────────────────────
function EmployerRequests({ user }) {
  const myReqs = DB.contactRequests.filter(r=>r.employerId===user.id);
  return (
    <div>
      <Section title={`${myReqs.length} Contact Requests`}>
        {myReqs.map(req=>{
          const seeker = DB.seekers.find(s=>s.id===req.seekerId);
          const view = getSeekerProfile(seeker,"employer",user.id);
          return (
            <div key={req.id} style={{background:T.card,border:`1px solid ${req.status==="approved"?T.green+"44":req.status==="rejected"?T.red+"22":T.border}`,borderRadius:13,padding:"14px 16px",marginBottom:10}}>
              <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:10}}>
                <Avatar initials={seeker?.avatar||"?"} color={seeker?.color||"#666"} size={40}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:T.textPrimary}}>{view.name}</div>
                  <div style={{fontSize:12,color:T.textSecondary}}>{view.role}</div>
                </div>
                <Pill color={req.status==="approved"?T.green:req.status==="rejected"?T.red:T.gold}>
                  {req.status==="approved"?"Approved":req.status==="rejected"?"Declined":"Pending"}
                </Pill>
              </div>
              {req.status==="approved"&&seeker?.resume?.sensitive&&(
                <div style={{background:`${T.green}0d`,border:`1px solid ${T.green}33`,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.green,marginBottom:8,letterSpacing:"0.06em"}}>CONTACT INFO UNLOCKED</div>
                  <div style={{display:"grid",gap:6}}>
                    <Row label="Address" value={<span style={{color:T.green,fontSize:13}}>📍 {seeker.resume.sensitive.address}</span>}/>
                    <Row label="Phone" value={<span style={{color:T.green,fontSize:13}}>📞 {seeker.resume.sensitive.phone}</span>}/>
                    <Row label="Email" value={<span style={{color:T.green,fontSize:13}}>✉️ {seeker.resume.sensitive.email}</span>}/>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {myReqs.length===0&&<div style={{textAlign:"center",padding:"36px",color:T.textMuted,fontSize:13}}>No contact requests yet. Browse candidates!</div>}
      </Section>
    </div>
  );
}

// ─---EMPLOYER PROFILE---───────────────────────────────────────────────────────
function EmployerProfile({ user, setUser, onUpgrade, onLogout }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({company:user.company,industry:user.industry,size:user.size});
  const isPro = user.plan==="pro";
  const save = () => { setUser(u=>({...u,...form})); setEditing(false); };
  return (
    <div>
      <div style={{background:"linear-gradient(135deg,#061a12,#07201a)",border:`1px solid ${T.employer}33`,borderRadius:20,padding:"24px 20px",marginBottom:16,textAlign:"center"}}>
        {/* Company Banner */}
        {user.bannerUrl && (
          <div style={{width:"100%",height:80,borderRadius:12,overflow:"hidden",marginBottom:12,position:"relative"}}>
            <img src={user.bannerUrl} alt="Company Banner" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
        )}
        {/* Company Logo */}
        <div style={{display:"flex",justifyContent:"center",marginBottom:8,position:"relative"}}>
          {user.logoUrl ? (
            <img src={user.logoUrl} alt="Company Logo" style={{width:72,height:72,borderRadius:14,objectFit:"cover",border:`2px solid ${T.employer}44`,background:"#fff"}}/>
          ) : (
            <Avatar initials={user.avatar} color={user.color} size={72}/>
          )}
          <label style={{position:"absolute",bottom:0,right:"calc(50% - 36px)",width:22,height:22,borderRadius:"50%",background:T.employer,border:`2px solid ${T.bg}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12}}>
            📷
            <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{display:"none"}} onChange={e=>{
              const file=e.target.files[0]; if(!file) return;
              if(file.size>5*1024*1024){alert("Logo must be under 5MB");return;}
              const reader=new FileReader();
              reader.onload=ev=>setUser(u=>({...u,logoUrl:ev.target.result}));
              reader.readAsDataURL(file);
            }}/>
          </label>
        </div>
        <div style={{fontSize:10,color:T.textMuted,marginBottom:4}}>📷 Company Logo · JPG, PNG · Max 5MB</div>
        {/* Banner Upload */}
        <label style={{display:"inline-flex",alignItems:"center",gap:5,background:`${T.employer}15`,border:`1px solid ${T.employer}33`,borderRadius:20,padding:"4px 12px",fontSize:11,color:T.employerBright,cursor:"pointer",marginBottom:8}}>
          🖼️ {user.bannerUrl?"Change Banner":"Upload Banner"}
          <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{display:"none"}} onChange={e=>{
            const file=e.target.files[0]; if(!file) return;
            if(file.size>10*1024*1024){alert("Banner must be under 10MB");return;}
            const reader=new FileReader();
            reader.onload=ev=>setUser(u=>({...u,bannerUrl:ev.target.result}));
            reader.readAsDataURL(file);
          }}/>
        </label>
        {editing?(
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
            {[["company","Company name"],["industry","Industry"],["size","Company size"]].map(([k,p])=>(
              <input key={k} value={form[k]||""} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={p} style={IS}/>
            ))}
            <div style={{display:"flex",gap:8}}>
              <button onClick={save} style={{flex:1,background:T.employer,border:"none",color:"#fff",borderRadius:10,padding:"10px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Save</button>
              <button onClick={()=>setEditing(false)} style={{flex:1,background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"10px",fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ):(
          <>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:T.textPrimary}}>{user.company}</div>
            <div style={{fontSize:13,color:T.textSecondary,marginTop:4}}>{user.industry} · {user.size} employees</div>
            <div style={{fontSize:13,color:T.textMuted,marginTop:4}}>Managed by {user.name}</div>
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:12}}>
              <Pill color={isPro?T.green:T.textMuted}>{isPro?"⚡ Pro Employer":"Free Plan"}</Pill>
            </div>
          </>
        )}
        {!editing&&<button onClick={()=>setEditing(true)} style={{marginTop:14,background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:10,padding:"7px 18px",fontSize:12,cursor:"pointer"}}>Edit Company</button>}
      </div>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:20,marginBottom:14}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Your Plan</div>
        {isPro?(
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:42,height:42,borderRadius:11,background:`${T.employer}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>🏢</div>
            <div><div style={{fontWeight:700,fontSize:14,color:T.employerBright}}>Vaultr Employer Pro</div><div style={{fontSize:12,color:T.textSecondary}}>$9/month · Unlimited hiring access</div></div>
          </div>
        ):(
          <>
            <div style={{fontSize:13,color:T.textSecondary,marginBottom:12}}>Upgrade to search candidates and post jobs.</div>
            <button onClick={onUpgrade} style={{width:"100%",background:`linear-gradient(135deg,${T.employer},#065f46)`,border:"none",color:"#fff",borderRadius:12,padding:"13px",fontWeight:800,fontSize:14,cursor:"pointer"}}>⚡ Upgrade — $9/month</button>
          </>
        )}
      </div>
      <button onClick={onLogout} style={{width:"100%",background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:12,padding:"12px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Sign Out</button>
    </div>
  );
}

// ─---SUPABASE CLIENT---────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Simple Supabase auth helper without SDK dependency
// ─---EMAIL HELPER---───────────────────────────────────────────────────────────
const sendWelcomeEmail = async (email, name, plan) => {
  const isPro = plan === "pro";
  const subject = isPro ? "⚡ Welcome to Vaultr Pro!" : "🚀 Welcome to Vaultr!";
  const html = isPro ? `
    <div style="font-family:'DM Sans',sans-serif;max-width:520px;margin:0 auto;background:#07090f;color:#f0f4ff;padding:32px;border-radius:16px;">
      <div style="text-align:center;margin-bottom:24px;">
        <div style="width:60px;height:60px;border-radius:16px;background:linear-gradient(135deg,#2563eb,#7c3aed);display:inline-flex;align-items:center;justify-content:center;font-size:28px;font-weight:800;color:#fff;margin-bottom:12px;">V</div>
        <h1 style="font-size:24px;font-weight:800;color:#f0f4ff;margin:0;">You're officially Pro! ⚡</h1>
      </div>
      <p style="color:#8899bb;font-size:15px;line-height:1.6;">Hey ${name}, welcome to <strong style="color:#f0f4ff;">Vaultr Pro</strong>! Your subscription is active and all features are unlocked.</p>
      <div style="background:#101520;border:1px solid #182030;border-radius:12px;padding:20px;margin:20px 0;">
        <p style="color:#f59e0b;font-weight:700;margin:0 0 12px;">✅ Your Pro features:</p>
        <ul style="color:#8899bb;font-size:14px;line-height:2;margin:0;padding-left:20px;">
          <li>Upload up to 3 privacy-protected resumes</li>
          <li>AI Career Coach — unlimited</li>
          <li>Apply to jobs directly</li>
          <li>Send & receive direct messages</li>
          <li>Salary range filters</li>
        </ul>
      </div>
      <div style="text-align:center;margin-top:24px;">
        <a href="https://app.vaultrapps.com" style="background:linear-gradient(135deg,#f59e0b,#f97316);color:#000;padding:14px 32px;border-radius:12px;font-weight:800;font-size:15px;text-decoration:none;">Start Using Pro →</a>
      </div>
      <p style="color:#445577;font-size:12px;text-align:center;margin-top:24px;">Questions? Email us at <a href="mailto:support@vaultrapps.com" style="color:#3b82f6;">support@vaultrapps.com</a></p>
    </div>
  ` : `
    <div style="font-family:'DM Sans',sans-serif;max-width:520px;margin:0 auto;background:#07090f;color:#f0f4ff;padding:32px;border-radius:16px;">
      <div style="text-align:center;margin-bottom:24px;">
        <div style="width:60px;height:60px;border-radius:16px;background:linear-gradient(135deg,#2563eb,#7c3aed);display:inline-flex;align-items:center;justify-content:center;font-size:28px;font-weight:800;color:#fff;margin-bottom:12px;">V</div>
        <h1 style="font-size:24px;font-weight:800;color:#f0f4ff;margin:0;">Welcome to Vaultr! 🚀</h1>
      </div>
      <p style="color:#8899bb;font-size:15px;line-height:1.6;">Hey ${name}, you're in! Vaultr is your career community built for job seekers nationwide.</p>
      <div style="background:#101520;border:1px solid #182030;border-radius:12px;padding:20px;margin:20px 0;">
        <p style="color:#3b82f6;font-weight:700;margin:0 0 12px;">🚀 Get started:</p>
        <ul style="color:#8899bb;font-size:14px;line-height:2;margin:0;padding-left:20px;">
          <li>Search thousands of verified jobs nationwide</li>
          <li>Join the Seeker Circle community</li>
          <li>Upload your resume (privacy protected)</li>
          <li>Upgrade to Pro for unlimited features</li>
        </ul>
      </div>
      <div style="text-align:center;margin-top:24px;">
        <a href="https://app.vaultrapps.com" style="background:linear-gradient(135deg,#2563eb,#7c3aed);color:#fff;padding:14px 32px;border-radius:12px;font-weight:800;font-size:15px;text-decoration:none;">Start Job Hunting →</a>
      </div>
      <p style="color:#445577;font-size:12px;text-align:center;margin-top:24px;">Questions? Email us at <a href="mailto:support@vaultrapps.com" style="color:#3b82f6;">support@vaultrapps.com</a></p>
    </div>
  `;

  try {
    await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${process.env.REACT_APP_RESEND_KEY}`
      },
      body: JSON.stringify({
        from: "Vaultr <noreply@vaultrapps.com>",
        to: [email],
        subject,
        html
      })
    });
  } catch(e) {
    console.error("Email send failed:", e);
  }
  const sendPasswordResetEmail = async (email, resetLink) => {
  try {
    await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${process.env.REACT_APP_RESEND_KEY}`
      },
      body: JSON.stringify({
        from: "Vaultr <noreply@vaultrapps.com>",
        to: [email],
        subject: "Reset your Vaultr password",
        html: `<div style="font-family:'DM Sans',sans-serif;max-width:520px;margin:0 auto;background:#0d1117;padding:32px;border-radius:16px;">
          <h1 style="color:#f0f4ff;font-size:24px;font-weight:800;">Reset your password</h1>
          <p style="color:#8899bb;font-size:15px;line-height:1.6;">Click the button below to reset your Vaultr password. This link expires in 1 hour.</p>
          <a href="${resetLink}" style="display:inline-block;background:linear-gradient(135deg,#3b82f6,#8b5cf6);color:#fff;padding:14px 28px;border-radius:8px;text-decoration:none;font-weight:700;margin:24px 0;">Reset Password</a>
          <p style="color:#445577;font-size:12px;">If you didn't request this, you can safely ignore this email.</p>
        </div>`
      })
    });
  } catch(e) {
    console.error("Password reset email failed:", e);
  }
};
};const supabaseAuth = {
  async signUp(email, password, metadata) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json", 
        "apikey": SUPABASE_KEY,
        "Authorization": `Bearer ${SUPABASE_KEY}`
      },
      body: JSON.stringify({ 
        email, 
        password, 
        data: metadata,
        options: { emailRedirectTo: null }
      })
    });
    return res.json();
  },
  async signIn(email, password) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
      body: JSON.stringify({ email, password })
    });
    return res.json();
  },
async resetPassword(email) {
  await sendPasswordResetEmail(email, 'https://app.vaultrapps.com/reset-password');
  return { success: true };
},

  async getUser(token) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: { 
        "apikey": SUPABASE_KEY, 
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    if (!res.ok) return null;
    return res.json();
  },
  async refreshToken(refresh_token) {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
      body: JSON.stringify({ refresh_token })
    });
    return res.json();
  },
  saveSession(session, userData, rememberMe = true, highSecurity = false) {
    try {
      const expiry = rememberMe
        ? Date.now() + (30 * 24 * 60 * 60 * 1000)  // 30 days if remember me
        : Date.now() + (24 * 60 * 60 * 1000);        // 1 day if not
      localStorage.setItem("vaultr_session", JSON.stringify({
        ...session,
        userData,
        savedAt: Date.now(),
        lastActive: Date.now(),
        rememberMe,
        highSecurity,
        expiresAt: expiry
      }));
    } catch {}
  },
  updateActivity() {
    try {
      const s = JSON.parse(localStorage.getItem("vaultr_session"));
      if (s) {
        s.lastActive = Date.now();
        localStorage.setItem("vaultr_session", JSON.stringify(s));
      }
    } catch {}
  },
  loadSession() {
    try {
      const s = JSON.parse(localStorage.getItem("vaultr_session"));
      if (!s) return null;
      // Check absolute expiry (30 days)
      if (s.expiresAt && Date.now() > s.expiresAt) {
        localStorage.removeItem("vaultr_session");
        return null;
      }
      // Check inactivity timeout
      // High security = 30 minutes, Normal = 7 days
      const inactivityLimit = s.highSecurity
        ? 30 * 60 * 1000              // 30 minutes
        : 7 * 24 * 60 * 60 * 1000;   // 7 days
      if (s.lastActive && Date.now() - s.lastActive > inactivityLimit) {
        localStorage.removeItem("vaultr_session");
        return null;
      }
      return s;
    } catch { return null; }
  },
  clearSession() {
    try { localStorage.removeItem("vaultr_session"); } catch {}
  }
};

// ─---ROOT---───────────────────────────────────────────────────────────────────

function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
const token = params.get("token") || params.get("access_token") || hashParams.get("access_token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

const handleSubmit = async () => {
  setError("");
  if (!token) { setError("Invalid or expired reset link. Please request a new one."); return; }
  if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
  if (password !== confirm) { setError("Passwords don't match."); return; }
  setStatus("loading");
  try {
    // First exchange the token for a session
    const sessionRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/token?grant_type=pkce`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ access_token: token })
    });

    // Then update the password using the token directly
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ password })
    });
    const data = await res.json();
    if (data.error) {
      setError(data.error.message || "Reset failed. The link may have expired.");
      setStatus("error");
    } else {
      setStatus("success");
      setTimeout(() => { window.location.href = "/"; }, 3000);
    }
  } catch(e) {
    setError("Something went wrong. Please try again.");
    setStatus("error");
  }
};

  return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <STYLES/>
      <div style={{width:"100%",maxWidth:400,animation:"fadeUp 0.4s ease"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:24,color:"#fff",margin:"0 auto 12px"}}>V</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:T.textPrimary}}>Reset Your Password</div>
          <div style={{fontSize:13,color:T.textSecondary,marginTop:6}}>Enter your new password below</div>
        </div>

        {status === "success" ? (
          <div style={{background:"#10b98115",border:"1px solid #10b98144",borderRadius:16,padding:"28px 24px",textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>✅</div>
            <div style={{fontWeight:800,fontSize:18,color:T.green,marginBottom:8}}>Password Updated!</div>
            <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6}}>Your password has been reset successfully. Redirecting you to login in 3 seconds...</div>
          </div>
        ) : (
          <div style={{background:T.card,border:`1px solid ${T.borderLight}`,borderRadius:20,padding:"26px 22px"}}>
            {!token && (
              <div style={{background:"#ef444415",border:"1px solid #ef444430",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:13,color:T.red}}>
                ⚠️ This reset link is invalid or has expired. <a href="/" style={{color:T.accentGlow}}>Request a new one</a>.
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>New Password</label>
                <input
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  type="password"
                  placeholder="At least 6 characters"
                  style={IS}
                />
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",display:"block",marginBottom:5}}>Confirm Password</label>
                <input
                  value={confirm}
                  onChange={e=>setConfirm(e.target.value)}
                  type="password"
                  placeholder="Repeat your new password"
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                  style={IS}
                />
              </div>
              {error && (
                <div style={{fontSize:12,color:T.red,background:"#ef444415",border:"1px solid #ef444430",borderRadius:8,padding:"8px 12px"}}>
                  {error}
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={status==="loading"}
                style={{width:"100%",marginTop:4,background:"linear-gradient(135deg,#2563eb,#7c3aed)",border:"none",color:"#fff",borderRadius:12,padding:"13px",fontWeight:800,fontSize:14,cursor:status==="loading"?"not-allowed":"pointer",opacity:status==="loading"?0.7:1}}
              >
                {status==="loading" ? "Updating..." : "Set New Password →"}
              </button>
            </div>
            <div style={{textAlign:"center",marginTop:14}}>
              <a href="/" style={{fontSize:12,color:T.textMuted,textDecoration:"none"}}>← Back to Sign In</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function TermsOfServicePage() {
  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.textPrimary,fontFamily:"'DM Sans',sans-serif",padding:"40px 20px"}}>
      <STYLES/>
      <div style={{maxWidth:680,margin:"0 auto"}}>
        <div style={{marginBottom:32}}>
          <a href="/" style={{color:T.accentGlow,fontSize:13,textDecoration:"none"}}>← Back to Vaultr</a>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:"#fff"}}>V</div>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:T.textPrimary}}>Vaultr Terms of Service</span>
        </div>
        <div style={{fontSize:12,color:T.textMuted,marginBottom:32}}>Last updated: June 2, 2026 · Vaultr LLC · Jacksonville, FL</div>

        {[
          {title:"1. Acceptance of Terms", content:"By accessing or using Vaultr at app.vaultrapps.com, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our service. These terms apply to all users including job seekers and employers."},
          {title:"2. Description of Service", content:"Vaultr is a job search and career platform that provides job listings, scam detection, AI career coaching, resume sharing, and community features. We offer Free and Pro subscription plans. Features vary by plan and are subject to change with notice."},
          {title:"3. User Accounts", content:"You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account and password. You must notify us immediately at support@vaultrapps.com of any unauthorized use of your account. You must be at least 16 years old to use Vaultr."},
          {title:"4. Acceptable Use", content:"You agree not to use Vaultr to post false or misleading job listings, harass or harm other users, attempt to access other users' private information without consent, violate any applicable laws or regulations, scrape or copy our platform data, or impersonate another person or organization."},
          {title:"5. Job Listings & Scam Detection", content:"Vaultr provides scam detection as a helpful tool but does not guarantee the accuracy or legitimacy of any job listing. We aggregate listings from third-party sources and cannot verify every posting. Always research employers independently and never pay fees to apply for a job."},
          {title:"6. Pro Subscription & Payments", content:"Pro subscriptions are billed monthly at $9/month or annually at $79/year. Payments are processed securely by Stripe. Subscriptions automatically renew until cancelled. You may cancel at any time and will retain Pro access until the end of your billing period. Refunds are handled on a case-by-case basis — contact support@vaultrapps.com."},
          {title:"7. Privacy & Resume Data", content:"Your resume and personal contact information is protected and never shared without your explicit approval. Employers can only access your contact details after you approve their request. See our Privacy Policy at app.vaultrapps.com/privacy for full details on how we handle your data."},
          {title:"8. AI Features", content:"Vaultr's AI Career Coach and other AI features are powered by Anthropic's Claude AI. AI responses are for informational purposes only and do not constitute professional career, legal, or financial advice. Always verify important information independently."},
          {title:"9. Intellectual Property", content:"All content, features, and functionality of Vaultr including but not limited to text, graphics, logos, and software are owned by Vaultr LLC and protected by intellectual property laws. You may not copy, modify, or distribute our content without written permission."},
          {title:"10. Limitation of Liability", content:"Vaultr LLC is not liable for any indirect, incidental, or consequential damages arising from your use of the platform, including but not limited to lost employment opportunities, data loss, or reliance on job listings or AI-generated content. Our total liability is limited to the amount you paid us in the 3 months preceding any claim."},
          {title:"11. Termination", content:"We reserve the right to suspend or terminate your account at any time for violation of these terms. You may delete your account at any time by contacting support@vaultrapps.com. Upon termination, your data will be handled per our Privacy Policy."},
          {title:"12. Changes to Terms", content:"We may update these Terms of Service from time to time. We will notify you of significant changes by email or in-app notice. Continued use of Vaultr after changes constitutes acceptance of the updated terms."},
          {title:"13. Governing Law", content:"These Terms are governed by the laws of the State of Florida, United States. Any disputes shall be resolved in the courts of Jacksonville, Florida. If any provision of these terms is found invalid, the remaining provisions continue in full force."},
          {title:"14. Contact Us", content:"For questions about these Terms of Service, contact us at support@vaultrapps.com or write to Vaultr LLC, Jacksonville, FL."},
        ].map((section,i)=>(
          <div key={i} style={{marginBottom:28,paddingBottom:28,borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:T.textPrimary,marginBottom:10}}>{section.title}</div>
            <div style={{fontSize:14,color:T.textSecondary,lineHeight:1.8}}>{section.content}</div>
          </div>
        ))}

        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px",marginTop:16,textAlign:"center"}}>
          <div style={{fontSize:13,color:T.textSecondary,marginBottom:8}}>Questions about these terms?</div>
          <a href="mailto:support@vaultrapps.com" style={{color:T.accentGlow,fontWeight:700,fontSize:14,textDecoration:"none"}}>support@vaultrapps.com</a>
          <div style={{fontSize:11,color:T.textMuted,marginTop:12}}>© 2026 Vaultr LLC · Veteran & Military Family Founded 🇺🇸</div>
        </div>
      </div>
    </div>
  );
}
function PrivacyPolicyPage() {
  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.textPrimary,fontFamily:"'DM Sans',sans-serif",padding:"40px 20px"}}>
      <STYLES/>
      <div style={{maxWidth:680,margin:"0 auto"}}>
        <div style={{marginBottom:32}}>
          <a href="/" style={{color:T.accentGlow,fontSize:13,textDecoration:"none"}}>← Back to Vaultr</a>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:"#fff"}}>V</div>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:T.textPrimary}}>Vaultr Privacy Policy</span>
        </div>
        <div style={{fontSize:12,color:T.textMuted,marginBottom:32}}>Last updated: May 31, 2026 · Vaultr LLC · Jacksonville, FL</div>

        {[
          {title:"1. Who We Are", content:"Vaultr LLC is a veteran and military family founded company based in Jacksonville, Florida. We operate the Vaultr job search and career platform at app.vaultrapps.com. Questions? Contact us at privacy@vaultrapps.com."},
          {title:"2. Information We Collect", content:"We collect information you provide when creating an account (name, email, password), profile information (job title, location, bio), resume information you choose to upload, payment information processed securely by Stripe (we never store card numbers), and usage data such as job searches and features used."},
          {title:"3. How We Use Your Information", content:"We use your information to provide and improve the Vaultr platform, send transactional emails (welcome emails, password resets) via Resend, process payments via Stripe, show you relevant job listings, and communicate important updates about your account."},
          {title:"4. Your Resume & Privacy Protection", content:"Vaultr is built with privacy at its core. Your address, phone number, and email are always hidden from employers until you explicitly approve a contact request. You control who sees your personal information at all times. You can archive or delete your resume at any time."},
          {title:"5. Data Sharing", content:"We do not sell your personal information to third parties. We share data only with trusted service providers necessary to operate Vaultr: Supabase (authentication and data storage), Stripe (payment processing), Resend (transactional email), and RapidAPI/JSearch (job listings). All providers are bound by data protection agreements."},
          {title:"6. Data Security", content:"We use industry-standard security measures including encrypted connections (HTTPS), secure authentication via Supabase, and server-side API key management. We never expose sensitive API keys in our frontend code."},
          {title:"7. Your Rights (CCPA & GDPR)", content:"You have the right to access the personal data we hold about you, request correction of inaccurate data, request deletion of your account and data, opt out of any marketing communications, and data portability. To exercise any of these rights, email us at privacy@vaultrapps.com."},
          {title:"8. Cookies", content:"Vaultr uses local storage to maintain your login session. We do not use third-party advertising cookies or tracking pixels. We may use basic analytics to understand how the app is used."},
          {title:"9. Children's Privacy", content:"Vaultr is not intended for users under the age of 16. We do not knowingly collect personal information from children under 16. If you believe a child has provided us with personal information, please contact us at privacy@vaultrapps.com."},
          {title:"10. Changes to This Policy", content:"We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by a notice within the app. Continued use of Vaultr after changes constitutes acceptance of the updated policy."},
          {title:"11. Contact Us", content:"For any privacy-related questions or requests, please contact us at privacy@vaultrapps.com or write to us at Vaultr LLC, Jacksonville, FL."},
        ].map((section,i)=>(
          <div key={i} style={{marginBottom:28,paddingBottom:28,borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:T.textPrimary,marginBottom:10}}>{section.title}</div>
            <div style={{fontSize:14,color:T.textSecondary,lineHeight:1.8}}>{section.content}</div>
          </div>
        ))}

        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px",marginTop:16,textAlign:"center"}}>
          <div style={{fontSize:13,color:T.textSecondary,marginBottom:8}}>Questions about your privacy?</div>
          <a href="mailto:privacy@vaultrapps.com" style={{color:T.accentGlow,fontWeight:700,fontSize:14,textDecoration:"none"}}>privacy@vaultrapps.com</a>
          <div style={{fontSize:11,color:T.textMuted,marginTop:12}}>© 2026 Vaultr LLC · Veteran & Military Family Founded 🇺🇸</div>
        </div>
      </div>
    </div>
  );
}
function AppRouter() {
  const path = window.location.pathname;
  const hash = window.location.hash;
  if (path === "/reset-password" || hash.includes("access_token")) return <ResetPasswordPage/>;
  if (path === "/privacy") return <PrivacyPolicyPage/>;
  if (path === "/privacy") return <PrivacyPolicyPage/>;
if (path === "/terms") return <TermsOfServicePage/>;
  return <App/>;
}

export default AppRouter;
function PrivacyBanner() {
  const [accepted, setAccepted] = useState(() => {
    try { return localStorage.getItem("vaultr_privacy_accepted") === "true"; } catch { return false; }
  });

  if (accepted) return null;

  return (
    <div style={{position:"fixed",bottom:0,left:0,right:0,background:"#0c1018",borderTop:`1px solid ${T.borderLight}`,padding:"14px 20px",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,backdropFilter:"blur(10px)"}}>
      <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.6,flex:1,minWidth:200}}>
        🍪 Vaultr uses local storage to keep you logged in and improve your experience. We never sell your data.
        {" "}<a href="/privacy" style={{color:T.accentGlow,textDecoration:"none",fontWeight:600}}>Privacy Policy</a>
        {" · "}<a href="/terms" style={{color:T.accentGlow,textDecoration:"none",fontWeight:600}}>Terms of Service</a>
      </div>
      <div style={{display:"flex",gap:8,flexShrink:0}}>
        <button onClick={()=>{localStorage.setItem("vaultr_privacy_accepted","true");setAccepted(true);}}
          style={{background:T.accent,border:"none",color:"#fff",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          Accept
        </button>
        <button onClick={()=>{localStorage.setItem("vaultr_privacy_accepted","true");setAccepted(true);}}
          style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecondary,borderRadius:8,padding:"8px 16px",fontSize:12,cursor:"pointer"}}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount - works across tabs and new windows
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const session = supabaseAuth.loadSession();
        if (session?.access_token) {
          // Always fetch fresh data from Supabase to get latest plan
          const userData = await supabaseAuth.getUser(session.access_token);
          if (userData?.id) {
            const meta = userData.user_metadata || {};
            const restoredUser = {
              id: userData.id,
              type: meta.type || "seeker",
              name: meta.name || userData.email?.split("@")[0] || "User",
              email: userData.email,
              avatar: meta.avatar || (userData.email?.[0]||"U").toUpperCase(),
              color: meta.color || "#3b82f6",
              role: meta.role || "",
              bio: meta.bio || "",
              location: meta.location || "",
              phone: meta.phone || "",
              company: meta.company || "",
              industry: meta.industry || "",
              size: meta.size || "",
              plan: meta.plan || "free", // Always fresh from Supabase
              joined: meta.joined || "2026",
              resume: meta.resume || null,
              hasUsedFreeCritique: meta.hasUsedFreeCritique || false,
              token: session.access_token,
            };
            // Update cached session with fresh plan
            session.userData = restoredUser;
            localStorage.setItem("vaultr_session", JSON.stringify(session));
            setUser(restoredUser);
          } else if (session.userData) {
            // Fallback to cached if Supabase unreachable
            setUser({...session.userData, token: session.access_token});
          }
        }
      } catch {
        // Fallback to cached session if network error
        try {
          const session = supabaseAuth.loadSession();
          if (session?.userData) setUser({...session.userData, token: session.access_token});
        } catch {}
      }
      setLoading(false);
    };
    restoreSession();
  }, []);

  // Track user activity to reset inactivity timer
  useEffect(() => {
    if (!user) return;
    const handleActivity = () => supabaseAuth.updateActivity();
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [user]);

const handleAuth = (userData) => {
    const session = supabaseAuth.loadSession();
    if (session?.access_token) {
      supabaseAuth.saveSession(session, userData);
    }
    setUser(userData);
  };

  const handleLogout = () => {
    supabaseAuth.clearSession();
    setUser(null);
  };

  if (loading) return (
    <div style={{minHeight:"100vh",background:"#07090f",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:14}}>
      <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#2563eb,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:24,color:"#fff",animation:"pulse 1.5s infinite"}}>V</div>
      <div style={{fontSize:13,color:"#445577",fontFamily:"'DM Sans',sans-serif"}}>Loading Vaultr...</div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );

  if (!user) return <><PrivacyBanner/><AuthScreen onAuth={handleAuth}/></>;
if (user.type==="seeker") return <><PrivacyBanner/><SeekerApp user={user} setUser={u=>setUser(typeof u==="function"?u(user):u)} onLogout={handleLogout}/></>;
return <><PrivacyBanner/><EmployerApp user={user} setUser={u=>setUser(typeof u==="function"?u(user):u)} onLogout={handleLogout}/></>;
}
